import React from 'react';
import {
  Alert,
  AlertTitle,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { alpha, type Theme } from '@mui/material/styles';
import {
  creativeReasonClearsOnItsOwn,
  creativeReasonSelfHealNote,
  formatMinorMoney,
  humanizeCreativeReasonCode,
} from './creativeDecisions/creativeDecisionFormat';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  FunnelABTestDecisionReportData,
  FunnelABTestDecisionRow,
  FunnelCreativePerformanceRow,
  FunnelCreativePerformanceSection,
  FunnelCreativeDrilldownData,
  FunnelProjectedLtvBasisResult,
  FunnelReportsDashboardData,
  FunnelReportsPersonScopeRollup,
  FunnelVersionPeopleScope,
  FunnelReportsScopeRollup,
  FunnelReportSectionError,
  FunnelValueBasisContract,
  MetricQualification,
} from '../types';
import { describeReportSectionError, TECH_DETAILS_LABEL } from '../api/errorCopy';
import { resolveSpendMoneyPresentation } from '../serving/spendMoneyPresentation';
// W-R5 (DISC-AC-144): computeCampaignVerdict is DELETED from this component —
// chips render the server's verdicts; the client recompute may return only as
// a labelled preview INSIDE the thresholds popover (designer-cycle item).
import {
  DEFAULT_VERDICT_THRESHOLDS,
  validateCampaignVerdictThresholds,
  VERDICT_THRESHOLD_ERROR_COPY,
  type CampaignVerdict,
  type CampaignVerdictThresholds,
} from '../campaignVerdict';
import { FunnelInsightsApiClient, ReportSettingsApiError } from '../api/insightsClient';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';
import {
  FunnelUnitProvider,
  PEOPLE_ABSENCE_LABEL,
  funnelUnitQueryFragment,
  unitNoun,
  useFunnelUnit,
  type FunnelUnit,
} from '../funnelUnit';
import { placementGateForUnit, type PlacementGateId } from '../unitMetrics';
import {
  ATTRIBUTION_ABSENCE_LABEL,
  META_EVIDENCE_COLUMN_NOTE,
  VIEW_THROUGH_DIVERGENCE_NOTE,
  attributionModelCopy,
} from '../attributionModelCopy';
import { metaPurchasesCopy } from '../metaPurchasesCopy';
import { PltvWithCac, cacBesideFromRow, cacLineText } from './PltvWithCac';
import {
  buildTrafficCampaignRows,
  buildTrafficCreativeRows,
  buildTrafficTotalRow,
  type TrafficGateView,
  type TrafficMoneyView,
  type TrafficSourceRow,
  type TrafficSourcesView,
} from '../trafficSourcesRows';
import { DecisionScorecard, type DecisionScorecardActions } from './DecisionScorecard';
import { StatTierChip } from './StatTierChip';
import { STAT_TIER_COPY, formatPct } from '../statTierCopy';
import { ReportWarningsSection } from './ReportWarningsSection';
import { FunnelGatesBand, type FunnelGateCellModel } from './FunnelGatesBand';
import {
  FUNNEL_REPORT_DATA_SECTION_IDS,
  filterAvailabilityByRole,
  getFunnelReportAvailability,
  isReportBlockVisibleToRole,
  type FunnelReportDataSectionId,
  type FunnelReportRoleGatedBlockId,
  type FunnelReportViewerRole,
} from '../reportAvailability';
import { useChartTheme } from '@modules/ui-components/hooks/useChartTheme';
import { FunnelHeroSection } from './FunnelHeroSection';
import { DataThroughCaption } from './DataThroughCaption';

export type FunnelReportsScopeMode = 'project' | 'alias' | 'onboarding' | 'version';

/** FU part E (D-F2): an A/B variant resolved to its CHILD placement's funnel/version. */
export interface AbPlacementReportVariant {
  variantId: string;
  name: string;
  funnelId?: string;
  versionId?: string;
}

export interface AbPlacementReportInfo {
  placementId: string;
  variants: AbPlacementReportVariant[];
}

interface FunnelReportsDashboardProps {
  data: FunnelReportsDashboardData | null;
  /** Release gate for the Creative Decisions UI and its Reports entry point. */
  creativeDecisionsEnabled?: boolean;
  abTestDecisionReport?: FunnelABTestDecisionReportData | null;
  showEmptySections?: boolean;
  /**
   * Role visibility (VISIBILITY_SPEC.md, owner round 5): `member` sees the three
   * decision answers only. Defaults to `member` on purpose — a caller that
   * forgets the prop under-shows rather than leaking a diagnosis section.
   */
  viewerRole?: FunnelReportViewerRole;
  scopeMode?: FunnelReportsScopeMode;
  onAliasSelect?: (alias: string) => void;
  /** versionId absent = whole-onboarding drill (multi-version parent row). */
  onOnboardingVersionSelect?: (scope: { alias: string; onboardingId: string; versionId?: string }) => void;
  /** Opens the placement entity page (independent of the report filter). */
  onOpenPlacementEntity?: (ref: { placementId?: string; alias: string }) => void;
  /** Opens the onboarding editor (independent of the report filter). */
  onOpenOnboardingEntity?: (ref: { onboardingId: string; versionId?: string }) => void;
  /** People-attribution S10: the version row's «Sources» button — traffic INTO that version. */
  onOpenVersionSources?: (ref: { alias: string; onboardingId: string; versionId: string; versionName?: string; onboardingName?: string }) => void;
  /** R2-2 canvas deep link parts (PE-4); hero canvas button hidden without them. */
  projectId?: string;
  scopeOnboardingId?: string;
  /** R2-3 (P11): envelope freshness bound for the payments «Data through» caption. */
  dataThrough?: string;
  /** R3-3: registry-linked decision actions for a decision row (undefined = read-only card). */
  decisionActionsFor?: (row: FunnelABTestDecisionRow) => DecisionScorecardActions | undefined;
  /** FU part E (D-F2): A/B placements keyed by alias — drives inline variant sub-rows. */
  abPlacementsByAlias?: Record<string, AbPlacementReportInfo>;
  /** FU part E (D-F2): opens a variant's funnel in canvas report mode in a NEW tab. */
  onOpenVariantReport?: (ref: { funnelId: string; versionId?: string }) => void;
  /**
   * W-R4-2 (DISC-AC-134): lazy per-campaign creative drilldown. Absent →
   * legacy behavior (preloaded top-creatives render on expand, no fetch).
   */
  loadCampaignCreatives?: (campaignId: string, cursor?: string) => Promise<FunnelCreativeDrilldownData>;
  /**
   * People-attribution S6: the Traffic sources view (campaigns / creatives in
   * people). The page owns the URL param (`traffic=`); absent → the button is
   * still rendered but the panel cannot open (read-only embed).
   */
  trafficView?: TrafficSourcesView | null;
  onTrafficViewChange?: (view: TrafficSourcesView | null) => void;
  /** S6: the creatives tab reads the standalone performance lane (DISC-AC-149). */
  loadCreativesPerformance?: () => Promise<FunnelCreativePerformanceSection>;
  /**
   * W-R5 (DISC-AC-144): verdict-thresholds Save re-fetches the payload — the
   * table chips render server verdicts and change only with a fresh payload.
   */
  onVerdictThresholdsSaved?: () => void;
  /**
   * F3 (FT-AC-089): model switch → the page re-fetches ONLY the acquisition
   * lane. Absent → the selector is not rendered (read-only caption only).
   */
  onAttributionModelChange?: (modelId: string) => void;
  /** F3: true while the acquisition lane refetches for a model switch. */
  attributionSwitchPending?: boolean;
  /**
   * B3: the gates band's display-only «Show visits» escape, offered when the
   * scope serves no person twins. Absent → the action is not rendered (the
   * typed absence still states the reason). A5 hides the unit toggle, so this
   * is the reader's only way out of a people-absent scope.
   */
  onShowVisits?: () => void;
}

const CHART_HEIGHT = 260;
// Stable identity for the no-creatives case — a fresh [] every render would
// defeat PaidAcquisitionSection's React.memo.
const REPORT_TABLE_MAX_HEIGHT = 320;
/**
 * D7: versions indent 18 px under their placement caption with a HAIRLINE
 * vertical rule — one quiet line that says «these belong to the caption
 * above». The old 2 px rule read as a tint block and competed with the row
 * rules it crossed.
 */
const versionSubRowCellSx = {
  pl: '18px',
  borderLeft: (theme: Theme) => `1px solid ${theme.palette.divider}`,
} as const;
// NB: px strings on purpose — in the MUI sx system a bare `width: 1` means 100%
// (fraction shorthand), which blew every truncated cell's hidden copy up to a
// viewport-sized absolute box and gave the PAGE a horizontal scrollbar.
const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: '1px',
  whiteSpace: 'nowrap',
} as const;

type SortDirection = 'asc' | 'desc';

export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface EvidenceColumn {
  id: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
  /** Metric guide shown as a tooltip on the column header. */
  description?: string;
  /** Secondary metric — rendered muted so the primary funnel gates stand out. */
  dimmed?: boolean;
  /**
   * People-attribution S5 (owner rule 2026-08-29): the table states its unit
   * ONCE in the caption; a column whose unit DIFFERS carries it here as a
   * dim second header line (text.disabled) — never «· unit» in every label.
   */
  unitNote?: string;
}

/**
 * D-U1 (FT-AC-118/119): D5 grammar — the counting unit is part of the VISIBLE
 * column label, lowercase after a middot, never tooltip-only. The three
 * primary gates follow the page toggle; every other metric column is pinned to
 * visits because it has no person twin, so a reader comparing two columns of
 * the same table always knows whether the numbers count the same things.
 *
 * The space AFTER the middot is non-breaking on purpose: a wrapping header
 * must break BEFORE the middot («First -> second» / «· people»), never leave a
 * dangling «·» at the end of the first line.
 */
const VISITS_SUFFIX = ' \u00b7\u00a0visits';
const JOURNEY_ONLY_GATE_NOTE = ' This gate has no person twin yet — it counts visits in both modes.';
/**
 * People-attribution S5 (D-A/D-C/D-G): version rows DO switch units. Their
 * people come from the version lane — the journey's own jff gate flags, no
 * K window, each person counted once per version they used (rows do not add
 * up; the served scope total does). FT-AC-071 was about the K-cohort and is
 * re-worded at rollout.
 */
const VERSION_ROW_NOTE = ' In people mode each human counts once per version they used (rows do not add up — the caption states the served scope total); gate flags are the journey\'s own, with no maturity window, so the figures compare with the Traffic sources view.';
const VERSION_ROW_GUIDES = {
  starts: `Started this funnel version in the window — people (each human once per version) or journeys (visits), per the page unit.${VERSION_ROW_NOTE}`,
  firstToSecond: `PRIMARY: of those who opened the first screen, how many reached the second.${VERSION_ROW_NOTE}`,
  paywallReach: `PRIMARY: of ALL who started, how many reached the paywall.${VERSION_ROW_NOTE}`,
  purchaseConv: `PRIMARY: of ALL who started, how many completed a purchase — a real payment (purchase_recorded), trials excluded in BOTH units.${VERSION_ROW_NOTE}`,
  purchases: `Completed purchases on this version — people or journeys per the page unit.${VERSION_ROW_NOTE}`,
  firstScreen: 'Started journeys that actually rendered the first screen (tracking sanity gate). Visits in both modes — no person twin.',
  paywallToBuy: 'Of journeys that saw the paywall, how many clicked a buy button. Visits in both modes — no person twin.',
  offers: 'Journeys that converted an offer of any type (purchase or trial start). Visits in both modes — no person twin.',
} as const;

export interface EvidenceRow {
  key: React.Key;
  cells: React.ReactNode[];
  sortValues?: Record<string, string | number | null | undefined>;
  /**
   * S5 grouped tables: rows sharing a `group` stay together; the row flagged
   * `groupHeader` renders as ONE full-width cell (cells[0]) above its members
   * and never sorts away from them — sorting reorders members INSIDE a group.
   */
  group?: string;
  groupHeader?: boolean;
  /**
   * S13 wave D: the row a link pointed at — `action.selected` tint and a
   * `data-highlighted` marker so the page can scroll it into view. This is the
   * ONLY row tint left: the data-quality «trust tint» and its coloured left
   * edge are gone (owner review 08.10 — the approved artifact paints no row,
   * and severity already has the band chip and the Data quality section).
   */
  selected?: boolean;
}

export function FunnelReportsDashboard({
  data: rawData,
  creativeDecisionsEnabled = false,
  abTestDecisionReport = null,
  showEmptySections = false,
  viewerRole = 'member',
  scopeMode = 'project',
  onAliasSelect,
  onOnboardingVersionSelect,
  onOpenPlacementEntity,
  onOpenOnboardingEntity,
  onOpenVersionSources,
  projectId,
  scopeOnboardingId,
  dataThrough,
  decisionActionsFor,
  abPlacementsByAlias,
  onOpenVariantReport,
  loadCampaignCreatives,
  trafficView = null,
  onTrafficViewChange,
  loadCreativesPerformance,
  onVerdictThresholdsSaved,
  onAttributionModelChange,
  attributionSwitchPending = false,
  onShowVisits,
}: FunnelReportsDashboardProps): React.ReactElement {
  const chart = useChartTheme();
  // F2 (FT-AC-083): the page counting unit, provided by FunnelReportsPage.
  const funnelUnit = useFunnelUnit();
  const [showAllLeaderboardColumns, setShowAllLeaderboardColumns] = React.useState(false);
  const [activePlacementSort, setActivePlacementSort] = React.useState<SortState>({
    columnId: 'starts',
    direction: 'desc',
  });
  const [abDecisionSort, setABDecisionSort] = React.useState<SortState>({
    columnId: 'revenue',
    direction: 'desc',
  });
  // Referentially stable payload — required for React.memo children (the
  // isolated PaidAcquisitionSection) to actually skip re-renders.
  const memoizedData = React.useMemo(
    () => (rawData ? normalizeDashboardData(rawData) : null),
    [rawData],
  );

  if (!rawData || !memoizedData) {
    return (
      <Alert severity="info" data-testid={FUNNEL_REPORTS_TEST_IDS.dashboardScopePrompt}>
        Select a date range to load project reports. Use onboarding/version only when drilling into a specific funnel.
      </Alert>
    );
  }

  const data = memoizedData;
  // E (VISIBILITY_SPEC): the role gate runs FIRST — before availability, before
  // `showEmptySections` / the `showEmptyReports` URL param, before the section
  // counter, before the section nav and before a section error can become a
  // card. Everything downstream reads the already-filtered values, so no
  // counter, caption or error surface can mention a block the reader cannot see.
  const roleAllows = (id: FunnelReportRoleGatedBlockId): boolean => isReportBlockVisibleToRole(id, viewerRole);
  const availability = filterAvailabilityByRole(getFunnelReportAvailability(data), viewerRole);
  // A one-effective-variant epoch is ordinary delivery. Keep its traffic in
  // normal report sections but omit experiment scorecards and evidence rows.
  const abTestDecisionRows = roleAllows('ab-test-decisions')
    ? (abTestDecisionReport?.decisions ?? []).filter(
      row => row.srm?.status !== 'not_applicable' && row.status !== 'delivery_only',
    )
    : [];
  const hasAnyData = FUNNEL_REPORT_DATA_SECTION_IDS.some(id => availability[id]) || abTestDecisionRows.length > 0;
  // PE-1 (handoff §A8): errored sections degrade inline, never disappear. When
  // ALL sections failed and nothing has data, the page-level banner takes over
  // — per-section error cards render only alongside surviving data.
  const sectionErrorById = new Map(
    hasAnyData
      ? (data.sectionErrors ?? [])
        .filter(entry => roleAllows(entry.section as FunnelReportRoleGatedBlockId))
        .map(entry => [entry.section, entry] as const)
      : [],
  );
  const isFlowDrilldown = scopeMode === 'onboarding' || scopeMode === 'version';
  const showFlowSections = isFlowDrilldown || showEmptySections;
  /**
   * The one place a data section decides to render. `roleAllows` is the OUTER
   * term on purpose: `showEmptySections` must not be able to reveal a hidden
   * section (AC-003), and neither must a section error.
   */
  const showSection = (id: FunnelReportDataSectionId): boolean => roleAllows(id)
    && (showEmptySections || availability[id] || sectionErrorById.has(id));
  const placementTotals = summarizePlacementRollups(data.aliasSummaries);
  const displaySummary = mergeSummaryWithPlacementTotals(
    data.summary,
    placementTotals ?? fallbackPlacementTotals(data.summary),
  );
  // F2 (FT-AC-090) + R-wave (FT-AC-110): person-grain headline. People are NOT
  // additive across placements — one human can appear on several — so the
  // headline reads the SERVED scope-grain rollup, which counts each person
  // once for the whole scope. It is never a sum or a max of the placement
  // rows: aliases without person rows carry no person keys at all, so any
  // client-side arithmetic over them is a fabrication (FT-AC-087).
  //
  // Decision table (FT-AC-110), in order:
  //   rows >= 1 + scope field  → the scope field
  //   rows == 1, no field      → the single row (pre-R payloads, unchanged)
  //   rows >  1, no field      → typed absence naming the reason
  //   rows == 0                → typed absence
  // Gates without a person twin (first screen, buy clicks, offers, money)
  // keep their journey figures labelled «· visits».
  const peopleMode = funnelUnit === 'people';
  const selectedUnitSuffix = ` \u00b7\u00a0${unitNoun(funnelUnit)}`;
  /**
   * W11 (stage 2, owner 2026-10-07): the band reads the ATTRIBUTION basis —
   * `versionPeopleScope` — so the whole Reports page, and the Research report
   * with it, mean ONE thing. `MODULE.md` → *Gate basis* carries the contract.
   *
   * Why this is also a bug fix, not only a basis change: at VERSION scope the
   * K-window block (`personScopeRollup`) is not served at all, so the band
   * stated «People data is not served for this scope» while the table 200 px
   * below rendered the very same people from `versionPeopleScope`
   * (design review P0-2, measured in `W10_LATE_CONVERSION_MEASUREMENT.md`).
   *
   * W10 measured the cost of the switch on real DEV data: zero for every gate
   * the band shows — Starts, First → second, Paywall reached and the
   * trial-inclusive conversion are identical on both bases, and the real-payment
   * gate is MORE complete on this one (107 payers vs 97).
   */
  const attributionPeopleScope = data.versionPeopleScope;
  const peopleScope = attributionPeopleScope
    ?? (data.aliasSummaries.length > 0 ? data.personScopeRollup : undefined);
  const peopleRow: FunnelVersionPeopleScope | FunnelReportsPersonScopeRollup | FunnelReportsScopeRollup | undefined =
    peopleScope ?? (data.aliasSummaries.length === 1 ? data.aliasSummaries[0] : undefined);
  /** Which basis the band's people cells came from — it changes ONE label. */
  const bandPeopleBasis: 'attribution' | 'conversion_window' = attributionPeopleScope
    ? 'attribution'
    : 'conversion_window';
  const peopleAbsenceDetail = data.aliasSummaries.length > 1
    ? 'n/a — people are not additive across placements'
    : PEOPLE_ABSENCE_LABEL;
  // FT-AC-111: the headline and the visible placement rows disagree ON PURPOSE
  // whenever the scope spans several placements, and the reader who adds the
  // column up must find the reason without hovering anything.
  //
  // The copy deliberately states NO direction and does NOT present the overlap
  // count as the arithmetic explanation of the gap. Both would be false in real
  // cases found on stage: the placements plane is capped at 300 rows, so on a
  // wide scope the VISIBLE rows sum to LESS than the headline; and one person on
  // k placements adds (k−1) to the gap but 1 to the count, so 12 people
  // legitimately accounted for a 408 gap there. What is always true — and what
  // the reader actually needs — is that each row counts its own people, the
  // headline counts each human once, and this many people used more than one
  // placement. The magnitude is the SERVED count; `Σ rows − scope` would be
  // wrong wherever a placement carries no person keys or the table is capped.
  const peopleScopeCaption = peopleMode && peopleScope && data.aliasSummaries.length > 1
    // The magnitude sentence needs the PLACEMENT overlap count. The attribution
    // scope block serves a VERSION overlap instead, which is a different claim,
    // so on that basis the caption keeps the statement and drops the number
    // rather than relabelling one count as the other.
    ? `Each placement counts its own people, so these rows do not add up to the headline — it counts each person once.${
      'peopleOnMultiplePlacements' in peopleScope && typeof peopleScope.peopleOnMultiplePlacements === 'number'
        ? ` ${formatInteger(peopleScope.peopleOnMultiplePlacements)} people used more than one placement in this range.`
        : ' People who used more than one placement are counted once.'}`
    : '';
  const peopleCountParts = (value: number | undefined, detail: string): { value: string; detail: string } =>
    typeof value === 'number'
      ? { value: formatInteger(value), detail }
      : { value: 'n/a', detail: peopleAbsenceDetail };
  const peopleRateParts = (
    rate: number | null | undefined,
    numerator: number | undefined,
    denominator: number | undefined,
  ): { top: string; bottom: string } =>
    typeof numerator === 'number' && typeof denominator === 'number'
      ? rateEvidenceParts(rate ?? null, numerator, denominator)
      : { top: 'n/a', bottom: peopleAbsenceDetail };
  // D-U1 (FT-AC-117..120): ONE per-unit resolver drives BOTH the rate cell and
  // the column's sortValue on ALIAS rows, so a switched column can never be
  // sorted by the other unit. Version rows and A/B sub-rows never come here —
  // they are journey-only by contract (FT-AC-071) and say so in their labels.
  const placementGateCell = (
    row: FunnelReportsScopeRollup,
    gate: PlacementGateId,
    key: string,
  ): React.ReactNode => {
    const view = placementGateForUnit(row, gate, funnelUnit);
    if (!view.available) return <MetricAbsenceCell key={key} detail={PEOPLE_ABSENCE_LABEL} />;
    return (
      <RateEvidenceCell
        key={key}
        rate={view.rate ?? null}
        numerator={view.numerator ?? 0}
        denominator={view.denominator ?? 0}
        qualification={view.qualification}
        align="center"
      />
    );
  };
  const placementGateSort = (row: FunnelReportsScopeRollup, gate: PlacementGateId): number | null => {
    const view = placementGateForUnit(row, gate, funnelUnit);
    if (!view.available) return null;
    return sortableRate(view.rate ?? null, view.numerator ?? 0, view.denominator ?? 0);
  };
  // R2-3 (P11): staged paywall rates per funnel entrant over time, from the
  // same daily rows that power Funnel Health. The last day renders as a dashed
  // tail when the envelope freshness bound falls inside it (partial period).
  const paymentsTail = splitDailyTail(data.funnelHealthDaily, row => row.reportDate, dataThrough);
  const paymentsLastDayPartial = paymentsTail.partial;
  const paymentsRateRows = data.funnelHealthDaily.map((row, index) => {
    const rate = (numerator: number) => (row.startedJourneys > 0 ? numerator / row.startedJourneys : null);
    const solid = paymentsTail.isSolid(index);
    const tail = paymentsTail.isTail(index);
    return {
      date: row.reportDate,
      paywallRate: solid ? rate(row.paywallReachedJourneys) : null,
      trialRate: solid ? rate(row.trialStartedJourneys) : null,
      purchaseRate: solid ? rate(row.purchaseJourneys) : null,
      paywallRateTail: tail ? rate(row.paywallReachedJourneys) : null,
      trialRateTail: tail ? rate(row.trialStartedJourneys) : null,
      purchaseRateTail: tail ? rate(row.purchaseJourneys) : null,
    };
  });
  // R4-3: same partial-day technique for the daily Data Quality trend.
  const dataQualityTail = splitDailyTail(data.dataQuality, row => row.reportDate, dataThrough);
  const dataQualityTrendRows = data.dataQuality.map((row, index) => ({
    reportDate: row.reportDate,
    healthScore: dataQualityTail.isSolid(index) ? row.healthScore : null,
    issueCount: dataQualityTail.isSolid(index) ? row.issueCount : null,
    healthScoreTail: dataQualityTail.isTail(index) ? row.healthScore : null,
    issueCountTail: dataQualityTail.isTail(index) ? row.issueCount : null,
  }));
  const revenuePerFunnelStart = displaySummary.purchaseAmount !== null && displaySummary.startedJourneys > 0
    ? formatCurrency(displaySummary.purchaseAmount / displaySummary.startedJourneys, displaySummary.currency)
    : null;
  // R4-1 (P9): the quality chip carries QUANTIFIED evidence, not just a word —
  // duplicate/issue totals come from the same rows the Data Quality table shows.
  const qualityTotals = data.dataQuality.reduce(
    (acc, row) => ({
      duplicates: acc.duplicates + row.duplicateEventIdCount,
      issues: acc.issues + row.issueCount,
      lastDuplicateDate: row.duplicateEventIdCount > 0 ? row.reportDate : acc.lastDuplicateDate,
    }),
    { duplicates: 0, issues: 0, lastDuplicateDate: null as string | null },
  );
  const qualityQuantified = data.dataQuality.length > 0
    ? `This window: ${formatInteger(qualityTotals.duplicates)} duplicate events${qualityTotals.lastDuplicateDate ? ` (last on ${qualityTotals.lastDuplicateDate})` : ''}, ${formatInteger(qualityTotals.issues)} issues flagged.`
    : undefined;
  // Collapsed-row answers, in the approved artifact's own words:
  // «health score 0 · 11,326 issues in window» and «1 critical, 3 watch».
  const dataQualityAnswer = data.dataQuality.length > 0
    ? `health score ${formatInteger(data.dataQuality[data.dataQuality.length - 1].healthScore)}`
      + ` · ${formatInteger(qualityTotals.issues)} issues in window`
    : undefined;
  const anomalyCriticalCount = data.anomalies.filter(row => row.severity === 'critical').length;
  const anomalyInboxAnswer = data.anomalies.length === 0
    ? 'all healthy'
    : `${anomalyCriticalCount} critical, ${data.anomalies.length - anomalyCriticalCount} watch`;
  // Shared between the default screen-flow layout and the hero's Table view (R2-1).
  const screenFlowEvidenceTable = (
    <EvidenceTable
      id="screen-flow"
      columns={['Route', 'Action', 'Transitions', 'Unique journeys', 'Forward', 'Back', 'Jump']}
      rows={data.screenFlowEdges.slice(0, 8).map(row => [
        edgeRouteLabel(row),
        row.actionKey || 'n/a',
        formatInteger(row.transitionCount),
        formatInteger(row.uniqueJourneys),
        formatInteger(row.forwardTransitionCount),
        formatInteger(row.backTransitionCount),
        formatInteger(row.jumpTransitionCount),
      ])}
    />
  );
  // 1.4: schema-first anomaly columns render only when at least one row carries
  // a real signal in the SOURCE fields (never the 'n/a'/'unranked'/'unknown'
  // formatter fallbacks). Populated data keeps every column.
  const anomalyTableRows = data.anomalies.slice(0, 8);
  const anomalyColumnSpecs: Array<{
    header: string;
    cell: (anomaly: FunnelReportsDashboardData['anomalies'][number]) => React.ReactNode;
    hasSignal?: (anomaly: FunnelReportsDashboardData['anomalies'][number]) => boolean;
  }> = [
    { header: 'Metric', cell: anomaly => formatMetricLabel(anomaly.metricName) },
    {
      header: 'Priority',
      cell: anomaly => formatAnomalyPriority(anomaly),
      hasSignal: anomaly => Boolean(anomaly.anomalyPriorityLabel)
        || (anomaly.anomalyPriorityScore !== null && anomaly.anomalyPriorityScore !== undefined),
    },
    {
      header: 'Recurrence',
      cell: anomaly => anomalyRecurrenceLabel(anomaly),
      hasSignal: anomaly => Boolean(anomaly.incidentRecurrenceState || anomaly.incidentOccurrenceCount),
    },
    { header: 'Severity', cell: anomaly => anomaly.severity },
    {
      header: 'Confidence',
      cell: anomaly => formatAnomalyConfidence(anomaly),
      hasSignal: anomaly => Boolean(anomaly.anomalyConfidenceLabel)
        || (anomaly.anomalyConfidence !== null && anomaly.anomalyConfidence !== undefined),
    },
    {
      header: 'Score',
      cell: anomaly => formatAnomalyScore(anomaly),
      hasSignal: anomaly => (anomaly.anomalyScore !== null && anomaly.anomalyScore !== undefined && !Number.isNaN(anomaly.anomalyScore))
        || Boolean(anomaly.anomalyModelMethod),
    },
    {
      header: 'Narrative',
      cell: anomaly => <WrapText key="narrative" text={anomaly.anomalyHeadline || anomaly.anomalyExplanation || 'n/a'} />,
      hasSignal: anomaly => Boolean(anomaly.anomalyHeadline || anomaly.anomalyExplanation),
    },
    { header: 'Next action', cell: anomaly => <WrapText key="next-action" text={anomaly.anomalyNextAction || anomaly.remediationHint || 'n/a'} /> },
    { header: 'Actual', cell: anomaly => formatMetricValue(anomaly.metricName, anomaly.actualValue, displaySummary.currency) },
    { header: 'Expected', cell: anomaly => formatMetricValue(anomaly.metricName, anomaly.expectedValue, displaySummary.currency) },
    { header: 'Delta', cell: anomaly => formatRate(anomaly.relativeDelta) },
    { header: 'Revenue impact', cell: anomaly => formatCurrency(anomaly.estimatedRevenueDelta, displaySummary.currency) },
    { header: 'Paid exposure', cell: anomaly => anomalyPaidExposureLabel(anomaly, displaySummary.currency) },
    { header: 'Paid campaigns', cell: anomaly => anomaly.topPaidCampaigns || 'n/a' },
    { header: 'Affected segment / driver', cell: anomaly => formatDriverSummary(anomaly.topDriverSummary || anomaly.remediationHint || 'n/a') },
    { header: 'Artifact', cell: anomaly => anomalyArtifactLabel(anomaly) },
  ];
  const visibleAnomalyColumns = anomalyColumnSpecs.filter(column =>
    !column.hasSignal || anomalyTableRows.some(column.hasSignal),
  );
  // R1-3: per-gate daily series (sparklines) + an HONEST within-window delta.
  // The payload covers only the requested window, so the delta compares the
  // per-day average of the second half against the first half — it never
  // fabricates a prior period the API did not return.
  const healthDays = data.funnelHealthDaily;
  const gateSeries = (pick: (row: FunnelReportsDashboardData['funnelHealthDaily'][number]) => number): number[] =>
    healthDays.map(pick);
  const halfWindowDelta = (series: number[]): number | null => {
    if (series.length < 2) return null;
    const mid = Math.floor(series.length / 2);
    const firstAvg = series.slice(0, mid).reduce((a, b) => a + b, 0) / mid;
    const secondAvg = series.slice(mid).reduce((a, b) => a + b, 0) / (series.length - mid);
    if (firstAvg <= 0) return null;
    return secondAvg / firstAvg - 1;
  };
  // PE-6b (D-P6): when the server returned a prior-period summary, KPI deltas
  // become an honest period-over-period comparison of the RAW summaries —
  // displaySummary is merged with placement totals and would lie. Without a
  // prior, the labeled half-window split stays (never silently reinterpreted).
  const priorSummary = data.priorSummary;
  const priorWindowLabel = priorSummary && data.priorWindow
    ? `${formatWindowDay(data.priorWindow.from)} – ${formatWindowDay(new Date(Date.parse(data.priorWindow.to) - 1).toISOString())}`
    : null;
  const kpiDeltaContext = priorWindowLabel ? `vs prior period (${priorWindowLabel})` : undefined;
  const kpiDeltaFor = (
    pick: (summary: FunnelReportsDashboardData['summary']) => number | null,
    series: number[],
  ): number | null => {
    if (!priorSummary) return halfWindowDelta(series);
    const prior = pick(priorSummary);
    const current = pick(data.summary);
    if (prior === null || current === null || prior <= 0) return null;
    return current / prior - 1;
  };
  const startedSeries = gateSeries(row => row.startedJourneys);
  // Metric guides (hover the dotted header). The three PRIMARY funnel gates —
  // first→second, paywall reached, purchase conversion — lead; the rest are
  // dimmed supporting evidence (user review feedback).
  const METRIC_GUIDES = {
    placement: 'Published placement that received traffic: display name first, public alias below.',
    onboarding: 'V2 onboarding (funnel) that was active on the placement: name first, id below.',
    version: 'Published funnel version: name first, id below.',
    starts: 'Journeys (visits) that started the onboarding in the selected window. The second line is unique PEOPLE — one person restarting counts as several starts but one visitor; compare the people number with user-based tools (Amplitude, ad managers).',
    firstToSecond: peopleMode
      ? 'PRIMARY: of PEOPLE who opened the first screen, how many reached the second — each human counted once on this placement.'
      : 'PRIMARY: of journeys that opened the first screen, how many reached the second — early-funnel engagement.',
    paywallReach: peopleMode
      ? 'PRIMARY: of ALL people who started, how many reached the paywall — each human counted once on this placement.'
      : 'PRIMARY: of ALL started journeys, how many reached the paywall.',
    // People-attribution S2a (substance check S-1): the person gate reads the
    // journey's first conversion (jff converted_at = MIN(TrialStarted,
    // Purchase)), so it counts trial starters who never paid. It is labelled
    // for what it is; paid-only people live on the campaign/creative surfaces.
    // The visits gate reads purchase_recorded — a real payment — and keeps
    // its name.
    purchaseConv: peopleMode
      ? 'PRIMARY: of ALL people who started, how many converted — a trial start OR a purchase within the conversion window (the served person gate reads the first conversion event; trial starters who never paid are included). Paid-only people are on the campaign and creative surfaces.'
      : 'PRIMARY: of ALL started journeys, how many completed a purchase — the end-to-end funnel conversion. Trials are counted separately in the Trials column.',
    firstScreen: `Started journeys that actually rendered the first screen (tracking sanity gate).${JOURNEY_ONLY_GATE_NOTE}`,
    paywallToBuy: `Of journeys that saw the paywall, how many clicked a buy button.${JOURNEY_ONLY_GATE_NOTE}`,
    offers: `Journeys that converted an offer of any type (purchase or trial start).${JOURNEY_ONLY_GATE_NOTE}`,
    trials: `Journeys that started a trial.${JOURNEY_ONLY_GATE_NOTE}`,
    purchases: `Journeys with a completed purchase (count; the rate is Purchase conv).${JOURNEY_ONLY_GATE_NOTE}`,
    purchaseRevenue: 'Sum of purchase amounts attributed to this scope in the window.',
    initialBilling: 'Initial billing revenue attributed to this scope (first invoices).',
    quality: 'Data-quality verdict for this scope. Hover the chip for what it means and how to fix it; details in the Warnings section below.',
    actions: 'Open ↗ opens this funnel version in report mode (new tab). Scope the report with the filters above.',
  } as const;

  // People-attribution S5 (plan 2026-08-29, D-A/D-C/D-G): the placements
  // table and the visits-only version table are REPLACED by ONE versions
  // table — every funnel version that received traffic, grouped under its
  // placement (or A/B test), all visible without drill-down. The gate columns
  // follow the page unit through the ONE resolver (placementGateForUnit —
  // cell AND sort); people on version rows come from the version lane
  // (journey-flag gates, no K) and degrade PER ROW to a typed absence.
  // R1-6: which column indexes stay at L0 (name/starts + 3 primary gates + purchases/revenue + action).
  // The approved artifact shows EIGHT columns here: identity, Starts, the three
  // gates, Purchases, Revenue, Details. `quality` (11) lives behind «Show all
  // columns» again now that no row is tinted.
  const VERSION_PRIMARY_COLUMN_INDEXES = [0, 1, 2, 3, 4, 5, 6, 12];
  const pickLeaderboardColumns = <T,>(items: T[], keep: number[]): T[] =>
    showAllLeaderboardColumns ? items : keep.map(index => items[index]);
  /**
   * D-U1 (`modules/funnel-insights/roles/designer.md`): «every column WITHOUT a
   * person twin is pinned to `· visits` in BOTH modes, so **no rate is ever
   * unitless**», and D5: the unit noun is part of the VISIBLE label, never
   * tooltip-only.
   *
   * The owner rule of 2026-08-29 — state the unit ONCE in the caption, and
   * sub-label only the columns that differ — rested on a caption that D1
   * deleted. With the caption gone, «only the differing columns are labelled»
   * inverts the meaning: an unlabelled column reads as «same as the labelled
   * ones». So every metric column carries its own noun:
   *   - `pageUnitNote` — the three switching gates plus Starts and Purchases,
   *     which read the served person figure in people mode;
   *   - `visitsNote` — journey-only gates and money, in BOTH modes.
   */
  const pageUnitNote = unitNoun(funnelUnit);
  const visitsNote = 'visits';
  const versionColumns: EvidenceColumn[] = [
    { id: 'version', label: 'Version', sortable: true, minWidth: 230, description: METRIC_GUIDES.version },
    // D8/mock parity (owner review 08.10): in the approved artifact EVERY column
    // but the identity is centred — head and body alike. Mixing right-aligned
    // counts with centred rates put each column's value under a different part
    // of its own label, which is what read as «the numbers are shifted».
    { id: 'starts', label: 'Starts', sortable: true, unitNote: pageUnitNote, description: VERSION_ROW_GUIDES.starts },
    // S8 review: the qualification chip («✓ 24.2% ± 0.9») needs ~120 px — a
    // narrower gate column ellipsises the interval, which is a Must-fix.
    { id: 'firstToSecond', label: 'First -> second', sortable: true, minWidth: 132, unitNote: pageUnitNote, description: VERSION_ROW_GUIDES.firstToSecond },
    { id: 'paywallReach', label: 'Paywall reached', sortable: true, minWidth: 132, unitNote: pageUnitNote, description: VERSION_ROW_GUIDES.paywallReach },
    { id: 'purchaseConv', label: 'Purchase conv', sortable: true, minWidth: 132, unitNote: pageUnitNote, description: VERSION_ROW_GUIDES.purchaseConv },
    { id: 'purchases', label: 'Purchases', sortable: true, unitNote: pageUnitNote, description: VERSION_ROW_GUIDES.purchases },
    { id: 'purchaseRevenue', label: 'Revenue', sortable: true, unitNote: visitsNote, description: METRIC_GUIDES.purchaseRevenue },
    { id: 'firstScreen', label: 'First screen opened', sortable: true, dimmed: true, unitNote: visitsNote, description: VERSION_ROW_GUIDES.firstScreen },
    { id: 'paywallToBuy', label: 'Paywall -> buy', sortable: true, dimmed: true, unitNote: visitsNote, description: VERSION_ROW_GUIDES.paywallToBuy },
    { id: 'offers', label: 'Offers', sortable: true, dimmed: true, unitNote: visitsNote, description: VERSION_ROW_GUIDES.offers },
    { id: 'initialBilling', label: 'Initial billing', sortable: true, dimmed: true, unitNote: visitsNote, description: METRIC_GUIDES.initialBilling },
    // The row tint that P1-11 added this column as a legend for is gone (owner
    // review 08.10 — the approved artifact paints no row), so `quality` goes
    // back to the «Show all columns» level where S5 put it. Severity still has
    // two homes the artifact keeps: the band's data-quality chip and the Data
    // quality section.
    { id: 'quality', label: 'Quality', sortable: true, description: METRIC_GUIDES.quality },
    // D8: the two unlabelled icon buttons become two named text links under a
    // «Details» header — an icon that needs a tooltip to be understood is not a
    // label. The slot grows to fit the words.
    { id: 'action', label: 'Details', minWidth: 148, description: METRIC_GUIDES.actions },
  ];
  // FU part E (D-F2): an alias is an A/B rollup when the page resolved it from
  // webPlacements OR the server identity says so (fallback covers a stale
  // placement list — variants render by version identity alone).
  const abInfoForAlias = (row: FunnelReportsDashboardData['aliasSummaries'][number]): AbPlacementReportInfo | undefined => {
    const resolved = abPlacementsByAlias?.[row.alias];
    if (resolved) return resolved;
    return row.identity?.placementType === 'ab_test'
      ? { placementId: row.identity?.placementId ?? '', variants: [] }
      : undefined;
  };
  type OnboardingSummaryRow = FunnelReportsDashboardData['aliasOnboardingSummaries'][number];
  /** Unit-aware COUNT cell: people (typed absence per row) or journeys. */
  const versionCountCell = (
    key: string,
    people: number | undefined,
    journeys: number,
  ): React.ReactNode => {
    if (!peopleMode) return formatInteger(journeys);
    if (typeof people !== 'number') return <MetricAbsenceCell key={key} detail={PEOPLE_ABSENCE_LABEL} />;
    return formatInteger(people);
  };
  const versionCountSort = (people: number | undefined, journeys: number): number | null =>
    (peopleMode ? (typeof people === 'number' ? people : null) : journeys);
  const versionRowCells = (row: OnboardingSummaryRow, nameCell: React.ReactNode): React.ReactNode[] => [
    nameCell,
    // The Starts cell keeps the F2 testid: in people mode it reads the served
    // person figure (or the typed absence), never a relabelled journey count.
    <Box key="starts" data-testid={FUNNEL_REPORTS_TEST_IDS.startsUniqueCell} sx={{ textAlign: 'center' }}>
      {versionCountCell('starts', row.startedPeople, row.startedJourneys)}
    </Box>,
    placementGateCell(row, 'firstToSecond', 'first-second'),
    placementGateCell(row, 'paywallReach', 'paywall'),
    placementGateCell(row, 'purchaseConv', 'purchase-conv'),
    versionCountCell('purchases', row.purchasePeople, row.purchaseJourneys),
    formatNullableCurrency(row.purchaseAmount, row.currency || displaySummary.currency),
    <RateEvidenceCell key="first-screen" rate={row.firstScreenOpenRate} numerator={row.firstScreenOpenedJourneys} denominator={row.startedJourneys} qualification={row.qualification?.firstScreenOpenRate} align="center" />,
    <RateEvidenceCell key="paywall-buy" rate={row.paywallToBuyClickRate} numerator={row.buyButtonClickJourneys} denominator={row.paywallReachedJourneys} qualification={row.qualification?.paywallToBuyClickRate} align="center" />,
    formatInteger(row.offerConvertedJourneys),
    formatNullableCurrency(row.initialBillingRevenue, row.currency || displaySummary.currency),
    <QualitySeverityChip key="quality" severity={row.dataQualitySeverity} />,
    // D8: «Dropoffs» opens the version in canvas report mode — flow-level
    // drop-off diagnosis. «Economics» opens the traffic INTO this version
    // (campaigns and creatives, spend apportioned by the version's share,
    // S10/D-M). Both keep their original test ids so every deep-link assertion
    // and e2e step still points at the same action. On a row with no traffic
    // there is nothing to diagnose, so both are muted and inert.
    // The approved artifact stacks the two links, one under the other, centred
    // in the column — side by side they widened the row and read as one control.
    <Stack key="actions" direction="column" spacing={0.5} sx={{ whiteSpace: 'nowrap', alignItems: 'center' }}>
      <VersionDetailLink
        label="Dropoffs"
        testId={FUNNEL_REPORTS_TEST_IDS.versionOpenReport(row.alias, row.onboardingId || 'unknown', row.versionId || 'unknown')}
        ariaLabel={`Drop-off detail for ${row.identity?.versionName ?? row.versionId ?? 'version'}`}
        disabled={!onOpenOnboardingEntity || !row.onboardingId || row.startedJourneys === 0}
        onClick={() => row.onboardingId && onOpenOnboardingEntity?.({ onboardingId: row.onboardingId, versionId: row.versionId })}
      />
      <VersionDetailLink
        label="Economics"
        testId={FUNNEL_REPORTS_TEST_IDS.versionSources(row.alias, row.onboardingId || 'unknown', row.versionId || 'unknown')}
        ariaLabel={`Traffic and spend into ${row.identity?.versionName ?? row.versionId ?? 'version'}`}
        disabled={!onOpenVersionSources || !row.onboardingId || !row.versionId || row.startedJourneys === 0}
        onClick={() => row.onboardingId && row.versionId && onOpenVersionSources?.({
          alias: row.alias,
          onboardingId: row.onboardingId,
          versionId: row.versionId,
          versionName: row.identity?.versionName,
          onboardingName: row.identity?.onboardingName,
        })}
      />
    </Stack>,
  ];
  const versionRowSortValues = (row: OnboardingSummaryRow, versionLabel: string): EvidenceRow['sortValues'] => ({
    version: versionLabel,
    starts: versionCountSort(row.startedPeople, row.startedJourneys),
    firstToSecond: placementGateSort(row, 'firstToSecond'),
    paywallReach: placementGateSort(row, 'paywallReach'),
    purchaseConv: placementGateSort(row, 'purchaseConv'),
    purchases: versionCountSort(row.purchasePeople, row.purchaseJourneys),
    purchaseRevenue: row.purchaseAmount,
    firstScreen: sortableRate(row.firstScreenOpenRate, row.firstScreenOpenedJourneys, row.startedJourneys),
    paywallToBuy: sortableRate(row.paywallToBuyClickRate, row.buyButtonClickJourneys, row.paywallReachedJourneys),
    offers: row.offerConvertedJourneys,
    initialBilling: row.initialBillingRevenue,
    quality: row.dataQualitySeverity,
  });
  // Groups = aliases in the server's traffic order; an alias that only appears
  // on version rows (no alias rollup in the window) still gets a header.
  const versionGroupOrder: string[] = [];
  const versionGroups = new Map<string, OnboardingSummaryRow[]>();
  for (const alias of data.aliasSummaries.map(row => row.alias)) {
    if (!versionGroups.has(alias)) { versionGroupOrder.push(alias); versionGroups.set(alias, []); }
  }
  for (const row of data.aliasOnboardingSummaries) {
    if (!versionGroups.has(row.alias)) { versionGroupOrder.push(row.alias); versionGroups.set(row.alias, []); }
    versionGroups.get(row.alias)?.push(row);
  }
  const versionRows: EvidenceRow[] = versionGroupOrder.flatMap(alias => {
    const aliasRow = data.aliasSummaries.find(row => row.alias === alias);
    const members = versionGroups.get(alias) ?? [];
    const abInfo = aliasRow ? abInfoForAlias(aliasRow) : undefined;
    const decision = abInfo ? (abTestDecisionReport?.decisions ?? []).find(row => row.testId === abInfo.placementId) : undefined;
    const onboardingNames = Array.from(new Set(members.map(row => row.identity?.onboardingName ?? row.onboardingId ?? '').filter(Boolean)));
    const headerRow: EvidenceRow = {
      key: `group-${alias}`,
      group: alias,
      groupHeader: true,
      cells: [
        <Stack
          key="group"
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          data-testid={FUNNEL_REPORTS_TEST_IDS.versionGroupHeader(alias)}
        >
          {/* D7: a quiet caption, not a band. The `placement` chip repeated the
              column it sat in and the «N versions with traffic» counter counted
              rows that are visible right underneath. Both go. The A/B chip
              STAYS — it is the only marker that these rows are variants of one
              test rather than independent versions, which changes how they are
              read. The funnel-name counter returns only when it adds something:
              one placement serving SEVERAL funnels. */}
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {aliasRow?.identity?.placementName ?? alias}
          </Typography>
          {aliasRow?.identity?.placementName && (
            <Typography variant="caption" color="text.disabled">{alias}</Typography>
          )}
          {abInfo && (
            <Chip size="small" variant="outlined" color="info" label="A/B test" />
          )}
          {!abInfo && onboardingNames.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              {`${onboardingNames.length} funnels`}
            </Typography>
          )}
        </Stack>,
      ],
      sortValues: {},
    };
    const memberRows: EvidenceRow[] = members.map(row => {
      const variant = abInfo?.variants.find(candidate => candidate.funnelId === row.onboardingId
        && (!candidate.versionId || candidate.versionId === row.versionId));
      const probability = variant && decision
        ? formatVariantProbability(
          decision.scores.find(score => score.variantId === variant.variantId)
            ?.comparisonToControl?.[decision.primaryMetric]?.probabilityToBeatControl ?? null,
        )
        : null;
      const versionLabel = variant
        ? `Variant ${variant.name} · ${row.identity?.versionName ?? row.versionId ?? ''}`
        : row.identity?.versionName ?? row.versionId ?? '';
      const nameCell = (
        <Stack
          key="version"
          direction="row"
          spacing={0.75}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          data-testid={FUNNEL_REPORTS_TEST_IDS.versionRow(row.alias, row.onboardingId || 'unknown', row.versionId || 'unknown')}
          sx={versionSubRowCellSx}
        >
          {/* D6 (plan §3 S1): the FUNNEL name leads — served by the QW1 identity
              layer (`identity.onboardingName`) — with the version label as the
              second line. The version document id leaves the table. A row whose
              name did not resolve falls back to its id and carries the ⚠ marker
              (AC-021), so it is still identifiable. */}
          <IdentityNameCell
            name={row.identity?.onboardingName || undefined}
            id={row.onboardingId || row.versionId || 'n/a'}
            secondary={versionLabel || undefined}
            warnings={row.identity?.identityWarnings}
          />
          {probability && <Chip size="small" variant="outlined" label={`P(beat) ${probability}`} />}
        </Stack>
      );
      return {
        key: `${row.alias}-${row.onboardingId || 'unknown'}-${row.versionId || 'unknown'}`,
        group: alias,
        cells: versionRowCells(row, nameCell),
        sortValues: versionRowSortValues(row, versionLabel),
      };
    });
    // FU-AC-011 (kept): an A/B variant with NO rows in this window is still
    // listed — explicit «no data in window», never a silent gap. Its P(beat)
    // still renders (the decision lane is independent of traffic rows).
    const missingVariantRows: EvidenceRow[] = (abInfo?.variants ?? [])
      .filter(variant => !members.some(row => row.onboardingId === variant.funnelId
        && (!variant.versionId || row.versionId === variant.versionId)))
      .map(variant => {
        const probability = decision
          ? formatVariantProbability(
            decision.scores.find(score => score.variantId === variant.variantId)
              ?.comparisonToControl?.[decision.primaryMetric]?.probabilityToBeatControl ?? null,
          )
          : null;
        return {
          key: `${alias}-variant-${variant.variantId}-no-data`,
          group: alias,
          cells: [
            <Stack
              key="version"
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              data-testid={FUNNEL_REPORTS_TEST_IDS.abVariantRow(alias, variant.variantId)}
              sx={versionSubRowCellSx}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{`Variant ${variant.name}`}</Typography>
              {probability && <Chip size="small" variant="outlined" label={`P(beat) ${probability}`} />}
              <Typography variant="caption" color="text.disabled">no data in window</Typography>
            </Stack>,
            ...Array.from({ length: versionColumns.length - 1 }, () => '—' as React.ReactNode),
          ],
          sortValues: {},
        };
      });
    if (memberRows.length === 0 && missingVariantRows.length === 0) {
      // An alias rollup with no version rows in this window (typed absence,
      // never a silent gap): keep the header and say so.
      return [headerRow, {
        key: `${alias}-no-versions`,
        group: alias,
        cells: [
          <Typography key="none" variant="caption" color="text.disabled" sx={versionSubRowCellSx}>no version rows in window</Typography>,
          ...Array.from({ length: versionColumns.length - 1 }, () => '—' as React.ReactNode),
        ],
        sortValues: {},
      }];
    }
    return [headerRow, ...memberRows, ...missingVariantRows];
  });
  // The collapsed row's answer, in the approved artifact's own words:
  // «1 version took traffic on 1 placement · v4 carries all 7,167 starts». The
  // «carries all» clause is only true when ONE version took traffic, so it is
  // the only case that prints it — with more versions the row states the counts
  // and the table below does the ranking.
  const trafficVersions = versionGroupOrder.flatMap(alias => versionGroups.get(alias) ?? []);
  const soleTrafficVersion = trafficVersions.length === 1 ? trafficVersions[0] : undefined;
  const soleTrafficStarts = soleTrafficVersion
    ? (peopleMode ? soleTrafficVersion.startedPeople : soleTrafficVersion.startedJourneys)
    : undefined;
  const activePlacementsAnswer = versionGroupOrder.length === 0
    ? undefined
    : [
      `${trafficVersions.length} ${trafficVersions.length === 1 ? 'version' : 'versions'}`
      + ` took traffic on ${versionGroupOrder.length} ${versionGroupOrder.length === 1 ? 'placement' : 'placements'}`,
      ...(soleTrafficVersion && typeof soleTrafficStarts === 'number'
        ? [`${soleTrafficVersion.identity?.versionName ?? soleTrafficVersion.versionId ?? 'the version'} carries all ${formatInteger(soleTrafficStarts)} starts`]
        : []),
    ].join(' · ');
  // D1 (owner decision 8.1/8.2, 2026-10-07): the two versions-table captions
  // are DELETED. `versionPeopleScope` is still read by the rows themselves; the
  // served scope total and the gate-basis sentence are no longer printed here.
  // Where the four explanation texts return (versions, campaigns, creatives,
  // non-additivity) is ONE later owner decision — and stage 2 (W11) removes the
  // gate-basis divergence itself rather than explaining it.
  void onAliasSelect;
  void onOnboardingVersionSelect;
  void onOpenPlacementEntity;
  void onOpenVariantReport;
  // B7 — honest unmatched-spend guidance on the paid-acquisition section.
  /**
   * B1: the five canonical gates in funnel order, resolved HERE so the band
   * renders and never computes. `MODULE.md:379` owns the contract: Purchase
   * conv is purchases ÷ ALL starts in both units.
   *
   * B3: a people cell with no person twin is a typed absence — a bare `n/a`
   * with NO evidence line. 0% would read as «nobody converted» and the journey
   * number under a people label is this program's named failure (FT-AC-090).
   *
   * Change rows read the RAW `summary` against the RAW `priorSummary`
   * (PE6-AC-006) — never `displaySummary`, which is merged with placement
   * totals and would lie. In PEOPLE mode there is no prior person rollup at
   * all, so the band states that once instead of showing journey deltas under
   * people values.
   */
  const bandGate = (
    cell: Omit<FunnelGateCellModel, 'unavailable'> & { parts?: { top: string; bottom: string } },
  ): FunnelGateCellModel => {
    const value = cell.parts ? cell.parts.top : cell.value;
    const unavailable = isUnavailableDisplayText(value);
    return {
      ...cell,
      value,
      evidence: unavailable ? undefined : (cell.parts ? cell.parts.bottom : cell.evidence),
      change: unavailable ? null : cell.change,
      changeNote: unavailable ? undefined : cell.changeNote,
      qualification: unavailable ? undefined : cell.qualification,
      unavailable,
    };
  };
  const paywallToBuyParts = rateEvidenceParts(
    displaySummary.paywallToBuyClickRate,
    displaySummary.buyButtonClickJourneys,
    displaySummary.paywallReachedJourneys,
  );
  const bandGates: FunnelGateCellModel[] = peopleMode
    ? [
      bandGate({
        id: 'starts',
        label: 'Starts',
        unitNoun: 'people',
        ...(() => {
          const parts = peopleCountParts(peopleRow?.startedPeople, 'entered a funnel');
          return { value: parts.value, evidence: parts.detail };
        })(),
      }),
      bandGate({
        id: 'first-to-second',
        label: 'First → second',
        unitNoun: 'people',
        value: '',
        parts: peopleRateParts(
          peopleRow?.firstToSecondRatePeople,
          peopleRow?.firstToSecondPeople,
          peopleRow?.firstScreenOpenedPeople,
        ),
        qualification: peopleRow?.qualification?.firstToSecondRatePeople,
      }),
      bandGate({
        id: 'paywall',
        label: 'Paywall reached',
        unitNoun: 'people',
        value: '',
        parts: peopleRateParts(peopleRow?.paywallReachRatePeople, peopleRow?.paywallReachedPeople, peopleRow?.startedPeople),
        qualification: peopleRow?.qualification?.paywallReachRatePeople,
      }),
      // Journey-grain in BOTH modes — it has no person twin, so it keeps its
      // own unit noun rather than borrowing the page's (D5).
      bandGate({
        id: 'paywall-to-buy',
        label: 'Paywall to buy',
        unitNoun: 'visits',
        value: '',
        parts: paywallToBuyParts,
        changeNote: 'buy button pressed',
      }),
      // The gate is labelled for what it COUNTS, which depends on the basis:
      //  - attribution (W11, the default now): `purchasePeople` is
      //    purchase_recorded with trials excluded → «Purchase conv»;
      //  - K-window fallback: the scope gate counts a person's FIRST conversion
      //    (`jff converted_at = MIN(TrialStarted, Purchase)`), so it is trial
      //    inclusive and must NOT sit under a purchase word.
      bandGate({
        id: 'purchase-conv',
        label: bandPeopleBasis === 'attribution' ? 'Purchase conv' : 'Converted (trial or purchase)',
        unitNoun: 'people',
        value: '',
        parts: peopleRateParts(peopleRow?.purchaseConversionPeople, peopleRow?.purchasePeople, peopleRow?.startedPeople),
        qualification: peopleRow?.qualification?.purchaseConversionPeople,
      }),
    ]
    : [
      bandGate({
        id: 'starts',
        label: 'Starts',
        unitNoun: 'visits',
        value: formatInteger(displaySummary.startedJourneys),
        evidence: 'entered a funnel',
        change: kpiDeltaFor(summary => summary.startedJourneys, startedSeries),
      }),
      bandGate({
        id: 'first-to-second',
        label: 'First → second',
        unitNoun: 'visits',
        value: '',
        parts: rateEvidenceParts(
          displaySummary.firstToSecondRate,
          displaySummary.firstToSecondJourneys,
          displaySummary.firstScreenOpenedJourneys,
        ),
        // The RAW summary carries no firstScreenOpened/firstToSecond counts, so
        // this gate has no prior-period twin to compare against. B1: then
        // nothing renders — never a placeholder, never a delta borrowed from a
        // differently-defined ratio.
        qualification: displaySummary.qualification?.firstToSecondRate,
      }),
      bandGate({
        id: 'paywall',
        label: 'Paywall reached',
        unitNoun: 'visits',
        value: '',
        parts: rateEvidenceParts(displaySummary.paywallReachRate, displaySummary.paywallReachedJourneys, displaySummary.startedJourneys),
        change: kpiDeltaFor(
          summary => safeRate(summary.paywallReachedJourneys, summary.startedJourneys),
          [],
        ),
        qualification: displaySummary.qualification?.paywallReachRate,
      }),
      bandGate({
        id: 'paywall-to-buy',
        label: 'Paywall to buy',
        unitNoun: 'visits',
        value: '',
        parts: paywallToBuyParts,
        // The RAW summary counts buy CLICKS where the displayed rate counts
        // buy-click JOURNEYS. Comparing the two definitions period over period
        // would put a different ratio under the same word, so this gate states
        // what it measures instead of a delta it cannot honestly compute.
        changeNote: 'buy button pressed',
      }),
      bandGate({
        id: 'purchase-conv',
        label: 'Purchase conv',
        unitNoun: 'visits',
        value: '',
        parts: rateEvidenceParts(
          safeRate(displaySummary.purchaseJourneys, displaySummary.startedJourneys),
          displaySummary.purchaseJourneys,
          displaySummary.startedJourneys,
        ),
        change: kpiDeltaFor(
          summary => safeRate(summary.purchaseJourneys, summary.startedJourneys),
          [],
        ),
        qualification: displaySummary.qualification?.purchaseConversion,
      }),
    ];
  // B3: the reason is stated ONCE for the band, with how many gates it hit, and
  // a display-only escape to visits. No cell repeats it.
  const peopleGatesWithoutTwin = peopleMode
    ? bandGates.filter(gate => gate.unavailable).length
    : 0;
  const bandAbsence = peopleGatesWithoutTwin > 0
    ? {
      // B3: the reason is stated ONCE, with how many gates it hit — and it keeps
      // the SPECIFIC reason the cells used to carry. «Not served» alone would
      // drop the part the reader needs: the page refuses to sum placement rows
      // into a people headline because people are not additive across them.
      // P0-2 (design review): the old sentence claimed «People data is not
      // served for this scope» whenever ANY gate was absent — including at
      // version scope, where the table 200 px below was rendering those very
      // people. It now says which thing is missing, and says it in the reader's
      // words rather than «person twin in this payload».
      message: attributionPeopleScope
        ? `${peopleGatesWithoutTwin} of the ${bandGates.length} gates have no people rate for this window yet — the counts are here, the rates are served separately and this report was built before they were. Refresh the report to get them.`
        : `People data is not served for this scope — ${peopleGatesWithoutTwin} of the ${bandGates.length} gates have none. ${
          data.aliasSummaries.length > 1
            ? 'People are not additive across placements, so these rows are never summed into a headline.'
            : 'People data needs a refresh for this scope.'
        }`,
      onShowVisits,
    }
    : undefined;
  // PE-6b (D-P6): the change rows' BASIS is stated once on the band and never
  // silently reinterpreted — a prior-period comparison and the half-window
  // split are different claims. With no change row rendered, neither is said.
  const bandShowsChange = bandGates.some(gate => typeof gate.change === 'number' && Number.isFinite(gate.change));
  const bandComparisonLabel = !bandShowsChange
    ? undefined
    : kpiDeltaContext ?? 'vs first half of window';
  const abDecisionColumns: EvidenceColumn[] = [
    { id: 'test', label: 'Test / iteration', sortable: true, minWidth: 190 },
    { id: 'variant', label: 'Variant', sortable: true, minWidth: 130 },
    { id: 'status', label: 'Status', sortable: true, minWidth: 126 },
    { id: 'traffic', label: 'Traffic', sortable: true, minWidth: 126 },
    { id: 'earlyFunnel', label: 'Early funnel', sortable: true, minWidth: 132 },
    { id: 'revenue', label: 'Revenue', sortable: true, minWidth: 140 },
    { id: 'paidEconomics', label: 'Paid economics', sortable: true, minWidth: 148 },
    { id: 'confidence', label: 'Decision confidence', sortable: true, minWidth: 164 },
    { id: 'flags', label: 'Flags', minWidth: 190 },
  ];
  const abDecisionRows = buildABDecisionEvidenceRows(abTestDecisionRows, displaySummary.currency);

  return (
    <Stack spacing={3} data-testid={FUNNEL_REPORTS_TEST_IDS.nativeDashboard}>
      {/* C4: the intro block is deleted — a page title, a paragraph restating
          what the page is, and a «Showing N available report sections» alert
          that was on screen on every load. A banner that is always there
          carries no signal, and the counter it held is the reason a hidden
          section could be named to a reader who cannot open it (AC-003).
          The two CONDITIONAL banners stay and are NOT in scope (plan §3 S4):
          the structural page banner for the all-sections-failed case
          (R0-AC-005) and the report-level trust banner (R4-AC-001), both owned
          by FunnelReportsPage. */}

      {/* B1–B3 (IMPLEMENTATION_SPEC): ONE Funnel gates band replaces the
          «Project overview» card AND the KPI grid that repeated most of it.
          The test id is unchanged so every consumer of the canonical gates
          keeps its anchor. B2: «First screen opened» (always 100%), «Offer
          conversions» (the same purchases count), «Paid · people», the
          step-detail caption row and the «KPI cards show cumulative values…»
          caption are gone — nothing on the page renders a metric twice. */}
      <FunnelGatesBand
        gates={bandGates}
        comparisonLabel={bandComparisonLabel}
        // P1-9 (design review): the old sentence spent the band's best line on
        // internal vocabulary («visit-grain», «person twin», «payload») and on
        // reassuring the reader about denominators that are printed under every
        // rate anyway. It says the one thing a reader can act on.
        comparisonAbsenceLabel={peopleMode && priorSummary
          ? 'no change vs the previous period: the comparison is available for visits only'
          : undefined}
        revenue={{
          label: 'Revenue',
          unitNoun: 'visits',
          value: formatNullableCurrency(displaySummary.purchaseAmount, displaySummary.currency),
          evidence: 'money counts visits',
        }}
        // P1-5 (design review): a bare «0» reads as «zero problems». It is a
        // 0–100 health score, so it carries its denominator like every rate on
        // this band does.
        qualityScore={displaySummary.minHealthScore === null ? 'n/a' : `${formatInteger(displaySummary.minHealthScore)} / 100`}
        qualityChip={(
          <QualitySeverityChip
            severity={displaySummary.dataQualitySeverity || 'unknown'}
            quantified={qualityQuantified}
            warningsReachable={roleAllows('warnings')}
          />
        )}
        absence={bandAbsence}
        nonAdditivityCaption={peopleScopeCaption || undefined}
      />

      {(displaySummary.moneyTotalsState === 'mixed_currency'
        || displaySummary.moneyTotalsState === 'missing_currency') && (
        <Alert severity="warning">
          <AlertTitle>Money currency evidence incomplete</AlertTitle>
          Purchase-event and initial-billing totals are unavailable because the selected scope contains
          mixed currencies, missing currency evidence, or an unknown currency exponent. Native payment
          rows remain visible; no implicit FX conversion or `/100` assumption is applied.
        </Alert>
      )}

      {showSection('active-placements') && (
        <ChartSection
          id="active-placements"
          title="Active Funnel Versions"
          empty={!availability['active-placements']}
          error={sectionErrorById.get('active-placements')}
          // Owner round 5 in the generator: BOTH lead sentences go — «the
          // accordion row already says what this section answers». The question
          // moved out of the body and the answer moved into the collapsed row.
          summary={activePlacementsAnswer}
        >
          <Stack spacing={2}>
            {/* D1/D3: the lead paragraph is deleted — the card header already says
                what the section answers — and the two controls sit on ONE
                right-aligned row directly under the title. */}
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" useFlexGap flexWrap="wrap">
              {trafficView ? (
                <Button
                  size="small"
                  variant="outlined"
                  data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesBack}
                  onClick={() => onTrafficViewChange?.(null)}
                >
                  ‹ Back to report
                </Button>
              ) : (
                <>
                <Button
                  size="small"
                  data-testid={FUNNEL_REPORTS_TEST_IDS.leaderboardColumnsToggle}
                  onClick={() => setShowAllLeaderboardColumns(current => !current)}
                >
                  {showAllLeaderboardColumns ? 'Show primary gates only' : 'Show all columns'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesOpen}
                  onClick={() => onTrafficViewChange?.('campaigns')}
                  disabled={!onTrafficViewChange}
                >
                  Traffic sources ▸
                </Button>
                </>
              )}
            </Stack>
            {trafficView ? (
              <TrafficSourcesPanel
                data={data}
                unit={funnelUnit}
                view={trafficView}
                onViewChange={view => onTrafficViewChange?.(view)}
                loadCreativesPerformance={loadCreativesPerformance}
                currency={displaySummary.currency}
                scopeChips={[
                  ...(scopeOnboardingId ? [`onboarding ${scopeOnboardingId}`] : []),
                  ...versionGroupOrder.map(alias => `${alias} · ${(versionGroups.get(alias) ?? []).length} ${(versionGroups.get(alias) ?? []).length === 1 ? 'version' : 'versions'}`),
                ]}
                sortState={activePlacementSort}
                onSort={columnId => setActivePlacementSort(current => nextSortState(current, columnId))}
              />
            ) : (
            <>
            {/* D1 (owner decision 8.1/8.2, 2026-10-07): BOTH captions are gone —
                `versionsUnitCaption` and `versionsGateBasisCaption`. Everything
                on the page is read as if people mode is on, which is the house
                rule, and the grain rides the COLUMN labels (D5) instead of a
                230-character paragraph. Where the four explanation texts return
                (versions, campaigns, creatives, non-additivity) is a separate
                owner decision taken for all four at once. The band still carries
                the non-additivity statement (FT-AC-111 / AC-019).
                D2: the «Traffic distribution chart» is gone too — two bars under
                a raw placement id, no legend, no axis labels, and no number the
                table below does not already give. */}
            <SortableEvidenceTable
              id="active-placements"
              columns={pickLeaderboardColumns(versionColumns, VERSION_PRIMARY_COLUMN_INDEXES)}
              rows={versionRows.map(row => (row.groupHeader ? row : { ...row, cells: pickLeaderboardColumns(row.cells, VERSION_PRIMARY_COLUMN_INDEXES) }))}
              sortState={activePlacementSort}
              onSort={columnId => setActivePlacementSort(current => nextSortState(current, columnId))}
              // D4: the list of versions is what this section exists to show, so
              // it is never clamped into an inner scroll pane. A cap would have
              // to sit well above the common case; 320 px showed three rows.
              unclamped
            />
            </>
            )}
          </Stack>
        </ChartSection>
      )}

      {/* C2: DECISION order, not render order — the answer, then what produced
          it, then diagnosis. Traffic by version (above) answers «which version
          carries it», Paid Acquisition answers «does the spend pay back», and
          «What changed» is the first diagnosis a reader reaches for; Funnel
          health and Payments produced those answers, so they follow, and the
          deeper diagnosis (screen flow, trends, root cause, artifacts, data
          quality) comes after. The first implementation put Funnel health and
          Payments ABOVE the paid answer, which also made the Root-cause pointer
          («full audit in Root Cause below») point upwards. */}
      {!hasAnyData && !showEmptySections ? null : (
      <Grid container spacing={2}>
        {showSection('paid-acquisition') && (
        <Grid size={{ xs: 12 }}>
          <PaidAcquisitionSection
            rows={data.campaignEconomics}
            coverage={data.campaignEconomicsCoverage}
            currency={displaySummary.currency}
            loadCampaignCreatives={loadCampaignCreatives}
            empty={!availability['paid-acquisition']}
            error={sectionErrorById.get('paid-acquisition')}
            showEmptySections={showEmptySections}
            verdictThresholds={data.verdictThresholds}
            projectId={projectId}
            creativeDecisionsEnabled={creativeDecisionsEnabled}
            realizedValueBasis={data.realizedValueBasis}
            realizedGrossBasis={data.realizedGrossBasis}
            netCollectedBasis={data.netCollectedBasis}
            settlementBasis={data.settlementBasis}
            projectedValueBasis={data.projectedValueBasis}
            projectedValueBases={data.projectedValueBases}
            moneyRootCause={data.moneyRootCause}
            moneyDiagnosis={data.moneyDiagnosis}
            paidSummaryTiles={data.paidSummaryTiles}
            onVerdictThresholdsSaved={onVerdictThresholdsSaved}
            ltvNetOfRefunds={data.ltvNetOfRefunds}
            attribution={data.campaignEconomicsAttribution}
            onAttributionModelChange={onAttributionModelChange}
            attributionSwitchPending={attributionSwitchPending}
          />
        </Grid>
        )}
        {roleAllows('ab-test-decisions') && (showEmptySections || abTestDecisionRows.length > 0) && (
        <Grid size={{ xs: 12 }}>
          <ChartSection
            id="ab-test-decisions"
            title="A/B Test Decisions"
            empty={abTestDecisionRows.length === 0}
            description="Which A/B variant is likely better for the selected primary metric, and is the evidence strong enough to act?"
          >
            <Stack spacing={2}>
              {abTestDecisionRows.map(decision => (
                <DecisionScorecard
                  key={`${decision.testId}-${decision.iterationId}`}
                  row={decision}
                  actions={decisionActionsFor?.(decision)}
                />
              ))}
              <Typography variant="body2" color="text.secondary">
                Decision rows compare variants within each published A/B iteration. Paid economics are optional and stay n/a when Meta spend is not connected or not matched.
              </Typography>
              <SortableEvidenceTable
                id="ab-test-decisions"
                columns={abDecisionColumns}
                rows={abDecisionRows}
                sortState={abDecisionSort}
                onSort={columnId => setABDecisionSort(current => nextSortState(current, columnId))}
              />
            </Stack>
          </ChartSection>
        </Grid>
        )}
        {showSection('anomaly-inbox') && (
        <Grid size={{ xs: 12 }}>
          <ChartSection
            id="anomaly-inbox"
            title="What changed"
            /* R3-4: zero anomalies is a healthy STATEMENT (feed renders «All
               healthy»), not a missing-rows state — never the bare No-rows alert. */
            empty={false}
            error={sectionErrorById.get('anomaly-inbox')}
            defaultExpanded={showEmptySections || undefined}
            summary={anomalyInboxAnswer}
            description="One feed of unusual metric movements with their top drivers inline — ranked by severity, confidence, recurrence, revenue impact, and paid exposure."
          >
            <Stack spacing={2}>
              {/* R3-4 (D-Q5): merged insight-card feed leads; the audit table stays below. */}
              <WhatChangedFeed
                anomalies={data.anomalies.slice(0, 8)}
                drivers={data.rootCauseDrivers}
                currency={displaySummary.currency}
              />
              <AnomalyReadableSummary anomalies={data.anomalies} currency={displaySummary.currency} />
              <EvidenceTable
                id="anomaly-inbox"
                columns={visibleAnomalyColumns.map(column => column.header)}
                rows={data.anomalies.slice(0, 8).map(anomaly => visibleAnomalyColumns.map(column => column.cell(anomaly)))}
              />
            </Stack>
          </ChartSection>
        </Grid>
        )}
        {showSection('funnel-health') && (
        <Grid size={{ xs: 12, lg: 7 }}>
          <ChartSection
            id="funnel-health"
            title="Funnel Health"
            empty={!availability['funnel-health']}
            error={sectionErrorById.get('funnel-health')}
            summary="daily stoplight for the revenue-critical steps"
            description="Daily stoplight for revenue-critical steps: did traffic reach paywall, buy intent, offers, purchases, and trustworthy data quality?"
          >
            <FunnelHealthSummary
              dailyRows={data.funnelHealthDaily}
              currency={displaySummary.currency}
              moneyAvailable={displaySummary.moneyTotalsState !== 'mixed_currency'
                && displaySummary.moneyTotalsState !== 'missing_currency'}
            />
            <EvidenceTable
              id="funnel-health"
              // D-U1: journey-grain throughout, so every metric column says
              // `· visits` in BOTH modes — this table has no person twin at all.
              columns={['Date', 'Starts · visits', 'Paywall reached · visits', 'Buy clicks · visits',
                'Offers · visits', 'Trials · visits', 'Purchases · visits', 'Revenue · visits', 'Reason']}
              rows={data.funnelHealthDaily.map(row => [
                row.reportDate,
                formatInteger(row.startedJourneys),
                formatInteger(row.paywallReachedJourneys),
                formatInteger(row.buyButtonClicks),
                formatInteger(row.offerConvertedJourneys),
                formatInteger(row.trialStartedJourneys),
                formatInteger(row.purchaseJourneys),
                displaySummary.moneyTotalsState === 'mixed_currency'
                  || displaySummary.moneyTotalsState === 'missing_currency'
                  ? 'n/a'
                  : formatCurrency(row.purchaseAmount, displaySummary.currency),
                funnelHealthReason(row),
              ])}
            />
          </ChartSection>
        </Grid>
        )}
        {showSection('payments') && (
        <Grid size={{ xs: 12, lg: 5 }}>
          <ChartSection
            id="payments"
            title="Payments And Revenue"
            empty={!availability.payments}
            error={sectionErrorById.get('payments')}
            defaultExpanded={showEmptySections || undefined}
            summary="attributed purchases, reconciled gross, net, settlement"
            description="How many purchases were attributed, and what their reconciled gross, net-collected, settlement-cash, and contribution value is."
          >
            {/* R2-3: staged paywall rates per funnel entrant + revenue-per-start headline (P11). */}
            {revenuePerFunnelStart && (
              <Typography variant="body2" data-testid={FUNNEL_REPORTS_TEST_IDS.paymentsRevenuePerStart} sx={{ mb: 1 }}>
                Revenue per funnel start: <Box component="b">{revenuePerFunnelStart}</Box>{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  (purchase revenue / started journeys)
                </Typography>
              </Typography>
            )}
            {paymentsRateRows.length > 1 && (
              <Box data-testid={FUNNEL_REPORTS_TEST_IDS.paymentsRates}>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={paymentsRateRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" minTickGap={18} />
                    <YAxis domain={[0, 1]} tickFormatter={(value: number) => `${Math.round(value * 100)}%`} />
                    <RechartsTooltip formatter={ratioTooltipFormatter} />
                    <Line type="monotone" dataKey="paywallRate" name="Paywall reach / start" stroke={chart.primary} dot={false} />
                    <Line type="monotone" dataKey="trialRate" name="Trials / start" stroke={chart.secondary} dot={false} />
                    <Line type="monotone" dataKey="purchaseRate" name="Purchases / start" stroke={chart.positive} dot={false} />
                    {paymentsLastDayPartial && (
                      <>
                        <Line type="monotone" dataKey="paywallRateTail" name="Paywall reach (partial day)" stroke={chart.primary} strokeDasharray="5 4" dot={false} legendType="none" />
                        <Line type="monotone" dataKey="trialRateTail" name="Trials (partial day)" stroke={chart.secondary} strokeDasharray="5 4" dot={false} legendType="none" />
                        <Line type="monotone" dataKey="purchaseRateTail" name="Purchases (partial day)" stroke={chart.positive} strokeDasharray="5 4" dot={false} legendType="none" />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
            <DataThroughCaption
              dataThrough={dataThrough}
              partial={paymentsLastDayPartial}
              testId={FUNNEL_REPORTS_TEST_IDS.paymentsDataThrough}
            />
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart data={data.paymentOutcomes.map(row => ({
                ...row,
                purchases: row.purchases ?? row.subscriptions,
                paidPurchases: row.paidPurchases ?? row.paidSubscriptions,
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="outcome" />
                <YAxis />
                <RechartsTooltip formatter={tooltipNumberFormatter} />
                <Bar dataKey="purchases" name="Purchases" fill={chart.primary} />
                <Bar dataKey="paidPurchases" name="Paid purchases" fill={chart.positive} />
              </BarChart>
            </ResponsiveContainer>
            <EvidenceTable
              id="payments"
              columns={['Outcome', 'Type', 'Offer', 'Purchases', 'Paid', 'Gross', 'Net collected', 'Settlement cash', 'Contribution']}
              rows={data.paymentOutcomes.map(row => [
                row.outcome,
                formatPurchaseMode(row.purchaseMode),
                row.offerType || 'n/a',
                formatInteger(row.purchases ?? row.subscriptions),
                formatInteger(row.paidPurchases ?? row.paidSubscriptions),
                formatPaymentBasisMinorUnits(
                  row.grossCollected === undefined ? row.lifetimeValue : row.grossCollected,
                  row.currency || displaySummary.currency,
                  row.currencyExponent,
                  row.valueBasis?.gross,
                ),
                formatPaymentBasisMinorUnits(
                  row.netCollected,
                  row.currency || displaySummary.currency,
                  row.currencyExponent,
                  row.valueBasis?.netCollected,
                ),
                formatPaymentBasisMinorUnits(
                  row.settlementCash,
                  row.currency || displaySummary.currency,
                  row.currencyExponent,
                  row.valueBasis?.settlementCash,
                ),
                formatPaymentBasisMinorUnits(
                  row.contribution,
                  row.currency || displaySummary.currency,
                  row.currencyExponent,
                  row.valueBasis?.contribution,
                ),
              ])}
            />
          </ChartSection>
        </Grid>
        )}
      </Grid>
      )}

      {/* P1-8 (design review): an empty `Grid container` still occupies a grid
          row, which left a ~50 px phantom gap at project scope, where both of
          these sections are hidden. It renders only when it can have a child. */}
      {showFlowSections && (hasAnyData || showEmptySections) && (
      <Grid container spacing={2}>
        {showFlowSections && showSection('screen-flow') && (
        <Grid size={{ xs: 12, lg: scopeMode === 'version' ? 12 : 6 }}>
          <ChartSection
            id="screen-flow"
            title="Screen Flow And Dropoff"
            empty={!availability['screen-flow']}
            error={sectionErrorById.get('screen-flow')}
            description={scopeMode === 'version'
              ? undefined /* the hero carries its own caption + view toggle */
              : 'Which routes are users actually taking through this concrete onboarding/version?'}
          >
            {scopeMode === 'version' ? (
              /* R2-1/R2-2/R2-4 (D10): funnel-first hero at version scope; the
                 transitions table is demoted into the hero's Table view. */
              <FunnelHeroSection
                edges={data.screenFlowEdges}
                startedJourneys={displaySummary.startedJourneys}
                metricTimeseries={data.metricTimeseries}
                formatMetricLabel={formatMetricLabel}
                dataThrough={dataThrough}
                canvasHrefForScreen={projectId && scopeOnboardingId
                  // F2: the deep link carries the page unit so the canvas
                  // report opens in the SAME counting unit (FT-AC-083). F4:
                  // the fragment helper owns the inversion — the old
                  // `peopleMode ? '&unit=people' : ''` sent a visits reader
                  // into a people canvas once people became the default.
                  ? screenId => `/project/${projectId}/onboarding-v2/${scopeOnboardingId}?insights=1&focusScreen=${encodeURIComponent(screenId)}${funnelUnitQueryFragment(funnelUnit)}`
                  : undefined}
              >
                {screenFlowEvidenceTable}
              </FunnelHeroSection>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={data.screenFlowEdges.slice(0, 8)} layout="vertical" margin={{ left: 24, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey={edgeLabel} width={160} />
                    <RechartsTooltip formatter={tooltipNumberFormatter} labelFormatter={edgeTooltipLabel} />
                    <Bar dataKey="transitionCount" name="Transitions" fill={chart.info} />
                  </BarChart>
                </ResponsiveContainer>
                {screenFlowEvidenceTable}
              </>
            )}
          </ChartSection>
        </Grid>
        )}
        {showFlowSections && showSection('metric-timeseries') && (
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartSection
            id="metric-timeseries"
            title="Metric trends"
            empty={!availability['metric-timeseries']}
            error={sectionErrorById.get('metric-timeseries')}
            summary="canonical metrics over time with numerator and denominator"
            description="Which canonical metrics moved over time, using the same numerator and denominator rows that power anomaly checks?"
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={pivotMetricRows(data.metricTimeseries)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" minTickGap={18} />
                <YAxis />
                <RechartsTooltip formatter={tooltipNumberFormatter} />
                <Line type="monotone" dataKey="purchase_amount" name="Purchase amount" stroke={chart.positive} dot={false} />
                <Line type="monotone" dataKey="offer_converted_journeys" name="Offer conversions" stroke={chart.warning} dot={false} />
                <Line type="monotone" dataKey="trial_started_journeys" name="Trials" stroke={chart.secondary} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            {/* R4-3: buckets are arbitrary-grained here — the honest marker is the
                caption alone, no dashed tail (documented deviation). */}
            <DataThroughCaption dataThrough={dataThrough} testId={FUNNEL_REPORTS_TEST_IDS.metricTrendsDataThrough} />
            <EvidenceTable
              id="metric-timeseries"
              columns={['Bucket', 'Metric', 'Value', 'Numerator', 'Denominator', 'Quality']}
              rows={data.metricTimeseries.slice(0, 12).map(row => [
                row.bucketTs.slice(0, 16).replace('T', ' '),
                formatMetricLabel(row.metricName),
                formatMetricValue(row.metricName, row.metricValue, displaySummary.currency),
                formatMetricComponent(row.metricName, row.numerator, displaySummary.currency),
                row.denominator > 0 ? formatNumber(row.denominator) : 'n/a',
                <QualitySeverityChip severity={row.dataQualitySeverity} />,
              ])}
            />
          </ChartSection>
        </Grid>
        )}
      </Grid>
      )}

      {!hasAnyData && !showEmptySections ? null : (
      <Grid container spacing={2}>
        {showSection('root-cause') && (
        <Grid size={{ xs: 12 }}>
          <ChartSection
            id="root-cause"
            title="Root Cause And Variables"
            empty={!availability['root-cause']}
            error={sectionErrorById.get('root-cause')}
            defaultExpanded={showEmptySections || undefined}
            summary="ranked drivers behind the moves above"
            description="Which affected segment, screen, route, product, or traffic dimension should be inspected first for the anomaly?"
          >
            <Stack spacing={2}>
              <RootCauseReadableSummary drivers={data.rootCauseDrivers} currency={displaySummary.currency} />
              <RootCauseMetricGuide />
              <CollapsedChart title="Driver impact chart">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={data.rootCauseDrivers.slice(0, 8)} layout="vertical" margin={{ left: 32, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey={driverLabel} width={160} />
                    <RechartsTooltip formatter={tooltipNumberFormatter} />
                    <Bar dataKey="impactScore" name="Impact score" fill={chart.negative} />
                  </BarChart>
                </ResponsiveContainer>
              </CollapsedChart>
              <EvidenceTable
                id="root-cause"
                columns={['Metric', 'Rank', 'Affected segment / driver', 'Impact score', 'Metric delta', 'Est. revenue delta', 'How to use it']}
                minWidths={[180, 84, 220, 128, 128, 144, 300]}
                rows={data.rootCauseDrivers.slice(0, 8).map(row => [
                  formatMetricLabel(row.metricName),
                  `Rank ${formatInteger(row.contributionRank)}`,
                  <DriverEvidenceCell key={`driver-${row.contributionId}`} row={row} />,
                  formatDriverImpactScore(row, displaySummary.currency),
                  formatRate(row.relativeDelta),
                  formatCurrency(row.estimatedRevenueDelta, displaySummary.currency),
                  driverInvestigationHint(row),
                ])}
              />
            </Stack>
          </ChartSection>
        </Grid>
        )}
        {showFlowSections && showSection('artifacts') && (
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartSection
            id="artifacts"
            title="Artifacts And Creatives"
            empty={!availability.artifacts}
            error={sectionErrorById.get('artifacts')}
            defaultExpanded={showEmptySections || undefined}
            summary="per-creative evidence behind the paid numbers"
            description="Which deterministic published artifact is attached to the observed screen or paywall performance?"
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <PieChart>
                <Pie
                  data={data.artifacts.slice(0, 7)}
                  dataKey="screenVisitors"
                  nameKey="artifactKey"
                  outerRadius={92}
                  label={artifactPieLabel}
                >
                  {data.artifacts.slice(0, 7).map((artifact, index) => (
                    <Cell key={artifact.artifactKey} fill={chart.series[index % chart.series.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={tooltipNumberFormatter} />
              </PieChart>
            </ResponsiveContainer>
            <EvidenceTable
              id="artifacts"
              columns={['Artifact', 'Screen', 'Visitors', 'Exits', 'Buy clicks', 'Offers', 'Revenue', 'Paywall CR']}
              rows={data.artifacts.slice(0, 8).map(row => [
                row.artifactKey,
                row.screenName || row.screenId || 'n/a',
                formatInteger(row.screenVisitors),
                formatInteger(row.screenExits),
                formatInteger(row.buyButtonClicks),
                formatInteger(row.offerConvertedJourneys),
                formatCurrency(row.purchaseAmount, displaySummary.currency),
                formatRate(row.paywallToOfferConversionRate),
              ])}
            />
          </ChartSection>
        </Grid>
        )}
        {showSection('data-quality') && (
        // P1-8: `artifacts` is its grid partner and renders only at version
        // scope, so at project scope a fixed half width left a lone card beside
        // an empty half.
        <Grid size={{ xs: 12, lg: showFlowSections ? 6 : 12 }}>
          <ChartSection
            id="data-quality"
            title="Data Quality"
            empty={!availability['data-quality']}
            error={sectionErrorById.get('data-quality')}
            defaultExpanded={showEmptySections || undefined}
            summary={dataQualityAnswer}
            description="Can this report window be trusted, or are freshness, duplicate, null, or coverage issues suppressing interpretation?"
          >
            <Stack spacing={2}>
              {/* R4-2: the health VERDICT leads; per-bucket evidence demotes into the audit table. */}
              {data.dataQuality.length > 0 && (
                <Box data-testid={FUNNEL_REPORTS_TEST_IDS.dataQualityHealthLine}>
                  <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
                      {formatInteger(data.dataQuality[data.dataQuality.length - 1].healthScore)}
                    </Typography>
                    <QualitySeverityChip
                      severity={data.dataQuality[data.dataQuality.length - 1].severity}
                      quantified={qualityQuantified}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {formatInteger(qualityTotals.issues)} issues / {formatInteger(qualityTotals.duplicates)} duplicates in window
                    </Typography>
                    <KpiSparkline points={data.dataQuality.map(row => row.issueCount)} stroke={chart.negative} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Health score for the latest day in the window; sparkline tracks daily issue count.
                  </Typography>
                </Box>
              )}
              <CollapsedChart title="Health trend chart">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <LineChart data={dataQualityTrendRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="reportDate" minTickGap={16} />
                    <YAxis />
                    <RechartsTooltip formatter={tooltipNumberFormatter} />
                    <Line type="monotone" dataKey="healthScore" name="Health score" stroke={chart.positive} />
                    <Line type="monotone" dataKey="issueCount" name="Issues" stroke={chart.negative} />
                    {dataQualityTail.partial && (
                      <>
                        <Line type="monotone" dataKey="healthScoreTail" name="Health score (partial day)" stroke={chart.positive} strokeDasharray="5 4" legendType="none" />
                        <Line type="monotone" dataKey="issueCountTail" name="Issues (partial day)" stroke={chart.negative} strokeDasharray="5 4" legendType="none" />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
                <DataThroughCaption
                  dataThrough={dataThrough}
                  partial={dataQualityTail.partial}
                  testId={FUNNEL_REPORTS_TEST_IDS.dataQualityDataThrough}
                />
              </CollapsedChart>
              <Box component="details">
                <Box component="summary" sx={{ cursor: 'pointer', typography: 'body2', fontWeight: 600, py: 0.5 }}>
                  Audit table
                </Box>
                <EvidenceTable
                  id="data-quality"
                  columns={['Bucket', 'Health', 'Raw', 'Deduped', 'Duplicates', 'Issues', 'Severity']}
                  rows={data.dataQuality.map(row => [
                    row.bucketTs.slice(0, 16).replace('T', ' '),
                    formatInteger(row.healthScore),
                    formatInteger(row.rawEventCount),
                    formatInteger(row.dedupedEventCount),
                    formatInteger(row.duplicateEventIdCount),
                    formatInteger(row.issueCount),
                    <QualitySeverityChip severity={row.severity} />,
                  ])}
                />
              </Box>
            </Stack>
          </ChartSection>
        </Grid>
        )}
      </Grid>
      )}

      {roleAllows('warnings') && <ReportWarningsSection dashboard={data} />}
    </Stack>
  );
}

/**
 * R4-3 (generalizes payments R2-3): split DAILY rows into solid + dashed-tail
 * series when the envelope freshness bound falls inside the last plotted day.
 * The tail spans the last two indexes so the dashed segment connects visually.
 */
function splitDailyTail<T>(
  rows: T[],
  dateOf: (row: T) => string,
  dataThrough: string | undefined,
): { partial: boolean; isSolid: (index: number) => boolean; isTail: (index: number) => boolean } {
  const lastDate = rows.length > 0 ? dateOf(rows[rows.length - 1]) : undefined;
  const partial = Boolean(dataThrough && lastDate && dataThrough.slice(0, 10) === lastDate);
  return {
    partial,
    isSolid: index => !partial || index < rows.length - 1,
    isTail: index => partial && index >= rows.length - 2,
  };
}

function normalizeDashboardData(data: FunnelReportsDashboardData): FunnelReportsDashboardData {
  return {
    ...data,
    aliasSummaries: Array.isArray(data.aliasSummaries) ? data.aliasSummaries : [],
    aliasOnboardingSummaries: Array.isArray(data.aliasOnboardingSummaries) ? data.aliasOnboardingSummaries : [],
    funnelHealthDaily: Array.isArray(data.funnelHealthDaily) ? data.funnelHealthDaily : [],
    metricTimeseries: Array.isArray(data.metricTimeseries) ? data.metricTimeseries : [],
    screenFlowEdges: Array.isArray(data.screenFlowEdges) ? data.screenFlowEdges : [],
    paymentOutcomes: Array.isArray(data.paymentOutcomes) ? data.paymentOutcomes : [],
    anomalies: Array.isArray(data.anomalies) ? data.anomalies : [],
    rootCauseDrivers: Array.isArray(data.rootCauseDrivers) ? data.rootCauseDrivers : [],
    artifacts: Array.isArray(data.artifacts) ? data.artifacts : [],
    dataQuality: Array.isArray(data.dataQuality) ? data.dataQuality : [],
    campaignEconomics: Array.isArray(data.campaignEconomics) ? data.campaignEconomics : [],
  };
}

/** One stat in the Project Overview card (AC-807) — denser than a KpiTile. */
/** Tiny inline sparkline for KPI tiles (R1-3); pure SVG, colors from useChartTheme. */
function KpiSparkline({ points, stroke }: { points: number[]; stroke: string }): React.ReactElement | null {
  if (points.length < 2) return null;
  const width = 72;
  const height = 22;
  const max = Math.max(...points, 1);
  const path = points
    .map((value, index) => `${((index / (points.length - 1)) * width).toFixed(1)},${(height - (value / max) * (height - 3) - 1).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={width} height={height} aria-hidden="true">
      <polyline points={path} fill="none" stroke={stroke} strokeWidth={1.5} />
    </svg>
  );
}

/**
 * C1–C3 (`.design/studio-reports/IMPLEMENTATION_SPEC.md`): EVERY report section
 * is one collapsible card whose header IS the toggle — title left, chevron
 * right. Expanded, the body carries the whole section, nothing truncated.
 *
 * Three rules this component owns, so no call site can get them wrong:
 *
 * 1. **Collapsed by default, except `active-placements`** (C1). The default is
 *    derived from the section id here rather than passed per call site — the
 *    old `collapsible` / `defaultExpanded` pair let five sections render as
 *    permanently-open boxes and two others as accordions, which is how the
 *    page grew to ~7 000 px.
 * 2. **Collapse and label per SECTION ID, never per grid row** (C3). Two
 *    sections share a dashboard grid row, and in the mock's first hierarchy
 *    pass that paired one section's header with another's body. The title comes
 *    from `SECTION_TITLES[id]`, so a header can only ever name its own body.
 * 3. **Collapsing never unmounts the body** (AC-012). MUI keeps `Collapse`
 *    children mounted, and `unmountOnExit` stays OFF: a collapsed section's
 *    rows must still be queryable and must not refetch on re-expand.
 *
 * An errored section is forced open — a failure the reader cannot see is worse
 * than a long page (PE-1).
 */
const SECTION_TITLES: Record<string, string> = {
  'active-placements': 'Traffic by version',
  'paid-acquisition': 'Paid Acquisition',
  'anomaly-inbox': 'What changed',
  'funnel-health': 'Funnel health',
  payments: 'Payments and revenue',
  'screen-flow': 'Screen flow and drop-off',
  'metric-timeseries': 'Metric trends',
  'root-cause': 'Root cause and variables',
  artifacts: 'Artifacts and creatives',
  'data-quality': 'Data quality',
  'ab-test-decisions': 'A/B Test Decisions',
};

/** C1: the one section that opens on load — it carries the A/B answer. */
const SECTION_OPEN_ON_LOAD = 'active-placements';

function ChartSection({
  id,
  title,
  empty,
  error,
  description,
  summary,
  defaultExpanded,
  children,
}: {
  id: string;
  /** Fallback only — `SECTION_TITLES[id]` wins, so C3 cannot be violated. */
  title: string;
  empty: boolean;
  /** PE-1 (handoff §A8): section read failure — degrades the card, never hides it. */
  error?: FunnelReportSectionError;
  description?: string;
  /**
   * The one-line ANSWER the collapsed row carries (approved artifact, owner
   * round 3 — «sections become one-line rows carrying their own summary»).
   * It is hidden once the section opens: the body answers from there on, and
   * the mock hides it the same way (`[open] > summary .s { display: none }`).
   */
  summary?: string;
  /**
   * Overrides the per-id default. Used for `showEmptySections` (a QA sweep
   * wants everything open) and the paid-acquisition deep link.
   */
  defaultExpanded?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const errorCopy = error ? describeReportSectionError(error) : null;
  const sectionTitle = SECTION_TITLES[id] ?? title;

  const body = errorCopy ? (
    <Alert severity={errorCopy.severity} data-testid={FUNNEL_REPORTS_TEST_IDS.chartError(id)}>
      {errorCopy.headline}
      {errorCopy.techDetails && (
        <Box component="details" data-testid={FUNNEL_REPORTS_TEST_IDS.chartErrorDetails(id)} sx={{ mt: 1 }}>
          <Box component="summary" sx={{ cursor: 'pointer' }}>{TECH_DETAILS_LABEL}</Box>
          <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-word' }}>
            {errorCopy.techDetails}
          </Typography>
        </Box>
      )}
    </Alert>
  ) : empty ? (
    <Alert severity="info" data-testid={FUNNEL_REPORTS_TEST_IDS.chartEmpty(id)}>
      No rows for the selected scope and time window.
    </Alert>
  ) : (
    // C1/D4: expanded, the body carries the WHOLE section — no inner scroll
    // pane. Page length is controlled by collapse (C1), not by clamping every
    // section into a 560 px viewport with its own scrollbar, which is how the
    // versions table ended up showing three of its rows. The approved mock
    // sets `max-height:none; overflow:visible` on section bodies for the same
    // reason. `overflowX: hidden` stays: a wide table must not widen the PAGE.
    <Box sx={{ overflowX: 'hidden', pr: 0.5 }}>
      {children}
    </Box>
  );

  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={Boolean(errorCopy) || (defaultExpanded ?? id === SECTION_OPEN_ON_LOAD)}
      data-testid={FUNNEL_REPORTS_TEST_IDS.chart(id)}
      // A11y: MUI wraps `AccordionSummary` in a heading element (default h3).
      // The page title is an h4, so the sections sit one level under it at h5
      // and the visible title inside renders as a span — one heading per
      // section instead of an h3 wrapping an h6 (`skipped-heading`).
      slotProps={{ heading: { component: 'h5' } }}
      sx={{
        // C1 card tokens — the ones Paid Acquisition already used.
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        // MUI's `.MuiAccordion-rounded:first-of-type/:last-of-type` sets 4px and
        // outranks a plain `borderRadius`, so six of the seven section cards
        // rendered at 4px while the one that is not a grid child kept 16px —
        // three radii on one page. Matching the selector restores the DESIGN.md
        // 16px surface radius on all of them.
        '&.MuiAccordion-rounded:first-of-type, &.MuiAccordion-rounded:last-of-type': {
          borderRadius: 4,
        },
        bgcolor: 'background.paper',
        '&::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        data-testid={FUNNEL_REPORTS_TEST_IDS.chartToggle(id)}
        sx={{
          px: 3,
          '& .MuiAccordionSummary-content': { my: 1.5, mr: 2 },
          // Mock parity: the answer belongs to the CLOSED row. Once the section
          // is open its body is the answer, and repeating a summary of it above
          // the real numbers is noise.
          '&.Mui-expanded .funnel-reports-section-answer': { display: 'none' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
            {sectionTitle}
          </Typography>
          {empty && !errorCopy && <Chip size="small" label="No data" />}
          {errorCopy && <Chip size="small" color={errorCopy.severity === 'error' ? 'error' : 'warning'} label="Attention" />}
          {summary && !errorCopy && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              className="funnel-reports-section-answer"
              data-testid={FUNNEL_REPORTS_TEST_IDS.chartSummary(id)}
              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {summary}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
        <Stack spacing={1.5}>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
          {body}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function CollapsedChart({ title, children, testId, unmountOnExit }: {
  title: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
  /** For heavy children (recharts): render only while expanded. Keep OFF when hidden textContent must stay queryable. */
  unmountOnExit?: boolean;
}): React.ReactElement {
  return (
    <Accordion
      disableGutters
      defaultExpanded={false}
      data-testid={testId}
      // A11y: a chart accordion lives INSIDE a section card (h5), so its own
      // heading is h6 — MUI's wrapper defaults to h3 and jumped back up a
      // level in the middle of a section.
      slotProps={{
        heading: { component: 'h6' },
        ...(unmountOnExit ? { transition: { unmountOnExit: true } } : {}),
      }}
      sx={{
        bgcolor: 'transparent',
        border: theme => `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
        {typeof title === 'string' ? (
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        ) : title}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * D5 (`.design/studio-reports/IMPLEMENTATION_SPEC.md`): the rate cell is TWO
 * lines plus an affordance — rate over `n / d`, and the rate itself opens the
 * confidence card.
 *
 * What came out and why:
 *  - the 5 px inline bar: at a 1.5 % rate it is 0.075 px of length, i.e. noise
 *    that still cost a row of vertical space in every cell;
 *  - the `StatTierChip` under the evidence: it repeated the rate and its
 *    interval a third time, in a cell that already had two lines.
 *
 * The verdict is carried by the TEXT as well as the colour: a direction-only
 * rate is prefixed `~ `, so the cell still reads correctly in greyscale and to
 * a reader who cannot distinguish the green from the amber (F3).
 *
 * This component does NO statistics. Every CI bound comes from the server's
 * `qualification` (AC-D1-7, `builder-service/src/services/stats/statInference.ts`
 * `proportionCI` — a Wilson score interval at z = 1.96). With no qualification
 * served there is no card: a client-computed interval would be a fabrication.
 *
 * Exported for the ui-components Storybook (R5-2) — not part of the module API.
 */
export function RateEvidenceCell({
  rate,
  numerator,
  denominator,
  qualification,
  align,
}: {
  rate: number | null;
  numerator: number;
  denominator: number;
  /** Spec D D1: server-computed qualification — it drives colour AND the card. */
  qualification?: MetricQualification;
  /**
   * `center` puts the rate over its own evidence line. The rate carries
   * `width: fit-content` (so its underline hugs the number, not the column),
   * which makes it a shrunk flex item — without an explicit cross-axis
   * alignment it sits at the start of the cell while the evidence line below
   * it, a stretched item, honours the cell's own `text-align`. That is the
   * «shifted conversions» the owner saw. Default stays `stretch` for the
   * left-aligned tables.
   */
  align?: 'left' | 'center';
}): React.ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const rateRef = React.useRef<HTMLSpanElement | null>(null);
  const { top, bottom } = rateEvidenceParts(rate, numerator, denominator);
  const unavailable = isUnavailableDisplayText(top) || isUnavailableDisplayText(bottom);
  const tier = unavailable ? undefined : qualification?.tier;
  const openCard = !unavailable && Boolean(qualification);

  const close = () => {
    setAnchorEl(null);
    // AC-011: Escape returns focus to the rate it came from.
    rateRef.current?.focus();
  };

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, ...(align === 'center' ? { alignItems: 'center' } : {}) }}>
      <Typography
        ref={rateRef}
        component="span"
        variant="body2"
        role={openCard ? 'button' : undefined}
        tabIndex={openCard ? 0 : undefined}
        aria-haspopup={openCard ? 'dialog' : undefined}
        aria-expanded={openCard ? Boolean(anchorEl) : undefined}
        data-testid={openCard ? FUNNEL_REPORTS_TEST_IDS.rateConfidenceTrigger : undefined}
        data-tier={tier}
        onClick={openCard ? event => { event.stopPropagation(); setAnchorEl(event.currentTarget); } : undefined}
        onKeyDown={openCard
          ? event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }
          }
          : undefined}
        sx={{
          fontWeight: 500,
          lineHeight: 1.2,
          // F3: `text.disabled` fails contrast on live text. An absent value is
          // still a value the reader must read, so it is `text.secondary`.
          color: unavailable
            ? 'text.secondary'
            : tier === 'solid'
              ? 'success.main'
              : tier === 'directional'
                ? 'warning.main'
                : 'text.primary',
          ...(openCard && {
            cursor: 'pointer',
            // SOLID, not dotted: every column header on this page carries
            // `underline dotted` for its hover-only description, so a dotted
            // rate advertised the same affordance for something that opens a
            // dialog. One decoration, one meaning.
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            width: 'fit-content',
            // The trigger is a `span`, not a ButtonBase, so it has no MUI focus
            // ring of its own — a keyboard operator had nothing to see.
            '&:focus-visible': {
              outline: theme => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '2px',
            },
          }),
        }}
      >
        {tier === 'directional' ? `~ ${top}` : top}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.15, whiteSpace: 'nowrap' }}
      >
        {bottom}
      </Typography>
      {openCard && qualification && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          data-testid={FUNNEL_REPORTS_TEST_IDS.rateConfidenceCard}
          slotProps={{ paper: { onKeyDown: event => { if (event.key === 'Escape') close(); } } }}
        >
          <Box sx={{ p: 2, maxWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {qualification.tier === 'solid'
                ? '✓ Solid estimate'
                : qualification.tier === 'directional'
                  ? '~ Direction only'
                  : '✗ Insufficient sample'}
            </Typography>
            <Stack spacing={0.5}>
              <RateConfidenceRow
                label="Observed"
                value={`${formatPct(qualification.p)} of ${formatInteger(qualification.n)}`}
              />
              <RateConfidenceRow
                label="95% CI"
                value={`${formatPct(qualification.ciLow)} – ${formatPct(qualification.ciHigh)}`}
              />
              <RateConfidenceRow label="Method" value="Wilson score interval" />
              {qualification.neededN !== undefined && qualification.neededN > 0 && (
                <RateConfidenceRow
                  label="Needed"
                  value={`~${formatInteger(qualification.neededN)} more for a solid read`}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1, pt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}
            >
              {STAT_TIER_COPY[qualification.tier].meaning}
            </Typography>
          </Box>
        </Popover>
      )}
    </Stack>
  );
}

function RateConfidenceRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between">
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Stack>
  );
}

/**
 * D-U1 (FT-AC-121): the typed absence of a SWITCHED rate — people mode on a
 * row whose person twin never served. Deliberately NOT a RateEvidenceCell with
 * zeros: 0% would read as «nobody converted», and the journey number under a
 * people-labelled column is the program's named failure (FT-AC-090).
 */
/**
 * People-attribution S6 (plan 2026-08-29; decisions D-A/D-D/D-E/D-F/D-I/D-J):
 * the Traffic sources view — incoming traffic by campaign and by creative with
 * the funnel ladder in PEOPLE, read from the SAME served rows the campaign /
 * creative cards read (S7). No client arithmetic on people (AC-056): every
 * figure comes from the payload; the total row is the SERVED scope block.
 * CAC is an early metric here; pLTV is deliberately absent (D-J: pLTV never
 * appears without CAC — the converse is fine).
 */
function TrafficSourcesPanel({
  data,
  unit,
  view,
  onViewChange,
  loadCreativesPerformance,
  currency,
  scopeChips,
  sortState,
  onSort,
}: {
  data: FunnelReportsDashboardData;
  unit: FunnelUnit;
  view: TrafficSourcesView;
  onViewChange: (view: TrafficSourcesView) => void;
  loadCreativesPerformance?: () => Promise<FunnelCreativePerformanceSection>;
  currency?: string;
  scopeChips: string[];
  sortState: SortState;
  onSort: (columnId: string) => void;
}): React.ReactElement {
  const peopleMode = unit === 'people';
  const [creatives, setCreatives] = React.useState<{ state: 'idle' | 'loading' | 'ready' | 'failed'; section?: FunnelCreativePerformanceSection; error?: string }>({ state: 'idle' });
  // The loader rides a ref: its identity changes whenever the page re-derives
  // its scope (opening the tab itself writes `traffic=creatives` to the URL),
  // and an effect keyed on the callback would cancel the in-flight fetch and
  // leave the tab at `loading` forever (S8 e2e finding, AC-053/055).
  const loadCreativesRef = React.useRef(loadCreativesPerformance);
  loadCreativesRef.current = loadCreativesPerformance;
  // Fetch ONCE per panel mount, the first time the creatives tab is shown.
  // The request is not keyed on `creatives.state`: an effect that re-ran on
  // `idle → loading` would run its own cleanup and drop the response it was
  // waiting for. Late results are ignored only after a real unmount.
  const creativesRequestedRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  // Re-armed on every mount: React StrictMode (dev) runs mount → unmount →
  // mount, and an unmount-only cleanup would leave the flag false for good.
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  React.useEffect(() => {
    if (view !== 'creatives' || creativesRequestedRef.current) return;
    creativesRequestedRef.current = true;
    const load = loadCreativesRef.current;
    if (!load) {
      setCreatives({ state: 'failed', error: 'Creatives are not available on this surface.' });
      return;
    }
    setCreatives({ state: 'loading' });
    load()
      .then(section => { if (mountedRef.current) setCreatives({ state: 'ready', section }); })
      .catch((error: unknown) => { if (mountedRef.current) setCreatives({ state: 'failed', error: error instanceof Error ? error.message : String(error) }); });
  }, [view]);

  const campaignRows = React.useMemo(() => buildTrafficCampaignRows(data, unit), [data, unit]);
  const totalRow = React.useMemo(() => buildTrafficTotalRow(data, unit), [data, unit]);
  const creativeRows = React.useMemo(() => buildTrafficCreativeRows(creatives.section, unit), [creatives.section, unit]);
  const scope = data.campaignEconomicsPeople;
  const adScope = creatives.section?.people;
  const attribution = data.campaignEconomicsAttribution;
  const modelLabel = attribution?.selectedModelId ? attributionModelCopy(attribution.selectedModelId).label : undefined;

  const gateCell = (key: string, gate: TrafficGateView): React.ReactNode => (
    gate.available
      ? <RateEvidenceCell key={key} rate={gate.rate ?? null} numerator={gate.numerator ?? 0} denominator={gate.denominator ?? 0} />
      : <MetricAbsenceCell key={key} detail={gate.reason ?? PEOPLE_ABSENCE_LABEL} />
  );
  const moneyCell = (money: TrafficMoneyView & { count?: number }, options?: { countNoun?: string }): React.ReactNode => {
    if (money.value === undefined) return <MetricAbsenceCell detail={money.reason ?? PEOPLE_ABSENCE_LABEL} />;
    if (money.value === null) {
      return (
        <Typography variant="body2" color="text.disabled" title={money.reason}>
          {money.reason ? `— · ${money.reason}` : '—'}
        </Typography>
      );
    }
    return (
      <Stack spacing={0}>
        <Typography variant="body2">{formatNullableCurrency(money.value, currency)}</Typography>
        {typeof money.count === 'number' && options?.countNoun && (
          <Typography variant="caption" color="text.secondary">{`${formatInteger(money.count)} ${options.countNoun}`}</Typography>
        )}
      </Stack>
    );
  };
  const metaCell = (row: TrafficSourceRow): React.ReactNode => (
    row.meta ? (
      <Stack spacing={0} data-testid={FUNNEL_REPORTS_TEST_IDS.metaSevenDayClickCell(row.kind, row.id)}>
        <Typography variant="body2">
          {row.meta.basis === 'seven_day_click' ? row.meta.primary.replace(' · 7-day click', '') : row.meta.primary}
        </Typography>
        {row.meta.viewThrough && <Typography variant="caption" color="text.secondary">{row.meta.viewThrough}</Typography>}
      </Stack>
    ) : <Typography variant="body2" color="text.disabled">—</Typography>
  );
  const sourceNameCell = (row: TrafficSourceRow): React.ReactNode => (
    // S8 review: ad names carry an unbreakable hash («…-d1097c816023dcd1…»)
    // that overflowed into the next column — allow a break anywhere.
    <Stack spacing={0} sx={{ minWidth: 0 }} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourceRow(row.kind, row.id)}>
      <Typography variant="body2" sx={{ fontWeight: row.isNoCampaign ? 400 : 500, overflowWrap: 'anywhere' }}>{row.label}</Typography>
      {row.sublabel && <Typography variant="caption" color="text.secondary">{row.sublabel}</Typography>}
      {row.identityPlane === 'behavioural' && !row.isNoCampaign && (
        <Typography variant="caption" color="text.disabled">no ad account</Typography>
      )}
    </Stack>
  );
  const visitsNote = peopleMode ? undefined : undefined;
  const campaignColumns: EvidenceColumn[] = [
    { id: 'source', label: 'Campaign', sortable: true, minWidth: 220 },
    { id: 'spend', label: 'Spend', sortable: true, align: 'right', minWidth: 96 },
    { id: 'people', label: peopleMode ? 'People' : 'Journeys', sortable: true, align: 'right', minWidth: 96 },
    { id: 'firstToSecond', label: 'First -> second', sortable: true, minWidth: 132, unitNote: visitsNote },
    { id: 'paywall', label: 'Paywall', sortable: true, minWidth: 132 },
    { id: 'purchase', label: 'Purchase', sortable: true, minWidth: 132 },
    { id: 'costPerPerson', label: peopleMode ? 'Cost / person' : 'Cost / first screen', sortable: true, align: 'right', minWidth: 110 },
    { id: 'cac', label: 'CAC', sortable: true, align: 'right', minWidth: 120, description: 'Spend ÷ people whose FIRST paid conversion happened on a journey credited to this row (last click, the served model\'s window). Never spend ÷ trials.' },
    { id: 'meta', label: 'Meta purchases', sortable: true, minWidth: 150, unitNote: '7-day click', description: META_EVIDENCE_COLUMN_NOTE },
  ];
  const creativeColumns: EvidenceColumn[] = [
    { id: 'source', label: 'Creative', sortable: true, minWidth: 220 },
    { id: 'campaign', label: 'Campaign', sortable: true, minWidth: 150 },
    ...campaignColumns.slice(1),
  ];
  const sortValuesFor = (row: TrafficSourceRow): EvidenceRow['sortValues'] => ({
    source: row.label,
    campaign: row.campaignLabel ?? '',
    spend: row.spend.value ?? null,
    people: row.people.value ?? null,
    firstToSecond: row.firstToSecond.available ? row.firstToSecond.rate ?? null : null,
    paywall: row.paywall.available ? row.paywall.rate ?? null : null,
    purchase: row.purchase.available ? row.purchase.rate ?? null : null,
    costPerPerson: row.costPerPerson.value ?? null,
    cac: row.cac.value ?? null,
    meta: row.meta?.basis === 'seven_day_click' ? Number(row.meta.primary.split(' ')[0].replace(/,/g, '')) : null,
  });
  const rowCells = (row: TrafficSourceRow, withCampaign: boolean): React.ReactNode[] => [
    sourceNameCell(row),
    ...(withCampaign ? [<Typography key="campaign" variant="body2" color="text.secondary">{row.campaignLabel ?? '—'}</Typography>] : []),
    moneyCell(row.spend),
    row.people.value === undefined
      ? <MetricAbsenceCell key="people" detail={row.people.reason ?? PEOPLE_ABSENCE_LABEL} />
      : formatInteger(row.people.value),
    gateCell('first-second', row.firstToSecond),
    gateCell('paywall', row.paywall),
    gateCell('purchase', row.purchase),
    moneyCell(row.costPerPerson),
    // S7 / AC-060: the ONE CAC wording shared with the cards (PltvWithCac).
    <Typography key="cac" variant="body2" color={row.cac.value === undefined || row.cac.value === null ? 'text.disabled' : undefined} title={row.cac.reason}>
      {cacLineText(row.cac, currency).replace(/^CAC /, '')}
    </Typography>,
    metaCell(row),
  ];
  const campaignEvidenceRows: EvidenceRow[] = campaignRows.map(row => ({
    key: `campaign-${row.id}`,
    cells: rowCells(row, false),
    sortValues: sortValuesFor(row),
  }));
  const totalEvidenceRow: EvidenceRow | null = totalRow ? {
    key: 'total',
    group: '__total',
    cells: [
      <Stack key="total" spacing={0} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesTotalRow}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{totalRow.label}</Typography>
        <Typography variant="caption" color="text.secondary">computed on the scope, not summed from rows</Typography>
      </Stack>,
      moneyCell(totalRow.spend),
      formatInteger(totalRow.people),
      gateCell('total-first-second', totalRow.firstToSecond),
      gateCell('total-paywall', totalRow.paywall),
      gateCell('total-purchase', totalRow.purchase),
      moneyCell(totalRow.costPerPerson),
      <Typography key="total-cac" variant="body2" color="text.disabled" title={totalRow.cac.reason}>
        {cacLineText(totalRow.cac, currency).replace(/^CAC /, '')}
      </Typography>,
      <Typography key="total-meta" variant="body2" color="text.disabled">—</Typography>,
    ],
    sortValues: {},
  } : null;
  const campaignsCaption = peopleMode
    ? `People. A person who clicked two campaigns is in both rows — rows do not add up. ${
      scope
        ? `${formatInteger(scope.startedPeople)} people in scope, ${formatInteger(scope.peopleOnMultipleCampaigns)} came through more than one campaign.`
        : 'Scope total unavailable on this cached payload — press Refresh.'
    } Purchase is credited to the campaign of the session it happened in (last click${modelLabel ? `, ${modelLabel}` : ''}), as Meta does. Campaigns with no traffic into these funnels are not shown; «No campaign» keeps the people who arrived without one.`
    : 'Visits. Journeys credited to each campaign (last click). Paywall has no journey twin on this lane — switch to People for the full ladder.';
  const creativesCaption = peopleMode
    ? `People. One row per creative (ad). Identity comes from the paid-acquisition linkage. ${
      adScope
        ? `${formatInteger(adScope.startedPeople)} people attributable to a creative (${formatInteger(adScope.journeysAttributable)} of ${formatInteger(adScope.journeysInScope)} journeys) · ${formatInteger(adScope.peopleOnMultipleAds)} came through more than one creative — rows do not add up.`
        : 'Scope total unavailable on this payload.'
    } Unique clicks from Meta are not shown: daily uniques cannot be summed into people.`
    : 'Visits. Sessions the ad resolved on our side; the people ladder is on the People unit.';
  const creativeEvidenceRows: EvidenceRow[] = creativeRows.map(row => ({
    key: `creative-${row.id}`,
    cells: rowCells(row, true),
    sortValues: sortValuesFor(row),
  }));

  return (
    <Stack spacing={1.5} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesPanel}>
      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">Scope:</Typography>
        {scopeChips.map(chip => <Chip key={chip} size="small" variant="outlined" label={chip} />)}
      </Stack>
      <Tabs value={view} onChange={(_event, next: TrafficSourcesView) => onViewChange(next)} aria-label="Traffic sources">
        <Tab value="campaigns" label={`Campaigns (${campaignRows.length})`} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesTab('campaigns')} />
        <Tab value="creatives" label={creatives.section ? `Creatives (${creativeRows.length})` : 'Creatives'} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesTab('creatives')} />
      </Tabs>
      {view === 'campaigns' ? (
        <>
          <Typography variant="caption" color="text.secondary" data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesCaption}>{campaignsCaption}</Typography>
          <SortableEvidenceTable
            id="traffic-sources-campaigns"
            columns={campaignColumns}
            // S8 review: the SERVED scope total leads the table — pinned first
            // (own group, no sort values) so it is read before any row and
            // never hidden below the table's scroll edge on a long list.
            rows={[...(totalEvidenceRow ? [totalEvidenceRow] : []), ...campaignEvidenceRows]}
            sortState={sortState}
            onSort={onSort}
          />
          <Typography variant="caption" color="text.secondary">
            Total row is computed on the scope, not summed from rows. CAC is per person on the same journeys as the funnel gates.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" data-testid={FUNNEL_REPORTS_TEST_IDS.trafficSourcesCaption}>{creativesCaption}</Typography>
          {creatives.state === 'loading' && <CircularProgress size={20} />}
          {creatives.state === 'failed' && <Alert severity="warning">{creatives.error ?? 'Creatives could not be loaded.'}</Alert>}
          {creatives.state === 'ready' && (
            <>
              <SortableEvidenceTable
                id="traffic-sources-creatives"
                columns={creativeColumns}
                rows={creativeEvidenceRows}
                sortState={sortState}
                onSort={onSort}
              />
              <Typography variant="caption" color="text.secondary">
                {`Showing ${formatInteger(creativeRows.length)} creatives · sorted by spend · top creatives per campaign from the performance lane.`}
              </Typography>
            </>
          )}
        </>
      )}
    </Stack>
  );
}

export function MetricAbsenceCell({ detail }: { detail: string }): React.ReactElement {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        color="text.disabled"
        sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.15 }}
      >
        n/a
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.15 }}>
        {detail}
      </Typography>
    </Stack>
  );
}

/**
 * Inline trust marker (user review feedback): the quality verdict lives ON the
 * row it applies to. The tooltip answers "what does this mean?" and "what do I
 * do?"; the full per-scope breakdown lives in the bottom Warnings section.
 */
const QUALITY_GUIDE: Record<string, {
  color: 'success' | 'warning' | 'error' | 'default';
  tooltip: string;
  /**
   * The «go read the detail» sentence, kept SEPARATE from the meaning: the
   * «Warnings & data trust» section is admin-only (`VISIBILITY_SPEC.md`), so
   * for a member this pointer would name a block they cannot open — the same
   * dead end §11.2 removed from the trust banner's CTA and left here.
   */
  pointer?: string;
}> = {
  ok: {
    color: 'success',
    tooltip: 'All data-quality checks passed for this scope — the metrics can be trusted.',
  },
  warning: {
    color: 'warning',
    tooltip: 'Some data-quality checks flagged this scope; treat the metrics as approximate.',
    pointer: 'See the Warnings section below for what was flagged and how to fix it.',
  },
  critical: {
    color: 'error',
    tooltip: 'Data-quality checks FAILED for this scope (broken event chains, duplicates, or test traffic) — do not use these metrics for decisions.',
    pointer: 'See the Warnings section below for the fix.',
  },
  unknown: {
    color: 'default',
    tooltip: 'No data-quality signal for this scope yet.',
  },
};

function QualitySeverityChip({ severity, quantified, warningsReachable = true }: {
  severity: string;
  quantified?: string;
  warningsReachable?: boolean;
}): React.ReactElement {
  const normalized = (severity || 'unknown').toLowerCase();
  const guide = QUALITY_GUIDE[normalized === 'warn' ? 'warning' : normalized] ?? QUALITY_GUIDE.unknown;
  const title = [
    guide.tooltip,
    warningsReachable ? guide.pointer : undefined,
    quantified,
  ].filter(Boolean).join(' ');
  return (
    <MuiTooltip title={title}>
      {/* A11y: the quantified evidence («11 326 issues flagged») lived in a
          hover-only tooltip over a plain `div`, so a keyboard operator could
          not reach the number that licenses every other number on the page.
          `tabIndex` makes the chip a focus stop, which is also what MUI's
          Tooltip needs to open on focus. */}
      <Chip
        label={severity || 'unknown'}
        color={guide.color}
        size="small"
        variant="outlined"
        tabIndex={0}
        aria-label={`Data quality: ${severity || 'unknown'}. ${title}`}
        sx={{ cursor: 'help' }}
      />
    </MuiTooltip>
  );
}

/**
 * Names-first identity cell (QW1): the resolved display name leads, the raw
 * id demotes to a secondary caption. Rows with identity warnings get a
 * visible ⚠ marker whose tooltip lists exactly what could not be resolved.
 */
/**
 * Starts cell: journeys (visits) lead; distinct PEOPLE ride the second line —
 * the number user-based tools (Amplitude, ad managers) report. Absent on
 * payloads predating the uniqueVisitors field (old D-F1 cache) — count only.
 */
function StartsWithUniqueCell({
  starts,
  uniqueVisitors,
  startedPeople,
}: {
  starts: number;
  uniqueVisitors?: number;
  /** F2: the row's person twin (P-I3) — rows are per-placement, so no summation trap. */
  startedPeople?: number;
}): React.ReactElement {
  // F2 (FT-AC-090): in people mode the cell LEADS with the person figure and
  // keeps starts as the labelled visits context; an absent twin renders the
  // D7 typed absence, never a relabel.
  const unit = useFunnelUnit();
  if (unit === 'people') {
    return (
      <Box data-testid={FUNNEL_REPORTS_TEST_IDS.startsUniqueCell} sx={{ textAlign: 'right' }}>
        <Typography variant="body2" component="div">
          {typeof startedPeople === 'number' ? `${formatInteger(startedPeople)} people` : 'n/a · people'}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>
          {typeof startedPeople === 'number' ? `${formatInteger(starts)} starts (visits)` : PEOPLE_ABSENCE_LABEL}
        </Typography>
      </Box>
    );
  }
  return (
    <Box data-testid={FUNNEL_REPORTS_TEST_IDS.startsUniqueCell} sx={{ textAlign: 'right' }}>
      <Typography variant="body2" component="div">{formatInteger(starts)}</Typography>
      {typeof uniqueVisitors === 'number' && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>
          {formatInteger(uniqueVisitors)} people
        </Typography>
      )}
    </Box>
  );
}

/**
 * D8: a named text link in the versions table's «Details» column. Underlined so
 * it reads as an action without a button chrome, muted and inert on a row with
 * no traffic — a row with zero starts has no drop-off and no economics to open.
 * F3: the disabled state is `text.secondary`, never `text.disabled`.
 */
function VersionDetailLink({
  label,
  testId,
  ariaLabel,
  disabled,
  onClick,
}: {
  label: string;
  testId: string;
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <Box
      component="span"
      role={disabled ? undefined : 'button'}
      tabIndex={disabled ? undefined : 0}
      aria-label={disabled ? undefined : ariaLabel}
      aria-disabled={disabled ? 'true' : undefined}
      data-testid={testId}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled
        ? undefined
        : event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        }}
      sx={{
        fontSize: '0.875rem',
        color: disabled ? 'text.secondary' : 'primary.main',
        opacity: disabled ? 0.6 : 1,
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </Box>
  );
}

function IdentityNameCell({
  name,
  id,
  secondary,
  warnings,
}: {
  name?: string;
  id: string;
  /**
   * D6: the second line. The default is the raw `id`, which is what every
   * caller rendered before this redesign and what the A/B, campaign and
   * creative tables still need as evidence (`ab-test-1 / iter-1` is how an
   * operator finds the test in the config).
   *
   * D6 drops the id from ONE table — Traffic by version, where
   * `wuRHbvfVJuGIMpvChdwL` under a funnel name is noise the reader cannot act
   * on. That table passes its version label («v4») here instead, so the id
   * leaves that table and only that table.
   */
  secondary?: string;
  warnings?: string[];
}): React.ReactElement {
  // AC-021: a whitespace-only name is NOT a name. The identity resolver can
  // serve one (a placement renamed to blanks, a trimmed-empty onboarding
  // title), and rendering it would leave the row looking nameless AND idless.
  const resolvedName = name?.trim() || undefined;
  const hasName = Boolean(resolvedName && resolvedName !== id);
  const firstLine = hasName ? (resolvedName as string) : id;
  const secondLine = secondary ?? (hasName ? id : undefined);
  const marker = warnings?.length ? (
    <MuiTooltip title={warnings.join('; ')}>
      <Box component="span" sx={{ cursor: 'help', color: 'warning.main' }} aria-label="identity warnings">⚠</Box>
    </MuiTooltip>
  ) : null;

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      {/* D6: the resolved NAME leads (0.875 rem / 500). F3: it is `text.primary`
          — a name is live text, not a disabled hint. */}
      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
        {firstLine}{marker ? <> {marker}</> : null}
      </Typography>
      {/* The second line: the caller's label where it has one (the versions
          table's «v4»), otherwise the raw id as evidence. A caller-supplied
          label renders even when the NAME did not resolve — the first line
          then falls back to the id, and dropping the version label as well
          would leave the row unidentifiable (AC-021). */}
      {secondLine && secondLine !== firstLine && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15, whiteSpace: 'nowrap' }}>
          {secondLine}
        </Typography>
      )}
    </Stack>
  );
}

function FunnelHealthSummary({
  dailyRows,
  currency = 'usd',
  moneyAvailable = true,
}: {
  dailyRows: FunnelReportsDashboardData['funnelHealthDaily'];
  currency?: string;
  moneyAvailable?: boolean;
}): React.ReactElement {
  const totals = dailyRows.reduce((acc, row) => ({
    startedJourneys: acc.startedJourneys + row.startedJourneys,
    paywallReachedJourneys: acc.paywallReachedJourneys + row.paywallReachedJourneys,
    buyButtonClicks: acc.buyButtonClicks + row.buyButtonClicks,
    offerConvertedJourneys: acc.offerConvertedJourneys + row.offerConvertedJourneys,
    purchaseJourneys: acc.purchaseJourneys + row.purchaseJourneys,
    purchaseAmount: acc.purchaseAmount + row.purchaseAmount,
    severity: highestSeverity([acc.severity, row.dataQualitySeverity]),
  }), {
    startedJourneys: 0,
    paywallReachedJourneys: 0,
    buyButtonClicks: 0,
    offerConvertedJourneys: 0,
    purchaseJourneys: 0,
    purchaseAmount: 0,
    severity: 'unknown',
  });
  // Alert severity = max(funnel state, data quality): a broken funnel is never
  // rendered as info, and DQ critical stays error even when the funnel converts.
  const funnelBroken = totals.startedJourneys <= 0
    || totals.paywallReachedJourneys <= 0
    || (totals.offerConvertedJourneys <= 0 && totals.purchaseJourneys <= 0);
  const severity = totals.severity === 'critical'
    ? 'error'
    : totals.severity === 'warning' || funnelBroken
      ? 'warning'
      : 'success';

  return (
    <Stack spacing={1.5}>
      <Alert severity={severity}>
        {funnelHealthReason(totals)}
      </Alert>
      <Grid container spacing={1}>
        <MiniMetric label="Starts" value={formatInteger(totals.startedJourneys)} />
        <MiniMetric label="Paywall" value={formatInteger(totals.paywallReachedJourneys)} />
        <MiniMetric label="Buy clicks" value={formatInteger(totals.buyButtonClicks)} />
        <MiniMetric label="Offers" value={formatInteger(totals.offerConvertedJourneys)} />
        <MiniMetric label="Purchases" value={formatInteger(totals.purchaseJourneys)} />
        <MiniMetric label="Revenue" value={moneyAvailable ? formatCurrency(totals.purchaseAmount, currency) : 'n/a'} />
      </Grid>
    </Stack>
  );
}


type PaidAcquisitionSectionProps = {
  rows: FunnelReportsDashboardData['campaignEconomics'];
  coverage: FunnelReportsDashboardData['campaignEconomicsCoverage'];
  currency?: string;
  empty: boolean;
  error?: React.ComponentProps<typeof ChartSection>['error'];
  showEmptySections: boolean;
  /** Server-computed verdict thresholds — seed for the client-side re-verdict. */
  verdictThresholds?: CampaignVerdictThresholds;
  /** Needed by the thresholds gear (GET/PATCH settings); absent → gear hidden. */
  projectId?: string;
  creativeDecisionsEnabled?: boolean;
  realizedValueBasis?: FunnelValueBasisContract;
  realizedGrossBasis?: FunnelValueBasisContract;
  netCollectedBasis?: FunnelValueBasisContract;
  settlementBasis?: FunnelValueBasisContract;
  projectedValueBasis?: FunnelValueBasisContract;
  projectedValueBases?: FunnelReportsDashboardData['projectedValueBases'];
  /** I5c: server-computed root-cause rollup; absent below the 60% bar. */
  moneyRootCause?: FunnelReportsDashboardData['moneyRootCause'];
  moneyDiagnosis?: FunnelReportsDashboardData['moneyDiagnosis'];
  /**
   * W-R5 (DISC-AC-144): server-computed header tiles — the summary renders
   * this block; absent on pre-W-R5 caches → honest degraded caption.
   */
  paidSummaryTiles?: FunnelReportsDashboardData['paidSummaryTiles'];
  /**
   * W-R5 (DISC-AC-144): thresholds Save re-fetches the payload — the table's
   * chips render the SERVER verdicts and change only with a fresh payload.
   */
  onVerdictThresholdsSaved?: () => void;
  /** @deprecated Realized-only fallback for stale payloads. */
  ltvNetOfRefunds?: boolean;
  /** W-R4-2 (DISC-AC-134): lazy drilldown loader; absent → preloaded rows only. */
  loadCampaignCreatives?: (campaignId: string, cursor?: string) => Promise<FunnelCreativeDrilldownData>;
  /**
   * F3 (FT-AC-088): attribution identity of this payload. Absent (stale
   * cache, fixture, failed lane) → typed absence caption, NO selector.
   */
  attribution?: FunnelReportsDashboardData['campaignEconomicsAttribution'];
  onAttributionModelChange?: (modelId: string) => void;
  attributionSwitchPending?: boolean;
};

/** Client cache entry of one campaign's lazily fetched creative pages. */
type CampaignDrilldownEntry = {
  rows: FunnelCreativePerformanceRow[];
  nextCursor?: string;
  scopeRef?: string;
  totalRows?: number;
  loading: boolean;
  failed?: boolean;
};

// Isolated + memoized (table-UX rework): creatives expand, sorting and column
// toggles re-render ONLY this section — previously every interaction re-ran
// the WHOLE dashboard render incl. every recharts chart (the live jank).
/**
 * D deep link: analyst campaign-brief cards navigate to
 * `/project/{pid}/reports?section=paid-acquisition`. Read once per render —
 * initial-expand only (the accordion stays user-controlled afterwards).
 */
function isPaidAcquisitionDeepLink(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('section') === 'paid-acquisition';
}

function creativeDecisionsHref(projectId: string): string {
  const target = `/project/${encodeURIComponent(projectId)}/reports/creative-decisions`;
  if (typeof window === 'undefined') return target;
  const source = new URLSearchParams(window.location.search);
  const params = new URLSearchParams();
  const from = source.get('from');
  const to = source.get('to');
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();
  return query ? `${target}?${query}` : target;
}

const PaidAcquisitionSection = React.memo(function PaidAcquisitionSection({
  rows,
  coverage,
  currency,
  empty,
  error,
  showEmptySections,
  verdictThresholds,
  projectId,
  creativeDecisionsEnabled = false,
  realizedValueBasis,
  realizedGrossBasis,
  netCollectedBasis,
  settlementBasis,
  projectedValueBasis,
  projectedValueBases,
  moneyRootCause,
  moneyDiagnosis,
  paidSummaryTiles,
  onVerdictThresholdsSaved,
  ltvNetOfRefunds,
  loadCampaignCreatives,
  attribution,
  onAttributionModelChange,
  attributionSwitchPending = false,
}: PaidAcquisitionSectionProps): React.ReactElement {
  // The compatibility realized field is always gross. Independent net,
  // settlement and contribution values render only from their named columns;
  // the deprecated boolean can never relabel this value.
  const realizedBasisCaption = realizedGrossBasis?.kind === 'gross_collected'
    ? 'gross collected'
    : realizedValueBasis?.kind === 'gross_collected'
      ? 'gross collected'
      : 'gross collected';
  const netCollectedBasisSupported = !netCollectedBasis
    || (netCollectedBasis.kind === 'net_collected' && netCollectedBasis.availability === 'available');
  const settlementBasisSupported = !settlementBasis
    || (settlementBasis.kind === 'settlement_cash' && settlementBasis.availability === 'available');
  // Legacy payloads expose gross-only sBG fields. New payloads expose four
  // independently reconciled series; contribution is the primary decision
  // basis and an unavailable contribution series never falls back to gross.
  const projectedBasisSupported = !projectedValueBasis
    || (
      projectedValueBasis.kind === 'gross_collected'
      && projectedValueBasis.availability === 'available'
      && projectedValueBasis.trust === 'model_estimate'
    );
  const projectedBasisCaption = 'gross collected · estimated';
  const hasIndependentProjectedBases = Boolean(
    projectedValueBases || rows.some(row => row.pltvByBasis !== undefined),
  );
  const chart = useChartTheme();
  // People-attribution S7 (AC-015): the cost stack follows the page unit.
  const paidFunnelUnit = useFunnelUnit();
  const paidPeopleMode = paidFunnelUnit === 'people';
  const [paidAcquisitionSort, setPaidAcquisitionSort] = React.useState<SortState>({
    columnId: 'spend',
    direction: 'desc',
  });
  // Verdict thresholds applied LIVE: seeded from the payload (project
  // settings), replaced on popover Save — every chip re-verdicts through the
  // pure client function WITHOUT a payload refetch (AC-011).
  const [activeThresholds, setActiveThresholds] = React.useState<CampaignVerdictThresholds>(
    verdictThresholds ?? DEFAULT_VERDICT_THRESHOLDS,
  );
  React.useEffect(() => {
    if (verdictThresholds) setActiveThresholds(verdictThresholds);
  }, [verdictThresholds]);
  // «Creatives (N)» on a MATCHED campaign row — LOCAL toggle; unmatched
  // campaigns have no funnel linkage to drill into.
  const [expandedCampaignCreatives, setExpandedCampaignCreatives] = React.useState<Record<string, boolean>>({});
  // W-R4-2 (DISC-AC-134): lazily fetched creative pages per campaign id. A
  // collapsed campaign fetches nothing; a second expand serves this cache; a
  // NEW payload (window/filter change) drops it — continuations never cross
  // request identities (the server refuses stale cursors anyway, CURSOR-KEEP).
  const [drilldownPages, setDrilldownPages] = React.useState<Record<string, CampaignDrilldownEntry>>({});
  React.useEffect(() => {
    setDrilldownPages({});
  }, [rows]);
  const requestCampaignDrilldown = React.useCallback((metaCampaignId: string) => {
    if (!loadCampaignCreatives) return;
    setDrilldownPages(current => {
      // Cached or in-flight — no new request; a FAILED entry may retry
      // (W-R6: with the preloaded dashboard rows gone, a stuck failure
      // would otherwise leave the expand permanently empty).
      if (current[metaCampaignId] && !current[metaCampaignId].failed) return current;
      loadCampaignCreatives(metaCampaignId)
        .then(page => setDrilldownPages(latest => ({
          ...latest,
          [metaCampaignId]: {
            rows: page.rows,
            nextCursor: page.nextCursor,
            scopeRef: page.scopeRef,
            totalRows: page.totalRows,
            loading: false,
          },
        })))
        .catch(() => setDrilldownPages(latest => ({
          ...latest,
          [metaCampaignId]: { rows: [], loading: false, failed: true },
        })));
      return { ...current, [metaCampaignId]: { rows: [], loading: true } };
    });
  }, [loadCampaignCreatives]);
  const loadMoreCampaignCreatives = React.useCallback((metaCampaignId: string) => {
    if (!loadCampaignCreatives) return;
    setDrilldownPages(current => {
      const entry = current[metaCampaignId];
      if (!entry?.nextCursor || entry.loading) return current;
      loadCampaignCreatives(metaCampaignId, entry.nextCursor)
        .then(page => setDrilldownPages(latest => {
          const existing = latest[metaCampaignId];
          // Cross-scope pages never append — replace wholesale (CURSOR-KEEP).
          const sameScope = existing && existing.scopeRef === page.scopeRef;
          return {
            ...latest,
            [metaCampaignId]: {
              rows: sameScope ? [...existing.rows, ...page.rows] : page.rows,
              nextCursor: page.nextCursor,
              scopeRef: page.scopeRef,
              totalRows: page.totalRows,
              loading: false,
            },
          };
        }))
        .catch(() => setDrilldownPages(latest => ({
          ...latest,
          [metaCampaignId]: { ...latest[metaCampaignId], loading: false },
        })));
      return { ...current, [metaCampaignId]: { ...entry, loading: true } };
    });
  }, [loadCampaignCreatives]);
  // «Mapped to funnels only» — default ON: the section's question is campaign
  // economics and unmatched rows have none; tiles + fb_* hint keep the
  // unmatched money visible regardless.
  const [mappedOnly, setMappedOnly] = React.useState(true);
  const [showAllPaidColumns, setShowAllPaidColumns] = React.useState(false);
  const [paidFullscreen, setPaidFullscreen] = React.useState(false);
  const [copiedFbParamsSnippet, setCopiedFbParamsSnippet] = React.useState(false);
  const chartInputRows = rows.slice(0, 8);
  // Money-first table (table-UX rework): the section's question — «which
  // campaigns pay back» — gets first-class sortable columns; identity collapses
  // into ONE cell (id + source + match chips + creatives toggle); funnel-depth,
  // unit-cost and Meta-evidence columns hide behind «More columns».
  const paidAcquisitionColumns: EvidenceColumn[] = [
    { id: 'campaign', label: 'Campaign', sortable: true, minWidth: 210 },
    {
      id: 'results',
      label: 'Results',
      sortable: true,
      align: 'right',
      minWidth: 104,
      description: 'Purchases on spend-covered days (falls back to full-window purchases on older cached payloads); offers · trials below.',
    },
    { id: 'spend', label: 'Spend', sortable: true, align: 'right', minWidth: 112 },
    {
      id: 'revenue',
      label: 'Revenue',
      sortable: true,
      align: 'right',
      minWidth: 108,
      description: 'Purchase revenue on spend-covered days only. The dimmed «full» line appears when coverage is partial — it never feeds ROAS.',
    },
    {
      id: 'ltv',
      label: 'Realized value',
      sortable: true,
      align: 'right',
      minWidth: 116,
      description: 'Independent reconciled gross, net collected, settlement cash, and contribution values. Missing component coverage stays n/a with its reason.',
    },
    {
      id: 'roas',
      label: 'ROAS',
      sortable: true,
      align: 'right',
      minWidth: 84,
      description: `Covered revenue ÷ matched spend over the SAME synced days. Color: ≥${ROAS_GOOD_THRESHOLD}× paid back · ≥${ROAS_MID_THRESHOLD}× middling · below — losing.`,
    },
    {
      id: 'roasLtv',
      label: 'ROAS (LTV)',
      sortable: true,
      align: 'right',
      minWidth: 96,
      description: 'Realized LTV ÷ matched spend. ≥1.00× (✓) means the campaign has already paid back on realized revenue.',
    },
    {
      id: 'pltv',
      label: hasIndependentProjectedBases ? 'Projected value (12m)' : 'Gross pLTV (12m)',
      sortable: true,
      align: 'right',
      // I5(a) owner decision 2026-08-19: 260px matches the primary Creative
      // Performance table so the watch-only chip label renders untruncated
      // (EvidenceTable uses tableLayout:fixed with width=minWidth).
      minWidth: 260,
      description: hasIndependentProjectedBases
        ? 'Contribution pLTV : CAC is primary. Net collected, settlement cash, and gross audit series remain visible and are modeled independently. Missing reconciliation stays n/a; the UI never substitutes gross for contribution.'
        : 'Legacy projected 12-month gross-collected LTV : CAC (sBG over monthly renewal cycles; annual plans count their upfront fee). P10–P90 is the credible interval. It does not deduct refunds, disputes, fees, tax, credits or service COGS.',
    },
    ...(showAllPaidColumns
      ? ([
        { id: 'funnel', label: 'Funnel', sortable: true, minWidth: 150, description: 'Sessions (all/paid), starts and the first-screen / first→second gates.' },
        {
          id: 'costs',
          label: 'Unit costs',
          sortable: true,
          minWidth: 156,
          description: 'Each line divides the SAME matched spend by the count of one outcome type (price · count). Lines are independent per-outcome views, not a budget split.',
        },
        // F3 (D-F3-5/FT-AC-044): the view-through divergence rides the header
        // tooltip wherever the section compares against Meta.
        { id: 'metaEvidence', label: 'Meta evidence', minWidth: 220, description: META_EVIDENCE_COLUMN_NOTE },
      ] satisfies EvidenceColumn[])
      : []),
  ];
  const isMappedPaidRow = (row: FunnelReportsDashboardData['campaignEconomics'][number]): boolean =>
    Boolean(row.matchQuality && row.matchQuality !== 'none');
  const visiblePaidAcquisitionRows = mappedOnly
    ? rows.filter(isMappedPaidRow)
    : rows;
  const paidAcquisitionTableRows: EvidenceRow[] = visiblePaidAcquisitionRows.flatMap((row, index) => {
    const campaignKey = `${row.campaign || 'campaign'}-${row.metaAdId || row.metaCampaignId || index}`;
    // Drill-down is for MATCHED campaigns only: unmatched spend has no funnel
    // linkage — its creatives live in the CD `diagnostics` saved view.
    // W-R6 (DISC-AC-134): the dashboard payload no longer preloads creatives;
    // the lazy drilldown lane is the ONLY source (fetched on first expand).
    // F3 honesty guard: the creative drilldown lane is PINNED to the default
    // attribution model (W S5) — expanding it under a non-default selection
    // would mix two models in one table (the FT-AC-090 mislabel class). The
    // toggle hides until the default selection returns.
    const nonDefaultModelSelected = attribution?.isDefault === false;
    const drilldownAvailable = Boolean(
      loadCampaignCreatives && row.metaCampaignId && row.matchQuality && row.matchQuality !== 'none'
      && !nonDefaultModelSelected,
    );
    const creativesExpanded = Boolean(expandedCampaignCreatives[campaignKey]);
    const drilldownEntry = drilldownAvailable && row.metaCampaignId
      ? drilldownPages[row.metaCampaignId]
      : undefined;
    const drilldownRows = drilldownEntry && !drilldownEntry.failed && !drilldownEntry.loading
      ? drilldownEntry.rows
      : [];
    // Derived honesty metrics over the campaign's fetched creatives: how much
    // of the Meta-reported click volume we actually capture as sessions, and
    // what Meta's own attribution claims next to our session-side revenue.
    // Populate once the drilldown page is in (they lived on preloaded rows
    // before W-R6 — same numbers, now behind the first expand).
    const campaignClicks = drilldownRows.reduce((sum, creative) => sum + (creative.clicks ?? 0), 0);
    const metaValueTotal = drilldownRows.reduce((sum, creative) => sum + (creative.metaPurchaseValue ?? 0), 0);
    const captureRate = campaignClicks > 0 ? row.sessions / campaignClicks : null;
    const contributionProjection = row.pltvByBasis?.contribution;
    const sortValues = {
      campaign: row.campaign,
      results: row.purchasesCovered ?? row.purchaseJourneys,
      spend: row.spend ?? 0,
      revenue: row.purchaseRevenueCovered ?? null,
      ltv: row.ltvRealizedNetCollected ?? row.ltvRealizedGross ?? row.ltvRealized ?? null,
      roas: row.roasCovered ?? null,
      roasLtv: row.ltvRoasCovered ?? null,
      pltv: hasIndependentProjectedBases
        // W-A D1 (DISC-AC-001): partial sorts by its vouched projection.
        ? contributionProjection?.status === 'available' || contributionProjection?.status === 'partial'
          ? contributionProjection.pltv12Roas ?? null
          : null
        : row.pltv12Roas ?? null,
      funnel: row.sessions,
      costs: row.costPerPurchase ?? row.costPerTrialStart ?? row.costPerOfferConversion ?? row.costPerPaidSession,
    };
    // W-R5 (DISC-AC-144, review U-4): the chip renders the SERVER-computed
    // verdict — computed WITH the payload's thresholds in one place. The
    // client recompute survives ONLY inside the thresholds popover as a
    // labelled preview; a Save re-fetches the payload for new chips. Organic
    // rows keep the old client rule: never a paid verdict chip.
    const rowVerdict: CampaignVerdict = !isMappedPaidRow(row)
      ? 'none'
      : (hasIndependentProjectedBases
          ? contributionProjection?.verdict ?? row.pltvVerdict
          : row.pltvVerdict) ?? 'none';
    // Stale-cache / zero-coverage honesty shared by every money cell.
    const rowCoveredUsable = (row.spendCoverage?.coveredDays ?? 1) > 0;
    const rowPartialCoverage = Boolean(row.spendCoverage && row.spendCoverage.coveredDays < row.spendCoverage.windowDays);
    // Explicit null is the warehouse's fail-closed mixed/missing-currency
    // state. Undefined is accepted only for pre-contract cached reports.
    const rowMoneyCurrency = row.moneyCurrency === undefined ? currency : row.moneyCurrency;
    const rowMoneyUsable = Boolean(rowMoneyCurrency);
    const rowDisplayCurrency = rowMoneyCurrency || currency;
    // AC-H-8 render arm (DISC-AC-146): ONE resolver names WHY the spend
    // denominator is withheld — `policy_missing` (mixed currencies, no
    // registered policy → register CTA) is DISTINCT from mixed-with-policy
    // (decision lane owns the math; no register CTA). Legacy caches
    // (undefined state) keep the generic caption.
    const rowSpendMixed = row.spendMoneyState === 'mixed_currency';
    const rowSpendCurrencyMissing = row.spendMoneyState === 'missing_currency';
    const rowSpendPresentation = resolveSpendMoneyPresentation(row);
    const rowSpendReasonTitle = rowSpendPresentation.reasonTitle;
    const parentRow: EvidenceRow = {
      key: campaignKey,
      cells: [
        // Identity collapses into ONE cell: campaign id, source + match chips,
        // creatives toggle — two former columns and the Match column merged.
        <Stack key="campaign" spacing={0.5} alignItems="flex-start">
          <span>{row.campaign || '(not set)'}</span>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={sourceMediumLabel(row.source, row.medium)} sx={{ height: 20, fontSize: '0.66rem' }} />
            <Chip
              size="small"
              variant="outlined"
              label={matchQualityLabel(row.matchQuality)}
              data-testid={FUNNEL_REPORTS_TEST_IDS.paidMatchCell(index)}
              sx={{ height: 20, fontSize: '0.66rem' }}
            />
            <VerdictChip
              verdict={rowVerdict}
              cohortAgeDays={row.pltvCohortAgeDays}
              testId={FUNNEL_REPORTS_TEST_IDS.paidVerdictChip(index)}
              title={hasIndependentProjectedBases
                ? 'Contribution pLTV decision signal. Net, settlement, and gross remain separate supporting/audit series.'
                : 'Legacy gross pLTV signal only — not net-collected or contribution profitability evidence.'}
            />
          </Stack>
          {drilldownAvailable && (
            <Button
              size="small"
              variant="text"
              aria-expanded={creativesExpanded}
              data-testid={FUNNEL_REPORTS_TEST_IDS.paidCreativesToggle(index)}
              onClick={() => {
                const expanding = !expandedCampaignCreatives[campaignKey];
                setExpandedCampaignCreatives(current => ({ ...current, [campaignKey]: !current[campaignKey] }));
                // First expand fetches the campaign's full creative page;
                // collapse and re-expand serve the client cache (DISC-AC-134).
                if (expanding && row.metaCampaignId) requestCampaignDrilldown(row.metaCampaignId);
              }}
              sx={{ minWidth: 0, px: 0.75, whiteSpace: 'nowrap' }}
            >
              {`Creatives${drilldownEntry?.totalRows !== undefined ? ` (${drilldownEntry.totalRows})` : ''} ${creativesExpanded ? '▴' : '▾'}`}
            </Button>
          )}
        </Stack>,
        <Box key="results">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatInteger(row.purchasesCovered ?? row.purchaseJourneys)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {`${formatInteger(row.offerConvertedJourneys)} offers · ${formatInteger(row.trialStartedJourneys)} trials`}
          </Typography>
        </Box>,
        // Money columns — each first-class and sortable. n/a (never $0.00) on
        // stale cached payloads and zero-coverage windows; the dimmed «full»
        // line never feeds any ratio.
        <Box key="spend" data-testid={FUNNEL_REPORTS_TEST_IDS.paidSpendCell(index)}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {rowMoneyUsable
              ? formatNullableCurrency(row.spend, rowDisplayCurrency)
              // missing_currency keeps the legacy magnitude (recorded AC-H-9
              // deviation) — render it label-free instead of hiding it.
              : rowSpendCurrencyMissing && typeof row.spend === 'number'
                ? formatNumber(row.spend)
                : 'n/a'}
          </Typography>
          {!rowMoneyUsable && row.spendMoneyState !== 'no_spend' && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.68rem' }}>
              {rowSpendPresentation.spendCaption ?? 'FX/currency evidence incomplete'}
            </Typography>
          )}
          {row.spendCoverage && rowPartialCoverage && (
            <Typography variant="caption" color={row.spendCoverage.coveredDays === 0 ? 'warning.main' : 'text.secondary'} sx={{ display: 'block', fontSize: '0.68rem' }}>
              {row.spendCoverage.coveredDays === 0
                ? '⚠ no synced spend days in window'
                : `Spend days: ${row.spendCoverage.coveredDays} of ${row.spendCoverage.windowDays}`}
            </Typography>
          )}
          {(row.unmatchedSpend ?? 0) > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.68rem' }}>
              {rowMoneyUsable
                ? `+${formatCurrency(row.unmatchedSpend ?? 0, rowDisplayCurrency)} unmatched`
                : 'unmatched spend not safely aggregatable'}
            </Typography>
          )}
        </Box>,
        <Box key="revenue" data-testid={FUNNEL_REPORTS_TEST_IDS.paidRevenueCell(index)}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600 }}
            title="Purchase revenue on spend-covered days only — the numerator of ROAS."
          >
            {row.purchaseRevenueCovered !== undefined && rowCoveredUsable && rowMoneyUsable
              ? formatCurrency(row.purchaseRevenueCovered, rowDisplayCurrency)
              : 'n/a'}
          </Typography>
          {typeof row.purchaseRevenue === 'number' && rowPartialCoverage && rowMoneyUsable && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.68rem' }} title="Full report-window revenue — never feeds ROAS.">
              {`full ${formatCurrency(row.purchaseRevenue, rowDisplayCurrency)}`}
            </Typography>
          )}
        </Box>,
        <Box key="ltv" data-testid={FUNNEL_REPORTS_TEST_IDS.paidLtvCell(index)}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600 }}
            title="Compatibility realized LTV is explicitly gross collected. Net, settlement, and contribution never inherit its value."
          >
            {rowCoveredUsable && rowMoneyUsable && (row.ltvRealizedGross ?? row.ltvRealized) !== undefined
              // W-A: a partial basis renders its VOUCHED sum with the ≥ marker.
              ? `Gross ${row.ltvValueBasis?.gross.status === 'partial' ? '≥ ' : ''}${formatCurrency((row.ltvRealizedGross ?? row.ltvRealized) as number, rowDisplayCurrency)}`
              : 'n/a'}
          </Typography>
          {/* W-R5 rider (owner 2026-08-24): collected checks — server-computed
              vouched cycle count; pending rides the same basis metadata. */}
          {typeof row.pltvByBasis?.gross?.collectedCycles === 'number' && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={FUNNEL_REPORTS_TEST_IDS.paidChecksCollected(index)}
              sx={{ display: 'block', fontSize: '0.68rem' }}
              title="Checks (payments) collected AND verified against provider controls across this campaign's attributed subscriptions. Pending = beyond the verified boundary, not yet countable."
            >
              {`${formatInteger(row.pltvByBasis.gross.collectedCycles)} checks collected${
                (row.pltvByBasis.gross.pendingCycles ?? 0) > 0
                  ? ` · ${formatInteger(row.pltvByBasis.gross.pendingCycles ?? 0)} pending`
                  : ''}`}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {`Net ${netCollectedBasisSupported && rowCoveredUsable && rowMoneyUsable && typeof row.ltvRealizedNetCollected === 'number'
              ? `${row.ltvValueBasis?.netCollected.status === 'partial' ? '≥ ' : ''}${formatCurrency(row.ltvRealizedNetCollected, rowDisplayCurrency)}`
              : 'n/a'}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {`Settlement ${settlementBasisSupported && rowCoveredUsable && rowMoneyUsable && typeof row.ltvSettlementCash === 'number'
              ? `${row.ltvValueBasis?.settlementCash.status === 'partial' ? '≥ ' : ''}${formatCurrency(row.ltvSettlementCash, rowDisplayCurrency)}`
              : 'n/a'}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {`Contribution before ads ${rowCoveredUsable && rowMoneyUsable && typeof row.ltvContribution === 'number'
              ? `${row.ltvValueBasis?.contribution.status === 'partial' ? '≥ ' : ''}${formatCurrency(row.ltvContribution, rowDisplayCurrency)}`
              : 'n/a'}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {`Contribution after ads ${rowCoveredUsable && rowMoneyUsable && typeof row.ltvContributionAfterAdSpend === 'number'
              ? `${row.ltvValueBasis?.contribution.status === 'partial' ? '≥ ' : ''}${formatCurrency(row.ltvContributionAfterAdSpend, rowDisplayCurrency)}`
              : 'n/a'}`}
          </Typography>
          {/* W-A (DISC-AC-017): the caveat renders NEXT TO a shown value only
              while recent dispute events touched the project — a permanent
              caveat would be noise, not honesty. Server-computed flag. */}
          {moneyDiagnosis?.disputeCaveat
            && rowCoveredUsable && rowMoneyUsable
            && (row.ltvRealizedGross ?? row.ltvRealized) !== undefined && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={FUNNEL_REPORTS_TEST_IDS.paidDisputeCaveat(index)}
              sx={{ display: 'block', fontSize: '0.64rem' }}
            >
              value shown · recent dispute events may adjust it
            </Typography>
          )}
          {row.ltvValueBasis && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.64rem' }}>
              {row.ltvValueBasis.contribution.status === 'available'
                ? `${formatInteger(row.ltvSubscriptions ?? 0)} subs · reconciled`
                // W-A: a partial realized basis is vouched-prefix money, not a
                // failure — say so with the boundary tokens.
                : row.ltvValueBasis.contribution.status === 'partial'
                  ? `${formatInteger(row.ltvSubscriptions ?? 0)} subs · vouched prefix${row.ltvValueBasis.contribution.pendingSubscriptions ? ` · ${row.ltvValueBasis.contribution.pendingSubscriptions} pending` : ''}`
                // AC-H-8: the policy fail-closed state gets human words, not a
                // de-underscored raw reason code.
                : row.ltvValueBasis.contribution.unavailableReasons[0]?.startsWith('money_reporting_policy_missing')
                  ? 'reporting policy not configured'
                  // I5d: vocabulary phrase + self-heal note; unknown codes
                  // keep the readable de-underscore fallback.
                  : row.ltvValueBasis.contribution.unavailableReasons[0]
                    ? projectedBasisUnavailableReason(row.ltvValueBasis.contribution.unavailableReasons)
                    : 'contribution coverage incomplete'}
            </Typography>
          )}
        </Box>,
        <Box key="roas" data-testid={FUNNEL_REPORTS_TEST_IDS.paidRoasCell(index)}>
          <RoasValue
            value={rowCoveredUsable ? row.roasCovered : undefined}
            title={rowSpendReasonTitle ?? 'Revenue and spend limited to the SAME synced delivery days — full-window revenue over partial spend would overstate.'}
          />
        </Box>,
        <Box key="roas-ltv" data-testid={FUNNEL_REPORTS_TEST_IDS.paidRoasLtvCell(index)}>
          <RoasValue
            value={rowCoveredUsable ? row.ltvRoasCovered : undefined}
            payback
            title={rowSpendReasonTitle ?? 'Gross-collected realized LTV ÷ matched spend. This is not net or contribution ROAS.'}
          />
        </Box>,
        <Box key="pltv" data-testid={FUNNEL_REPORTS_TEST_IDS.paidPltvCell(index)}>
          {/* S7 / D-J: pLTV never renders without CAC beside it (plain view too). */}
          <PltvWithCac
            currency={currency}
            cac={cacBesideFromRow(row)}
            testId={FUNNEL_REPORTS_TEST_IDS.pltvCacLine('campaign', index)}
            pltv={(
              <PltvCellContent
                row={row}
                verdict={rowVerdict}
                thresholds={activeThresholds}
                basisSupported={projectedBasisSupported}
                independentRequired={hasIndependentProjectedBases}
                watchOnlyTestId={FUNNEL_REPORTS_TEST_IDS.paidCampaignWatchOnlyChip(index)}
                watchOnlyEnabled={creativeDecisionsEnabled}
              />
            )}
          />
        </Box>,
        // Optional depth columns — hidden by default («More columns»).
        ...(showAllPaidColumns
          ? [
        <EvidenceStack
          key="funnel"
          entries={[
            { label: 'Sessions', value: `${formatInteger(row.sessions)} · ${formatInteger(row.paidSessions ?? 0)} paid` },
            { label: 'Starts', value: formatInteger(row.startedJourneys) },
            { label: 'First screen', value: formatRateWithCounts(row.firstScreenOpenRate ?? null, row.firstScreenOpenedJourneys ?? 0, row.startedJourneys) },
            { label: 'First -> second', value: formatRateWithCounts(row.firstToSecondRate ?? null, row.firstToSecondJourneys ?? 0, row.firstScreenOpenedJourneys ?? 0) },
          ]}
        />,
        <EvidenceStack
          key="costs"
          entries={paidPeopleMode
            ? [
              // S7 (AC-015, S-1): people-mode stack — the SAME served twins the
              // Traffic sources view reads; «Paid session» is a visits notion.
              { label: 'CAC', value: cacLineText(cacBesideFromRow(row), currency).replace(/^CAC /, '') },
              { label: 'Purchase', value: peopleUnitCost(row.costPerPurchasePeople, row.purchasePeople, currency) },
              { label: 'First -> second', value: peopleUnitCost(row.costPerFirstToSecondPeople, row.firstToSecondPeople, currency) },
              { label: 'Start', value: peopleUnitCost(row.costPerStartPeople, row.startedPeople, currency) },
              ...(rowSpendPresentation.tooltipValue
                ? [{ label: 'Why n/a', value: rowSpendPresentation.tooltipValue }]
                : []),
            ]
            : [
            // The blended figure leads: matched spend ÷ ALL conversions; the
            // per-type lines are INDEPENDENT cost-per-one-outcome views over
            // the SAME spend (price · count) — not a budget split.
            { label: 'Any conversion', value: unitCostWithCount(row.costPerOfferConversion, row.offerConvertedJourneys, currency) },
            { label: 'Purchase', value: unitCostWithCount(row.costPerPurchase, row.purchaseJourneys, currency) },
            { label: 'Trial', value: unitCostWithCount(row.costPerTrialStart, row.trialStartedJourneys, currency) },
            { label: 'Paid session', value: unitCostWithCount(row.costPerPaidSession, row.paidSessions ?? 0, currency) },
            { label: 'First -> second', value: unitCostWithCount(row.costPerFirstToSecond, row.firstToSecondJourneys ?? 0, currency) },
            // AC-H-8: unit costs share the spend denominator — when it is
            // withheld the stack says why instead of five bare n/a lines
            // (policy_missing gets its own named state, never conflated).
            ...(rowSpendPresentation.tooltipValue
              ? [{ label: 'Why n/a', value: rowSpendPresentation.tooltipValue }]
              : []),
          ]}
        />,
        <EvidenceStack
          key="meta"
          entries={[
            { label: 'Creative', value: creativeLabel(row) },
            { label: 'Destination', value: destinationEvidenceLabel(row) },
            { label: 'Delivery', value: metaDeliveryLabel(row) },
            { label: 'IDs', value: metaIdsLabel(row) },
            ...(drilldownRows.length > 0
              ? [
                  // Sessions-per-click over the fetched creatives — the
                  // tracking honesty gauge. A ratio, not a percent: one click
                  // legitimately yields many sessions over the window (utm
                  // persists across revisits); the alarm signal is < 1×.
                  { label: 'Sessions / click', value: captureRate !== null ? `${captureRate.toFixed(1)}× (${formatInteger(row.sessions)} / ${formatInteger(campaignClicks)})` : 'n/a' },
                  // Pages without metaPurchaseValue — n/a, not a fake $0.
                  { label: 'Meta value', value: drilldownRows.some((creative) => creative.metaPurchaseValue !== undefined) ? formatCurrency(metaValueTotal, currency) : 'n/a' },
                ]
              : []),
          ]}
        />,
          ]
          : []),
      ],
      sortValues,
    };

    if (!creativesExpanded || !drilldownAvailable) return [parentRow];

    // Sub-rows inherit the parent's sortValues — the stable sort keeps them
    // glued right below the parent under any column order (versions pattern).
    // Sub-rows share the parent's COLUMN GRID: shared metrics land in the same
    // columns as the campaign (comparability), creative-unique metrics live in
    // a dimmed meta-line inside the identity cell. Ad-grain values render only
    // when sessions resolved this ad; otherwise «—» (never a fake 0).
    const creativeSubRows: EvidenceRow[] = drilldownRows.map((creative, creativeIndex) => ({
      key: `${campaignKey}-creative-${creative.creativeId || creative.adId || creativeIndex}`,
      sortValues,
      cells: [
        <Stack
          key="creative"
          direction="row"
          spacing={1}
          alignItems="flex-start"
          data-testid={FUNNEL_REPORTS_TEST_IDS.paidCreativeRow(index, creativeIndex)}
          sx={versionSubRowCellSx}
        >
          {creative.thumbnailUrl && (
            // Thumbnail opens the full-size creative image — the NO-LOGIN
            // preview (Meta's preview link gates behind a Business login).
            <Link
              href={creative.imageUrl ?? creative.thumbnailUrl}
              target="_blank"
              rel="noopener"
              sx={{ display: 'inline-flex', flexShrink: 0 }}
            >
              <Box
                component="img"
                src={creative.thumbnailUrl}
                alt=""
                // Meta CDN signed URLs expire (403) — hide the whole link
                // instead of rendering a broken-image glyph per sub-row.
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  const anchor = event.currentTarget.closest('a');
                  if (anchor) anchor.style.display = 'none';
                }}
                sx={{ width: 32, height: 32, borderRadius: 0.5, objectFit: 'cover' }}
              />
            </Link>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
              {creative.creativeName || creative.creativeId || creative.adId || 'n/a'}
            </Typography>
            {creative.creativeTitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {creative.creativeTitle}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
              {[
                creative.ctr !== null && creative.ctr !== undefined ? `CTR ${(creative.ctr * 100).toFixed(2)}%` : null,
                creative.cpc !== null && creative.cpc !== undefined ? `CPC ${formatCurrency(creative.cpc, currency)}` : null,
                // S3 (AC-021/022): Meta purchases like-for-like (7-day click);
                // «N people» from summed unique_clicks is gone — not a people count.
                `${metaPurchasesCopy(creative).compact}${creative.metaPurchaseValue !== undefined ? ` / ${formatCurrency(creative.metaPurchaseValue, currency)}` : ''}`,
                metaPurchasesCopy(creative).viewThrough ?? null,
              ].filter(Boolean).join(' · ')}
            </Typography>
            <Stack direction="row" spacing={1}>
              {creative.imageUrl && (
                <Link href={creative.imageUrl} target="_blank" rel="noopener" underline="hover" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  Image <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
              )}
              {creative.previewLink && (
                <Link
                  href={creative.previewLink}
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  variant="caption"
                  title="Opens Meta's ad preview — requires a Facebook login with access to the ad account."
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  Preview <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
              )}
            </Stack>
          </Box>
        </Stack>,
        <Box key="results">
          {creative.adSessions !== undefined ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatInteger(creative.adPurchaseJourneys ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                {`${formatInteger(creative.adOfferJourneys ?? 0)} offers · ${formatInteger(creative.adSessions)} sess`}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="text.disabled" title="Sessions did not resolve this ad — Meta-attributed purchases (7-day click) shown for reference only.">
              {metaPurchasesCopy(creative).compact}
            </Typography>
          )}
        </Box>,
        <Box key="spend">
          <Typography variant="body2">
            {formatCurrency(creative.adSpend ?? creative.spend, currency)}
          </Typography>
          {creative.adSessions !== undefined
            && creative.adCoveredDays !== undefined
            && creative.adWindowDays !== undefined
            && creative.adCoveredDays < creative.adWindowDays && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
              {`ad days ${creative.adCoveredDays} of ${creative.adWindowDays}`}
            </Typography>
          )}
        </Box>,
        <Box key="revenue">
          {creative.adSessions !== undefined ? (
            <Typography variant="body2">{formatCurrency(creative.adRevenueCovered ?? 0, currency)}</Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </Box>,
        <Typography key="ltv" variant="body2" color="text.disabled" title="Ad-grain LTV arrives with predictive LTV (Phase 2).">—</Typography>,
        <Box key="roas">
          {creative.adSessions !== undefined ? (
            <RoasValue value={creative.adRoasCovered} title="The ad's own covered revenue ÷ the ad's own spend." />
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </Box>,
        <Typography key="roas-ltv" variant="body2" color="text.disabled">—</Typography>,
        // I3 (decision 2.3): the watch-only pLTV chip replaces the bare dash
        // when the snapshot mart served an estimate for THIS ad. Informational
        // default palette, never success/error; deep-links into the Creative
        // Decisions drawer regardless of the scope toggle (unscoped detail).
        <PltvWithCac
          key="pltv"
          currency={currency}
          cac={cacBesideFromRow(creative)}
          testId={FUNNEL_REPORTS_TEST_IDS.pltvCacLine('creative', creativeIndex)}
          pltv={(
            <WatchOnlyPltvChip
              watchOnly={creative.watchOnlyPltv}
              projectId={projectId}
              enabled={creativeDecisionsEnabled}
              testId={FUNNEL_REPORTS_TEST_IDS.paidCreativeWatchOnlyChip(index, creativeIndex)}
            />
          )}
        />,
        ...(showAllPaidColumns
          ? [
        <EvidenceStack
          key="funnel"
          entries={[
            // S3 (AC-022): no «People clicked» — summed unique_clicks is not people.
            { label: 'Clicks', value: formatInteger(creative.clicks) },
            ...(creative.adSessions !== undefined
              ? [{ label: 'Sessions (ours)', value: formatInteger(creative.adSessions) }]
              : []),
          ]}
        />,
        <EvidenceStack
          key="costs"
          entries={[
            // S7 (AC-061): the same people figures the creatives tab shows.
            ...(creative.peopleIdentityPlane === 'linkage'
              ? [
                { label: 'People', value: typeof creative.startedPeople === 'number' ? formatInteger(creative.startedPeople) : 'n/a' },
                { label: 'Cost / person', value: formatNullableCurrency(creative.costPerStartPeople ?? null, currency) },
              ]
              : []),
            { label: 'Cost / offer', value: formatNullableCurrency(creative.adCostPerOffer ?? null, currency) },
            { label: 'CPC', value: formatNullableCurrency(creative.cpc, currency) },
          ]}
        />,
        <EvidenceStack
          key="meta"
          entries={[
            { label: 'Installs (Meta)', value: formatInteger(creative.metaInstalls ?? 0) },
            // S3 (D-F, AC-021): like-for-like bucket first; view-through is its
            // own line and only when > 0.
            {
              label: metaPurchasesCopy(creative).basis === 'seven_day_click' ? 'Purchases (Meta · 7-day click)' : 'Purchases (Meta · all windows)',
              value: formatInteger(metaPurchasesCopy(creative).basis === 'seven_day_click' ? creative.metaPurchases7dClick ?? 0 : creative.metaPurchases ?? 0),
            },
            ...(metaPurchasesCopy(creative).viewThrough
              ? [{ label: 'View-through (Meta)', value: `${formatInteger(creative.metaPurchasesViewThrough ?? 0)} · not observable` }]
              : []),
            { label: 'Meta value', value: formatCurrency(creative.metaPurchaseValue ?? 0, currency) },
          ]}
        />,
          ]
          : []),
      ],
    }));

    // First-expand affordance (designer REVIEW must-fix, dev 0.0.172): while
    // the initial full page fetches, the preloaded top-10 stays visible and
    // this sub-row announces the fetch — mirroring the load-more path instead
    // of a silent 4-second gap followed by a layout jump.
    if (drilldownEntry?.loading && !drilldownEntry.rows.length) {
      creativeSubRows.push({
        key: `${campaignKey}-creatives-loading`,
        sortValues,
        cells: [
          <Box
            key="drilldown-loading"
            sx={versionSubRowCellSx}
            data-testid={FUNNEL_REPORTS_TEST_IDS.paidCampaignCreativesLoading(index)}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Loading all creatives…</Typography>
            </Stack>
          </Box>,
          ...Array.from({ length: Math.max(0, parentRow.cells.length - 1) }, (_, cellIndex) => (
            <React.Fragment key={`drilldown-loading-pad-${cellIndex}`} />
          )),
        ],
      });
    }
    // W-R6: with no preloaded fallback rows, a failed first page must offer a
    // retry — otherwise the expand is a permanent silent blank.
    if (drilldownEntry?.failed && row.metaCampaignId) {
      const retryCampaignId = row.metaCampaignId;
      creativeSubRows.push({
        key: `${campaignKey}-creatives-failed`,
        sortValues,
        cells: [
          <Box key="drilldown-failed" sx={versionSubRowCellSx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">Couldn't load creatives.</Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => requestCampaignDrilldown(retryCampaignId)}
                sx={{ minWidth: 0, px: 0.75, whiteSpace: 'nowrap' }}
              >
                Retry
              </Button>
            </Stack>
          </Box>,
          ...Array.from({ length: Math.max(0, parentRow.cells.length - 1) }, (_, cellIndex) => (
            <React.Fragment key={`drilldown-failed-pad-${cellIndex}`} />
          )),
        ],
      });
    }
    // DISC-AC-134: continuation pages load on demand — never automatically.
    if (drilldownEntry?.nextCursor && row.metaCampaignId) {
      const drilldownCampaignId = row.metaCampaignId;
      creativeSubRows.push({
        key: `${campaignKey}-creatives-more`,
        sortValues,
        cells: [
          <Box key="load-more" sx={versionSubRowCellSx}>
            <Button
              size="small"
              variant="text"
              disabled={drilldownEntry.loading}
              onClick={() => loadMoreCampaignCreatives(drilldownCampaignId)}
              data-testid={FUNNEL_REPORTS_TEST_IDS.paidCampaignCreativesLoadMore(index)}
              sx={{ minWidth: 0, px: 0.75, whiteSpace: 'nowrap' }}
            >
              {drilldownEntry.loading
                ? 'Loading creatives…'
                : `Load more creatives (${drilldownEntry.rows.length} of ${drilldownEntry.totalRows ?? '…'})`}
            </Button>
          </Box>,
          ...Array.from({ length: Math.max(0, parentRow.cells.length - 1) }, (_, cellIndex) => (
            <React.Fragment key={`load-more-pad-${cellIndex}`} />
          )),
        ],
      });
    }
    return [parentRow, ...creativeSubRows];
  });
  // W-R5 (DISC-AC-144): the hint thresholds read the SERVER tile aggregates —
  // no client money summation; absent tiles (pre-W-R5 cache) hide the hint.
  const matchedSpendTotal = paidSummaryTiles?.totals.spend ?? 0;
  const unmatchedSpendTotal = paidSummaryTiles?.unmatched.spend ?? 0;
  const showUnmatchedHint = Boolean(paidSummaryTiles) && unmatchedSpendTotal > 0 && unmatchedSpendTotal >= matchedSpendTotal;
  const fbParamsSnippet = 'fb_campaign_id={{campaign.id}}&fb_adset_id={{adset.id}}&fb_ad_id={{ad.id}}';
  const copyFbParamsSnippet = () => {
    void navigator.clipboard?.writeText(fbParamsSnippet).then(() => {
      setCopiedFbParamsSnippet(true);
      setTimeout(() => setCopiedFbParamsSnippet(false), 2000);
    });
  };
  // Paid Acquisition section body — shared between the in-page section and the
  // fullscreen dialog (dense-table pattern); tableId keeps testids unique.
  const renderPaidAcquisitionBody = (tableId: string, isFullscreen: boolean): React.ReactElement => (
    <Stack spacing={2}>
      {/* F3 (FT-AC-088/089, D-F3-1): the attribution caption + selector lead
          the section — they qualify every money/conversion figure below. The
          ACTIVE model always derives from the payload; an absent block (stale
          cache, fixture, failed lane) renders the typed absence and NO
          selector (D-F3-6). */}
      {attribution?.selectedModelId ? (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap rowGap={0.5}>
          <Box
            sx={{ flex: '1 1 420px', minWidth: 260 }}
            data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidAttributionCaption}
          >
            <Typography variant="body2">
              <strong>{`Attribution: ${attributionModelCopy(attribution.selectedModelId).label}`}</strong>
              {attributionModelCopy(attribution.selectedModelId).description
                ? ` — ${attributionModelCopy(attribution.selectedModelId).description}`
                : ''}
              {' · '}
              <Box
                component="span"
                data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidMetaEvidenceNote}
              >
                {VIEW_THROUGH_DIVERGENCE_NOTE}
              </Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
              {attribution.selectedModelId}
            </Typography>
          </Box>
          {onAttributionModelChange && (attribution.availableModels?.length ?? 0) > 1 && (
            <Select
              size="small"
              value={attribution.selectedModelId}
              disabled={attributionSwitchPending}
              onChange={event => {
                const next = String(event.target.value);
                if (next && next !== attribution.selectedModelId) onAttributionModelChange(next);
              }}
              data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidAttributionModelSelect}
              sx={{ minWidth: 200 }}
            >
              {(attribution.availableModels ?? []).map(modelId => {
                const label = attributionModelCopy(modelId).label;
                return (
                  <MenuItem
                    key={modelId}
                    value={modelId}
                    data-testid={FUNNEL_REPORTS_TEST_IDS.paidAttributionModelOption(modelId)}
                  >
                    {label.charAt(0).toUpperCase() + label.slice(1)}
                    {modelId === attribution.defaultModelId ? ' · default' : ''}
                  </MenuItem>
                );
              })}
            </Select>
          )}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
          data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidAttributionCaption}
        >
          {ATTRIBUTION_ABSENCE_LABEL}
        </Typography>
      )}
      {attributionSwitchPending && <LinearProgress />}
      {projectId && creativeDecisionsEnabled && (
        <Alert
          severity="info"
          action={(
            <Button
              size="small"
              href={creativeDecisionsHref(projectId)}
              data-testid={FUNNEL_REPORTS_TEST_IDS.paidCreativeDecisionsOpen}
            >
              Open Creative decisions
            </Button>
          )}
        >
          Prioritize provider deployments by current spend pace, returned value, evidence coverage, and a reviewable next action.
        </Alert>
      )}
      {/* I5c: ONE server-diagnosed sentence naming the dominant withholding
          cause (info severity — self-healing is not an incident). Present
          only when the server emitted the rollup (≥60% bar, UNLOCK-AC-045);
          per-cell reasons stay in place below (UNLOCK-AC-046). Raw codes
          live ONLY in the technical disclosure (UNLOCK-AC-049). */}
      {moneyRootCause && (
        <Alert
          severity="info"
          data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidMoneyRootCause}
        >
          {/* The reason phrase is sometimes a noun («money reporting policy not
              configured») and sometimes a whole clause («cost categories … have
              no control source yet»), so the frame is a colon, not «are»:
              «Most money values are cost categories … have no control source
              yet» was the sentence this printed. */}
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {`Most withheld money values trace to one cause: ${humanizeCreativeReasonCode(moneyRootCause.rootReasonCode)}`}
          </Typography>
          <Typography variant="body2">
            {`${Math.round(moneyRootCause.share * 100)}% of the withheld money values in this window trace to this one cause`}
            {/* «no action is needed» was claimed for every cause that was not
                the literal string 'needs operator action' — including the
                policy and pipeline blocks whose own note names a lever. The
                classification is typed now. */}
            {creativeReasonClearsOnItsOwn(moneyRootCause.rootReasonCode)
              ? ' — it clears on its own; no action is needed.'
              : ' — it will not clear on its own.'}
            {' Each withheld cell keeps its own reason in place.'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {`affected: ${moneyRootCause.affectedCells} of ${moneyRootCause.withheldCells} withheld cells`}
            {creativeReasonSelfHealNote(moneyRootCause.rootReasonCode)
              ? ` · ${creativeReasonClearsOnItsOwn(moneyRootCause.rootReasonCode) ? 'expected to clear' : 'what unblocks it'}: ${creativeReasonSelfHealNote(moneyRootCause.rootReasonCode)}`
              : ''}
          </Typography>
        </Alert>
      )}
      {/* W-A diagnosis v1 (DISC-AC-028/031): the nine-category evidence board
          renders UNCONDITIONALLY of the rollup — the operator sees the whole
          board, not only the dominant cause. Server-computed fields only; the
          client maps typed codes to phrases (DISC-AC-030). */}
      {moneyDiagnosis && (
        <Box
          component="details"
          sx={{ '& summary': { cursor: 'pointer', fontSize: '0.75rem', color: 'text.secondary' } }}
          data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidMoneyRootCauseDetails}
        >
          <summary>Technical details — money evidence by category</summary>
          <Box sx={{ mt: 0.5 }}>
            {moneyDiagnosis.categories.map((entry) => (
              <Typography key={entry.category} variant="caption" color="text.secondary" component="div">
                {`${entry.category.replaceAll('_', ' ')}: ${moneyDiagnosisStateLine(entry)}`}
              </Typography>
            ))}
            {moneyRootCause && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                {`root_reason_code: ${moneyRootCause.rootReasonCode} · share ${moneyRootCause.share.toFixed(2)} · contributing: `}
                {moneyRootCause.contributing.map((entry) => `${entry.code} (${entry.count})`).join(', ')}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <PaidAcquisitionSummary
        tiles={paidSummaryTiles}
        currency={currency}
        coverage={coverage}
        thresholds={activeThresholds}
        projectId={projectId}
        // W-R5 (DISC-AC-144): Save updates the popover seed AND re-fetches
        // the payload — the table chips change only with fresh server verdicts.
        onThresholdsSaved={(saved) => {
          setActiveThresholds(saved);
          onVerdictThresholdsSaved?.();
        }}
        isFullscreen={isFullscreen}
        realizedBasisCaption={realizedBasisCaption}
        projectedBasisCaption={projectedBasisCaption}
        projectedBasisSupported={projectedBasisSupported}
        hasIndependentProjectedBases={hasIndependentProjectedBases}
      />
      {/* Collapsed by default (app-wide collapsed-section pattern): a one-line
          summary with the amount; the setup recipe expands on demand. */}
      {showUnmatchedHint && (
        <CollapsedChart
          testId={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.unmatchedSpendHint}
          title={(
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {`${formatCurrency(unmatchedSpendTotal, currency)} of Meta spend has no matched sessions — how to fix`}
              </Typography>
            </Stack>
          )}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Add Meta dynamic URL parameters to your ads so every click carries exact ids:
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              overflowX: 'auto',
            }}
          >
            <Typography component="code" variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
              {fbParamsSnippet}
            </Typography>
            <Button
              size="small"
              onClick={copyFbParamsSnippet}
              data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.unmatchedSpendHintCopy}
            >
              {copiedFbParamsSnippet ? 'Copied' : 'Copy'}
            </Button>
          </Stack>
        </CollapsedChart>
      )}
      <CollapsedChart title="Spend distribution chart" unmountOnExit>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={paidAcquisitionChartRows(chartInputRows)}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="campaign" minTickGap={16} />
            <YAxis />
            <RechartsTooltip formatter={tooltipNumberFormatter} />
            <Bar dataKey="spend" name="Matched spend" fill={chart.primary} />
            <Bar dataKey="unmatchedSpend" name="Unmatched spend" fill={chart.negative} />
            <Bar dataKey="offerConvertedJourneys" name="Offer conversions" fill={chart.positive} />
          </BarChart>
        </ResponsiveContainer>
      </CollapsedChart>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          label="Mapped to funnels only"
          size="small"
          clickable
          color={mappedOnly ? 'primary' : 'default'}
          variant={mappedOnly ? 'filled' : 'outlined'}
          onClick={() => setMappedOnly(current => !current)}
          data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidMappedOnlyToggle}
        />
        <Chip
          label="More columns"
          size="small"
          clickable
          color={showAllPaidColumns ? 'primary' : 'default'}
          variant={showAllPaidColumns ? 'filled' : 'outlined'}
          onClick={() => setShowAllPaidColumns(current => !current)}
          data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidMoreColumnsToggle}
        />
        {mappedOnly && (
          <Typography variant="caption" color="text.secondary">
            Hiding unmatched spend and organic utm buckets ({formatInteger(rows.length - visiblePaidAcquisitionRows.length)} rows). Totals above are always mapped-only.
          </Typography>
        )}
        {!isFullscreen && (
          <IconButton
            size="small"
            title="Open fullscreen"
            aria-label="Open Paid Acquisition fullscreen"
            onClick={() => setPaidFullscreen(true)}
            data-testid={FUNNEL_REPORTS_TEST_IDS.paidFullscreenOpen}
            sx={{ ml: 'auto' }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <SortableEvidenceTable
        id={tableId}
        columns={paidAcquisitionColumns}
        rows={paidAcquisitionTableRows}
        sortState={paidAcquisitionSort}
        onSort={columnId => setPaidAcquisitionSort(current => nextSortState(current, columnId))}
        // The section's centerpiece gets real room; fullscreen fills the viewport.
        maxHeight={isFullscreen ? 'calc(100vh - 190px)' : 560}
      />
    </Stack>
  );

  return (
    <>
      <ChartSection
        id="paid-acquisition"
        title="Paid Acquisition"
        empty={empty}
        error={error}
        // D deep link: analyst campaign-brief cards land here with
        // ?section=paid-acquisition — the target must open, not sit collapsed.
        //
        // P1-12 (design review) is an OPEN OWNER DECISION, not a defect:
        // `VISIBILITY_SPEC.md:53-58` draws the member page with «Paid
        // Acquisition (open)» while C1 names only Traffic by version, so the
        // page delivers two of the three answers its thesis promises without a
        // click. Opening it was tried and measured: the default page grows from
        // 1 370 px to 3 093 px and the first render gets ~3× more expensive
        // (this section is the widest table on the page with the creative
        // drill-downs under it; the jest file for this page went 36 s → 96 s and
        // started timing out). C1 stands until the owner rules, and the
        // measurement is in `DESIGN_REVIEW.md` → Deferred D7 so the ruling has
        // both numbers in front of it.
        defaultExpanded={showEmptySections || isPaidAcquisitionDeepLink()}
        description="Which paid campaigns are buying useful sessions, where spend is unmatched, and which conversion costs need action?"
      >
        {renderPaidAcquisitionBody('paid-acquisition', false)}
      </ChartSection>
      <Dialog fullScreen open={paidFullscreen} onClose={() => setPaidFullscreen(false)}>
        {/* Close lives on the LEFT of the header — the app-wide dismiss invariant. */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <IconButton
            onClick={() => setPaidFullscreen(false)}
            aria-label="Close fullscreen"
            data-testid={FUNNEL_REPORTS_TEST_IDS.paidFullscreenClose}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6">Paid Acquisition</Typography>
        </Box>
        <Box sx={{ p: 3, overflow: 'auto' }}>
          {paidFullscreen && renderPaidAcquisitionBody('paid-acquisition-fullscreen', true)}
        </Box>
      </Dialog>
    </>
  );
});

function PaidAcquisitionSummary({
  tiles,
  currency = 'usd',
  coverage,
  thresholds,
  projectId,
  onThresholdsSaved,
  isFullscreen,
  realizedBasisCaption = 'gross collected',
  projectedBasisCaption = 'gross collected · estimated',
  projectedBasisSupported = true,
  hasIndependentProjectedBases = false,
}: {
  /** W-R5 (DISC-AC-144): the SERVER-computed tile block — client renders only. */
  tiles?: FunnelReportsDashboardData['paidSummaryTiles'];
  currency?: string;
  coverage?: FunnelReportsDashboardData['campaignEconomicsCoverage'];
  thresholds: CampaignVerdictThresholds;
  projectId?: string;
  onThresholdsSaved: (thresholds: CampaignVerdictThresholds) => void;
  isFullscreen?: boolean;
  realizedBasisCaption?: string;
  projectedBasisCaption?: string;
  projectedBasisSupported?: boolean;
  hasIndependentProjectedBases?: boolean;
}): React.ReactElement {
  const [thresholdsAnchorState, setThresholdsAnchorState] = React.useState<HTMLElement | null>(null);
  // W-R5 (DISC-AC-144, review U-3): the ten money tiles render the SERVER-
  // computed paidSummaryTiles block — the browser aggregates NOTHING. The
  // pre-W-R5 client engine lives on verbatim in
  // funnel-insights/serving/paidSummaryTiles (the one compute, byte-pinned).
  if (!tiles) {
    return (
      <Stack spacing={1}>
        <Alert severity="info" data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidSummaryTilesUnavailable}>
          Summary tiles are unavailable on this cached payload — press Refresh to load them.
        </Alert>
      </Stack>
    );
  }
  const mappedRowCount = tiles.mappedRowCount;
  const mappedMoneyUsable = tiles.mappedMoneyUsable;
  const mappedMixedCurrency = tiles.mappedMixedCurrency;
  const summaryCurrency = tiles.currency;
  const mappedTotals = tiles.totals;
  const coveredKnown = tiles.coveredKnown;
  const anyCoveredDays = tiles.anyCoveredDays;
  const coverageDataUsable = tiles.coverageDataUsable;
  const coveredUsable = tiles.coveredUsable;
  const revenueFullKnown = tiles.revenueFullKnown;
  const roasCovered = tiles.roasCovered;
  const grossTile = tiles.realized.gross;
  const netCollectedTile = tiles.realized.netCollected;
  const settlementTile = tiles.realized.settlementCash;
  const contributionTile = tiles.realized.contribution;
  const contributionAfterAdsTile = tiles.realized.contributionAfterAdSpend;
  const ltvSubscriptionsTotal = tiles.ltvSubscriptionsTotal;
  const ltvRoas = tiles.ltvRoas;
  const totals = { unmatchedSpend: tiles.unmatched.spend };
  const unmatchedMoneyUsable = tiles.unmatched.moneyUsable;
  const unmatchedCurrency = tiles.unmatched.currency;
  const partialCoverage = Boolean(coverage && coverage.coveredDays < coverage.windowDays);
  const contributionPortfolio = tiles.projected.contribution;
  const netCollectedPortfolio = tiles.projected.netCollected;
  const settlementPortfolio = tiles.projected.settlementCash;
  const grossPortfolio = tiles.projected.gross;
  const primaryProjectedPortfolio = hasIndependentProjectedBases ? contributionPortfolio : grossPortfolio;
  const contributionUnavailableReason = tiles.projected.contributionUnavailableReasonCode ?? undefined;
  // Rule 7 (DISC-AC-029): the server serves typed cause CODES; the client is
  // a pure code -> phrase lookup with the two legacy fallback phrases.
  const realizedTileCause = (
    basis: 'gross' | 'netCollected' | 'settlementCash' | 'contribution',
  ): string => {
    const code = tiles.realizedCauseCodes[basis];
    if (code) return humanizeCreativeReasonCode(code) ?? code.replaceAll('_', ' ');
    if (!(tiles.mappedRowCount > 0 && tiles.anyCoveredDays && tiles.mappedMoneyUsable)) {
      return 'spend/currency evidence incomplete for this window';
    }
    return 'no campaign passes its money-basis gate in this window';
  };
  const thresholdsAnchor = thresholdsAnchorState;
  const setThresholdsAnchor = setThresholdsAnchorState;

  return (
    <Stack spacing={1}>
      {mappedRowCount > 0 && !mappedMoneyUsable && (
        // AC-H-8: mixed currencies get their own typed banner naming the fix;
        // everything else keeps the generic FX-evidence copy.
        mappedMixedCurrency ? (
          <Alert severity="warning" data-testid={FUNNEL_REPORTS_TEST_IDS.paidSummaryMixedCurrencyBanner}>
            <AlertTitle>Mixed ad-account currencies — cross-account totals withheld</AlertTitle>
            {`This project's ad accounts report spend in different currencies${tiles.mappedCurrencies.length > 1 ? ` (${tiles.mappedCurrencies.join(', ')})` : ''}. `}
            Sums and cost-per rates that would mix currencies are withheld instead of shown wrong.
            They resume automatically once a money-reporting (FX) policy is registered for this
            project. Funnel counts remain visible and rows can still be reviewed per account.
          </Alert>
        ) : (
          <Alert severity="warning">
            <AlertTitle>FX/currency evidence incomplete — global money ranking disabled</AlertTitle>
            Spend, revenue, realized LTV, ROAS and projected-LTV totals stay unavailable until every
            included campaign has one compatible native currency or a complete versioned FX basis.
            Funnel counts remain visible and rows can still be reviewed by account/currency context.
          </Alert>
        )
      )}
      {coveredKnown && coverage && partialCoverage && (
        <Alert severity="warning" data-testid={FUNNEL_REPORTS_TEST_IDS.paidSummaryCoverageCaption}>
          <AlertTitle>
            {`Ad spend is synced for ${coverage.coveredDays} of ${coverage.windowDays} report days`}
          </AlertTitle>
          {coverage.windowFrom && coverage.windowTo && (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {`Report window: ${formatCoverageDate(coverage.windowFrom)}–${formatCoverageDate(coverage.windowTo)} · synced ad spend: ${formatCoverageDate(coverage.from)}–${formatCoverageDate(coverage.to)} (${coverage.coveredDays} ${coverage.coveredDays === 1 ? 'day' : 'days'}).`}
            </Typography>
          )}
          <Typography variant="body2">
            Revenue, ROAS and purchases in the money tiles count only sessions on the synced spend days — a
            ratio of two sources must compare the same days, otherwise ROAS overstates. Full-window revenue is
            shown separately and never feeds ROAS. Run a longer spend sync to widen coverage.
          </Typography>
        </Alert>
      )}
      <Grid container spacing={1}>
        <MiniMetric
          label="Ad spend (mapped)"
          value={mappedMoneyUsable ? formatCurrency(mappedTotals.spend, summaryCurrency) : 'n/a'}
          testId={FUNNEL_REPORTS_TEST_IDS.paidSummaryAdSpend}
        />
        <MiniMetric
          label="Ad revenue (covered)"
          value={coveredUsable ? formatCurrency(mappedTotals.revenueCovered, summaryCurrency) : 'n/a'}
          title="Purchase revenue of mapped campaigns on spend-covered delivery days only."
          testId={FUNNEL_REPORTS_TEST_IDS.paidSummaryAdRevenue}
        />
        <MiniMetric
          label="ROAS (covered)"
          value={roasCovered !== null ? `${roasCovered.toFixed(2)}×` : 'n/a'}
          title="Ad revenue (covered) ÷ ad spend (mapped). Both sides use the same synced delivery days — full-window revenue over partial spend would overstate."
          testId={FUNNEL_REPORTS_TEST_IDS.paidSummaryRoas}
        />
        <MiniMetric
          label="Gross collected (realized)"
          value={grossTile !== null ? `${tileIsBounded(grossTile) ? '≥ ' : ''}${formatCurrency(grossTile.value, summaryCurrency)}` : 'n/a'}
          sub={grossTile !== null
            ? `${ltvSubscriptionsTotal === null ? 'n/a' : formatInteger(ltvSubscriptionsTotal)} subs · ${realizedBasisCaption}`
            : undefined}
          boundary={tileBoundaryLine(grossTile) !== null
            ? { text: tileBoundaryLine(grossTile) as string, testId: FUNNEL_REPORTS_TEST_IDS.paidSummaryTileBoundary('gross') }
            : null}
          cause={grossTile === null
            ? { text: realizedTileCause('gross'), testId: FUNNEL_REPORTS_TEST_IDS.paidTileCause('gross') }
            : null}
          title={`Realized subscription revenue to date (first payment + renewals) of the covered cohort. ${realizedBasisCaption[0].toUpperCase()}${realizedBasisCaption.slice(1)}; grows as the cohort matures. A ≥ value is the vouched lower bound — pending money can only add to it.`}
          testId={FUNNEL_REPORTS_TEST_IDS.paidSummaryLtv}
        />
        <MiniMetric
          label="Net collected (realized)"
          value={netCollectedTile !== null ? `${tileIsBounded(netCollectedTile) ? '≥ ' : ''}${formatCurrency(netCollectedTile.value, summaryCurrency)}` : 'n/a'}
          boundary={tileBoundaryLine(netCollectedTile) !== null
            ? { text: tileBoundaryLine(netCollectedTile) as string, testId: FUNNEL_REPORTS_TEST_IDS.paidSummaryTileBoundary('net') }
            : null}
          cause={netCollectedTile === null
            ? { text: realizedTileCause('netCollected'), testId: FUNNEL_REPORTS_TEST_IDS.paidTileCause('net') }
            : null}
          title="Gross collected less reconciled refunds, disputes, credits, and discounts, summed over the rows whose net basis is vouched."
        />
        <MiniMetric
          label="Settlement cash (realized)"
          value={settlementTile !== null ? `${tileIsBounded(settlementTile) ? '≥ ' : ''}${formatCurrency(settlementTile.value, summaryCurrency)}` : 'n/a'}
          boundary={tileBoundaryLine(settlementTile) !== null
            ? { text: tileBoundaryLine(settlementTile) as string, testId: FUNNEL_REPORTS_TEST_IDS.paidSummaryTileBoundary('settlement') }
            : null}
          cause={settlementTile === null
            ? { text: realizedTileCause('settlementCash'), testId: FUNNEL_REPORTS_TEST_IDS.paidTileCause('settlement') }
            : null}
          title="Reconciled net collected after processor, store, dispute, and FX fees, summed over the rows whose settlement basis is vouched."
        />
        <MiniMetric
          label="Contribution before ads"
          value={contributionTile !== null ? `${tileIsBounded(contributionTile) ? '≥ ' : ''}${formatCurrency(contributionTile.value, summaryCurrency)}` : 'n/a'}
          boundary={tileBoundaryLine(contributionTile) !== null
            ? { text: tileBoundaryLine(contributionTile) as string, testId: FUNNEL_REPORTS_TEST_IDS.paidSummaryTileBoundary('contribution') }
            : null}
          cause={contributionTile === null
            ? { text: realizedTileCause('contribution'), testId: FUNNEL_REPORTS_TEST_IDS.paidTileCause('contribution') }
            : null}
          title="Reconciled contribution after refunds, disputes, fees, tax, discounts, credits, FX cost, and service COGS, before acquisition spend."
        />
        <MiniMetric
          label="Contribution after ads"
          value={contributionAfterAdsTile !== null ? `${tileIsBounded(contributionAfterAdsTile) ? '≥ ' : ''}${formatCurrency(contributionAfterAdsTile.value, summaryCurrency)}` : 'n/a'}
          boundary={tileBoundaryLine(contributionAfterAdsTile) !== null
            ? { text: tileBoundaryLine(contributionAfterAdsTile) as string, testId: FUNNEL_REPORTS_TEST_IDS.paidSummaryTileBoundary('contribution-after-ads') }
            : null}
          cause={contributionAfterAdsTile === null
            ? { text: realizedTileCause('contribution'), testId: FUNNEL_REPORTS_TEST_IDS.paidTileCause('contribution-after-ads') }
            : null}
          title="Contribution before ads minus matched provider spend in the same reconciled currency."
        />
        <MiniMetric
          label="ROAS (LTV)"
          value={ltvRoas !== null ? `${tileIsBounded(grossTile) ? '≥ ' : ''}${ltvRoas.toFixed(2)}×${ltvRoas >= 1 ? ' ✓' : ''}` : 'n/a'}
          boundary={tileBoundaryLine(grossTile) !== null
            ? { text: tileBoundaryLine(grossTile) as string, testId: FUNNEL_REPORTS_TEST_IDS.paidSummaryTileBoundary('ltv-roas') }
            : null}
          cause={ltvRoas === null
            ? { text: realizedTileCause('gross'), testId: FUNNEL_REPORTS_TEST_IDS.paidTileCause('ltv-roas') }
            : null}
          title="LTV (realized) ÷ ad spend (mapped). ≥1.00× means the mapped campaigns have already paid back on realized revenue. A ≥ ratio is a lower bound: pending or blocked rows keep their spend in the denominator and add no revenue."
          testId={FUNNEL_REPORTS_TEST_IDS.paidSummaryLtvRoas}
        />
        <MiniMetric
          label={(
            <Stack direction="row" spacing={0.5} alignItems="center" component="span">
              <span>{hasIndependentProjectedBases ? 'Projected contribution LTV₁₂ ROAS' : 'Projected gross LTV₁₂ ROAS'}</span>
              {projectId && (
                <IconButton
                  size="small"
                  aria-label="Verdict thresholds"
                  data-testid={isFullscreen ? undefined : FUNNEL_REPORTS_TEST_IDS.paidThresholdsOpen}
                  onClick={event => setThresholdsAnchor(event.currentTarget)}
                  sx={{ p: 0.25 }}
                >
                  <SettingsOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>
          )}
          value={primaryProjectedPortfolio !== null
            ? `${primaryProjectedPortfolio.bounded ? '≥ ' : ''}${primaryProjectedPortfolio.pointRoas.toFixed(2)}×`
            : 'n/a'}
          sub={primaryProjectedPortfolio
            ? `P10–P90 ${primaryProjectedPortfolio.p10Roas.toFixed(2)}–${primaryProjectedPortfolio.p90Roas.toFixed(2)} · sBG · ${hasIndependentProjectedBases ? 'contribution · reconciled' : projectedBasisCaption}`
            : hasIndependentProjectedBases && contributionUnavailableReason
              ? contributionUnavailableReason.replaceAll('_', ' ')
              : undefined}
          boundary={primaryProjectedPortfolio?.bounded
            ? {
                text: [
                  primaryProjectedPortfolio.settledThrough ? `boundary ${primaryProjectedPortfolio.settledThrough}` : null,
                  primaryProjectedPortfolio.pendingCycles > 0 ? `${primaryProjectedPortfolio.pendingCycles} pending` : null,
                  primaryProjectedPortfolio.blockedRows > 0 ? `${primaryProjectedPortfolio.blockedRows} ${primaryProjectedPortfolio.blockedRows === 1 ? 'row' : 'rows'} blocked` : null,
                ].filter(Boolean).join(' · '),
                testId: FUNNEL_REPORTS_TEST_IDS.paidSummaryTileBoundary('projected'),
              }
            : null}
          cause={primaryProjectedPortfolio === null && hasIndependentProjectedBases
            ? {
                text: contributionUnavailableReason
                  ? (humanizeCreativeReasonCode(contributionUnavailableReason) ?? contributionUnavailableReason.replaceAll('_', ' '))
                  : 'no campaign passes the contribution projection gate in this window',
                testId: FUNNEL_REPORTS_TEST_IDS.paidTileCause('projected'),
              }
            : null}
          title={hasIndependentProjectedBases
            ? 'Primary decision series: projected 12-month contribution after refunds, disputes, credits, discounts, fees, tax, and service COGS, divided by matched spend. Every included campaign must pass the independent contribution reconciliation and currency gates.'
            : 'Legacy projected 12-month gross-collected LTV of the matched cohorts ÷ matched spend. It does not deduct refunds, disputes, fees, tax, credits or service COGS.'}
          testId={FUNNEL_REPORTS_TEST_IDS.paidSummaryPltvRoas}
        />
        {hasIndependentProjectedBases && (
          <MiniMetric
            label="Projected basis comparison"
            value={netCollectedPortfolio ? `Net ${netCollectedPortfolio.pointRoas.toFixed(2)}×` : 'Net n/a'}
            sub={`${settlementPortfolio ? `Settlement ${settlementPortfolio.pointRoas.toFixed(2)}×` : 'Settlement n/a'} · ${grossPortfolio ? `Gross audit ${grossPortfolio.pointRoas.toFixed(2)}×` : 'Gross audit n/a'}`}
            title="Independent pLTV:CAC series. Net collected includes refund/dispute/credit/discount movements; settlement also includes payment/store/dispute/FX fees; gross remains audit-only."
          />
        )}
        <MiniMetric
          label={coverageDataUsable ? 'Purchases (covered)' : 'Purchases (mapped)'}
          value={coveredKnown && !anyCoveredDays ? 'n/a' : formatInteger(coverageDataUsable ? mappedTotals.purchasesCovered : mappedTotals.purchasesFull)}
        />
        {/* Full-window revenue is a partial-coverage explainer — at full
            coverage it duplicates «Ad revenue (covered)» and hides. */}
        {partialCoverage && (
          <MiniMetric
            label="Revenue, full window"
            value={revenueFullKnown ? formatCurrency(mappedTotals.revenueFull, summaryCurrency) : 'n/a'}
            sub={`${formatInteger(mappedTotals.purchasesFull)} purchases · not used for ROAS`}
            dimmed
            testId={FUNNEL_REPORTS_TEST_IDS.paidSummaryRevenueFullWindow}
          />
        )}
        <MiniMetric
          label="Unmatched spend"
          value={unmatchedMoneyUsable ? formatCurrency(totals.unmatchedSpend, unmatchedCurrency) : 'n/a'}
          sub={unmatchedMoneyUsable ? undefined : 'mixed/missing currency evidence'}
          dimmed
        />
      </Grid>
      <Typography variant="caption" color="text.secondary">
        {hasIndependentProjectedBases
          ? 'Contribution pLTV is the primary decision basis. Net collected and settlement cash are supporting series; gross is audit-only. Every basis is modeled independently, and an unavailable contribution value never falls back to gross.'
          : projectedBasisSupported
            ? 'Legacy gross pLTV signal only — KILL/SCALE chips do not establish net-collected or contribution profitability.'
            : 'Projected values are hidden because the payload basis does not match the current gross pLTV model.'}
      </Typography>
      {projectId && (
        <VerdictThresholdsPopover
          anchorEl={thresholdsAnchor}
          onClose={() => setThresholdsAnchor(null)}
          projectId={projectId}
          thresholds={thresholds}
          onSaved={saved => {
            onThresholdsSaved(saved);
            setThresholdsAnchor(null);
          }}
        />
      )}
      {!coveredKnown ? (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={FUNNEL_REPORTS_TEST_IDS.paidSummaryCoverageCaption}
        >
          ⓘ Cached report from an older version — press Refresh to compute covered revenue and ROAS.
        </Typography>
      ) : !anyCoveredDays ? (
        <Typography
          variant="caption"
          color="warning.main"
          data-testid={FUNNEL_REPORTS_TEST_IDS.paidSummaryCoverageCaption}
        >
          ⚠ No synced ad-spend days inside the report window — covered revenue and ROAS are not
          computable. Run a spend sync covering this window.
        </Typography>
      ) : coverage && !partialCoverage ? (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={FUNNEL_REPORTS_TEST_IDS.paidSummaryCoverageCaption}
        >
          {`✓ Ad spend covers all ${coverage.windowDays} report days (${formatCoverageDate(coverage.from)}–${formatCoverageDate(coverage.to)}).`}
        </Typography>
      ) : null}
    </Stack>
  );
}

/** DD.MM over an ISO date — compact caption format, mirrors the report-window chips. */
function formatCoverageDate(isoDate: string): string {
  const [, month, day] = isoDate.split('-');
  return month && day ? `${day}.${month}` : isoDate;
}

function MiniMetric({ label, value, sub, dimmed, title, testId, boundary, cause }: {
  label: React.ReactNode;
  value: string;
  /** Small second line under the value (e.g. «600 purchases · not used for ROAS»). */
  sub?: string;
  /** Supporting metric — muted value so the headline money tiles stand out. */
  dimmed?: boolean;
  /** Native tooltip for honesty caveats (e.g. the ROAS window mismatch). */
  title?: string;
  testId?: string;
  /** W-A boundary line (`boundary <date> · N pending`) under a `≥`-prefixed value. */
  boundary?: { text: string; testId: string } | null;
  /** Rule 7: withheld tiles name their blocking mechanism — never a bare n/a. */
  cause?: { text: string; testId: string } | null;
}): React.ReactElement {
  return (
    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
      <Box
        title={title}
        data-testid={testId}
        sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1, minHeight: 64 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" color={dimmed ? 'text.secondary' : undefined} sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
            {sub}
          </Typography>
        )}
        {boundary && (
          <Typography data-testid={boundary.testId} variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {boundary.text}
          </Typography>
        )}
        {cause && (
          <Typography data-testid={cause.testId} variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {cause.text}
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

/**
 * R3-4 (D-Q5): the «What changed» insight-card feed — one merged reading layer
 * for anomalies + their root-cause drivers. Severity-ranked narrative cards;
 * drivers render inside each card's detail drawer; the audit tables below stay
 * as evidence. «All healthy» is an explicit statement, not silence.
 */
/** Exported for the ui-components Storybook (R5-2) — not part of the module API. */
export function WhatChangedFeed({
  anomalies,
  drivers,
  currency,
}: {
  anomalies: FunnelReportsDashboardData['anomalies'];
  drivers: FunnelReportsDashboardData['rootCauseDrivers'];
  currency?: string;
}): React.ReactElement {
  if (anomalies.length === 0) {
    return (
      <Alert severity="success" data-testid={FUNNEL_REPORTS_TEST_IDS.whatChangedAllHealthy}>
        All healthy — no statistically unusual metric movement in this window.
      </Alert>
    );
  }
  const severityColor: Record<string, 'error' | 'warning' | 'default'> = { critical: 'error', warning: 'warning' };
  return (
    <Stack spacing={1} data-testid={FUNNEL_REPORTS_TEST_IDS.whatChangedFeed}>
      {anomalies.map(anomaly => {
        const anomalyDrivers = drivers
          .filter(driver => driver.anomalyId === anomaly.anomalyId)
          .slice(0, 3);
        const direction = (anomaly.relativeDelta ?? 0) < 0 ? 'dropped' : 'rose';
        return (
          <Box
            key={anomaly.anomalyId}
            data-testid={FUNNEL_REPORTS_TEST_IDS.whatChangedItem(anomaly.anomalyId)}
            sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1.25 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" color={severityColor[anomaly.severity] ?? 'default'} label={anomaly.severity} />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 220 }}>
                <b>{formatMetricLabel(anomaly.metricName)}</b> {direction}{' '}
                {anomaly.relativeDelta === null ? '' : `${formatRate(Math.abs(anomaly.relativeDelta))} `}
                vs expected on {anomaly.bucketTs.slice(0, 10)}
                {anomaly.estimatedRevenueDelta != null
                  ? ` — est. ${formatCurrency(anomaly.estimatedRevenueDelta, currency)}`
                  : ''}
                {anomaly.remediationHint ? `. ${anomaly.remediationHint}` : ''}
              </Typography>
            </Stack>
            {anomalyDrivers.length > 0 && (
              <Box component="details" sx={{ mt: 0.75 }} data-testid={FUNNEL_REPORTS_TEST_IDS.whatChangedDrivers(anomaly.anomalyId)}>
                <Box component="summary" sx={{ cursor: 'pointer', typography: 'caption', color: 'text.secondary' }}>
                  Top drivers ({anomalyDrivers.length}) — full audit in Root Cause below
                </Box>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {anomalyDrivers.map(driver => (
                    <Typography key={driver.contributionId} variant="caption" color="text.secondary">
                      {readableDriverLabel(driver)} · impact {formatNumber(driver.impactScore)}
                      {driver.estimatedRevenueDelta != null ? ` · ${formatCurrency(driver.estimatedRevenueDelta, currency)}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function RootCauseReadableSummary({
  drivers,
  currency = 'usd',
}: {
  drivers: FunnelReportsDashboardData['rootCauseDrivers'];
  currency?: string;
}): React.ReactElement | null {
  const topDriver = drivers[0];
  if (!topDriver) return null;

  const topDriverKey = driverLabel(topDriver);
  const supportingDrivers = Array.from(new Map(
    drivers
      .slice(1)
      .filter(driver => driverLabel(driver) !== topDriverKey)
      .map(driver => [driverLabel(driver), readableDriverLabel(driver)]),
  ).values()).slice(0, 3).join('; ');

  return (
    <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Stack spacing={0.75}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Top investigation clue: {readableDriverLabel(topDriver)}
        </Typography>
        <Typography variant="body2">
          {driverNarrative(topDriver, currency)}
        </Typography>
        {supportingDrivers && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Related dimensions in the same anomaly scope: {supportingDrivers}.
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}

function RootCauseMetricGuide(): React.ReactElement {
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Rank</Box>
        {' '}is the deterministic investigation order from the rule-based contribution view. Allowlisted variables and concrete product, paywall, screen, route, and traffic dimensions are prioritized; it is not causal probability.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Metric delta</Box>
        {' '}is `(actual - expected) / expected` for the anomalous metric. Negative means the metric is below baseline; positive means it is above baseline.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Impact score</Box>
        {' '}is the absolute movement used to sort driver rows. The warehouse prefers estimated revenue delta when available, then estimated conversion delta, then relative metric delta.
      </Typography>
    </Stack>
  );
}

function DriverEvidenceCell({
  row,
}: {
  row: FunnelReportsDashboardData['rootCauseDrivers'][number];
}): React.ReactElement {
  return (
    <EvidenceStack
      entries={[
        {
          label: humanizeDimensionName(row.dimensionName),
          // D7: the raw warehouse key is demoted to a hover tooltip — the
          // readable dimension/value pair carries the row.
          value: (
            <MuiTooltip title={`Raw key: ${driverLabel(row)}`} placement="top-start">
              <Box component="span" sx={{ display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.dimensionValue}
              </Box>
            </MuiTooltip>
          ),
        },
      ]}
    />
  );
}

function AnomalyReadableSummary({
  anomalies,
  currency = 'usd',
}: {
  anomalies: FunnelReportsDashboardData['anomalies'];
  currency?: string;
}): React.ReactElement | null {
  const topAnomaly = anomalies[0];
  if (!topAnomaly) return null;

  return (
    <Alert severity={topAnomaly.severity === 'critical' ? 'warning' : 'info'} icon={false}>
      <Typography variant="body2">
        {anomalyNarrative(topAnomaly, currency)}
      </Typography>
    </Alert>
  );
}

export function EvidenceStack({
  entries,
}: {
  /** `emphasis` renders the value full-contrast/bold — the primary-gates-vs-supporting pattern. */
  entries: Array<{ label: string; value: React.ReactNode; emphasis?: boolean }>;
}): React.ReactElement {
  return (
    <Stack spacing={0.45} sx={{ minWidth: 0 }}>
      {entries.map(entry => (
        <Box key={entry.label} sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
            {entry.label}
          </Typography>
          <Box sx={{ minWidth: 0, fontSize: entry.emphasis ? '0.85rem' : '0.75rem', lineHeight: 1.2, fontWeight: entry.emphasis ? 600 : undefined }}>
            {renderEvidenceInline(entry.value)}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

export function DenseTable({
  columns,
  rows,
  sortState,
  onSort,
  fit = false,
}: {
  columns: EvidenceColumn[];
  rows: EvidenceRow[];
  sortState?: SortState;
  onSort?: (columnId: string) => void;
  /**
   * The approved artifact sizes this table to its container
   * (`table-layout: auto; width: 100%`, cells `min-width: 0`), so the columns
   * share the room they have instead of adding up to a fixed 1 098 px that an
   * 1 094 px card then has to scroll sideways. Fixed layout stays the default
   * for the tables that were designed around it.
   */
  fit?: boolean;
}): React.ReactElement {
  // S5 grouped tables: members sort INSIDE their group; group headers stay
  // put; groups keep the order the rows arrived in (the server's traffic order).
  const sortedRows = React.useMemo(() => {
    if (!sortState) return rows;
    if (!rows.some(row => row.group !== undefined)) return sortEvidenceRows(rows, sortState);
    const order: string[] = [];
    const headers = new Map<string, EvidenceRow>();
    const members = new Map<string, EvidenceRow[]>();
    for (const row of rows) {
      const group = row.group ?? '';
      if (!members.has(group)) { order.push(group); members.set(group, []); }
      if (row.groupHeader) headers.set(group, row);
      else members.get(group)?.push(row);
    }
    return order.flatMap(group => [
      ...(headers.has(group) ? [headers.get(group) as EvidenceRow] : []),
      ...sortEvidenceRows(members.get(group) ?? [], sortState),
    ]);
  }, [rows, sortState]);
  const minTableWidth = columns.reduce((sum, column) => sum + (column.minWidth ?? 108), 0);
  /**
   * D8: body 0.875 rem, padding 10×12, `vertical-align: middle`. The old
   * `verticalAlign: 'top'` left every single-line value floating at the top of
   * a two-line row, so a row of numbers did not read as a row.
   */
  const cellSx = {
    fontSize: '0.875rem',
    // 1.35, not the 1.25 this started at: the impeccable detector's
    // `tight-leading` rule wants >= 1.3 on functional text, and a table cell is
    // functional text 101 times over on a filled report. The extra ~1.4 px per
    // line is invisible next to the 10 px vertical padding.
    lineHeight: 1.35,
    px: '12px',
    py: '10px',
    verticalAlign: 'middle',
    whiteSpace: 'normal',
  } as const;
  /**
   * D8: labels are SMALLER than the body and 500, not bold. The spec asked for
   * 0.8125 rem; that step is not on the DESIGN.md type ramp
   * (2.125 / 1.5 / 1.25 / 1 / 0.875 / 0.75), so the header uses the documented
   * caption step and keeps the header-under-body hierarchy the design wanted.
   */
  const headCellSx = {
    ...cellSx,
    fontSize: '0.75rem',
    fontWeight: 500,
  } as const;

  // Metric guide: hover the dotted header label to see what the metric means.
  const headerLabel = (column: EvidenceColumn): React.ReactNode => {
    const text = (
      <Box
        component="span"
        sx={column.description
          ? { cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }
          : undefined}
      >
        {column.label}
      </Box>
    );
    return column.description ? <MuiTooltip title={column.description}>{text}</MuiTooltip> : text;
  };

  return (
    <Table size="small" sx={fit ? { tableLayout: 'auto', width: '100%' } : { tableLayout: 'fixed', minWidth: minTableWidth }}>
      <TableHead>
        {/* The head row relays the theme's `MuiTableHead` background to the
            cells (see the cell's `bgcolor: 'inherit'`). */}
        <TableRow sx={{ bgcolor: 'inherit' }}>
          {columns.map((column, columnIndex) => (
            <TableCell
              key={column.id}
              // D8: metric labels are CENTRED; only the identity column is left.
              align={column.align ?? (columnIndex === 0 ? 'left' : 'center')}
              sx={{
                ...headCellSx,
                position: 'sticky',
                top: 0,
                zIndex: columnIndex === 0 ? 4 : 3,
                // D8: the head colour is the THEME's own `MuiTableHead`
                // (ThemeModeContext.jsx — `#3D375B` dark / `grey.100` light),
                // never a local value. The old `action.selected` override is
                // what hid it.
                //
                // `inherit` carries that theme value down to the CELL, which a
                // sticky head needs to be opaque: the theme paints the `thead`,
                // and a transparent `th` would let rows scroll through the
                // header the moment F1 (`MuiTable { overflow: hidden }`, owner
                // decision 8.4) stops killing sticky. Inheriting keeps ONE
                // source for the colour instead of copying the hex here.
                bgcolor: 'inherit',
                ...(fit
                  ? { minWidth: 0, width: 'auto' }
                  : { minWidth: column.minWidth ?? 108, width: column.minWidth ?? 108 }),
                ...(columnIndex === 0 ? { left: 0 } : {}),
                ...(column.dimmed ? { color: 'text.secondary' } : {}),
              }}
            >
              {column.sortable && onSort ? (
                <TableSortLabel
                  active={sortState?.columnId === column.id}
                  direction={sortState?.columnId === column.id ? sortState.direction : 'asc'}
                  onClick={() => onSort(column.id)}
                  sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
                >
                  {headerLabel(column)}
                </TableSortLabel>
              ) : headerLabel(column)}
              {column.unitNote && (
                // F3: `text.disabled` fails contrast on text the reader must
                // read; and 0.68 rem is below the 0.75 rem caption FLOOR.
                <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: '0.75rem', color: 'text.secondary' }}>
                  {column.unitNote}
                </Box>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map(row => (
          row.groupHeader ? (
            // D7: no tint block behind a placement caption — a hairline ABOVE
            // it, and no rule under it (that would repeat its own line).
            <TableRow key={row.key}>
              <TableCell
                colSpan={columns.length}
                sx={{
                  ...cellSx,
                  position: 'sticky',
                  left: 0,
                  borderBottom: 0,
                  borderTop: theme => `1px solid ${theme.palette.divider}`,
                  pt: '14px',
                  pb: '4px',
                }}
              >
                {row.cells[0]}
              </TableCell>
            </TableRow>
          ) : (
          <TableRow
            key={row.key}
            data-highlighted={row.selected ? 'true' : undefined}
            sx={row.selected ? { bgcolor: 'action.selected' } : undefined}
          >
            {row.cells.map((cell, cellIndex) => (
              <TableCell
                key={cellIndex}
                align={columns[cellIndex]?.align ?? (cellIndex === 0 ? 'left' : 'center')}
                sx={{
                  ...cellSx,
                  ...(fit
                    ? { minWidth: 0, width: 'auto' }
                    : { minWidth: columns[cellIndex]?.minWidth ?? 108, width: columns[cellIndex]?.minWidth ?? 108 }),
                  ...(cellIndex === 0
                    ? {
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        // A sticky cell has to be opaque, but a flat
                        // `background.paper` would carve the identity column
                        // out of a highlighted row (measured `#ffffff` against
                        // the row's own tint). The row tint is translucent, so
                        // the cell paints the SAME tint as a gradient layer ON
                        // TOP of the paper: one source for the colour, an
                        // opaque result, and the row reads as one row.
                        bgcolor: 'background.paper',
                        ...(row.selected
                          ? {
                            backgroundImage: (theme: Theme) => `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
                            borderLeft: 3,
                            borderLeftColor: 'primary.main',
                          }
                          : {}),
                      }
                    : {}),
                  ...(columns[cellIndex]?.dimmed ? { color: 'text.secondary', opacity: 0.85 } : {}),
                }}
              >
                {renderEvidenceCell(cell)}
              </TableCell>
            ))}
          </TableRow>
          )
        ))}
      </TableBody>
    </Table>
  );
}

function EvidenceTable({
  id,
  columns,
  rows,
  minWidths,
}: {
  id: string;
  columns: string[];
  rows: React.ReactNode[][];
  minWidths?: number[];
}): React.ReactElement {
  const normalizedColumns = columns.map((label, index) => ({ id: `col-${index}`, label, minWidth: minWidths?.[index] }));
  const normalizedRows = rows.map((cells, index) => ({ key: index, cells }));

  return (
    // position:relative — absolutely-positioned visually-hidden spans (MUI
    // sort labels / OverflowText a11y text) must clip INSIDE this scroll
    // container; without a positioned ancestor they escape overflow:auto and
    // widen the PAGE (live-traced 47px page scroll at 1280).
    <Box data-testid={FUNNEL_REPORTS_TEST_IDS.chartEvidence(id)} sx={{ maxHeight: REPORT_TABLE_MAX_HEIGHT, overflow: 'auto', position: 'relative' }}>
      <DenseTable columns={normalizedColumns} rows={normalizedRows} />
    </Box>
  );
}

export function SortableEvidenceTable({
  id,
  columns,
  rows,
  sortState,
  onSort,
  maxHeight,
  unclamped = false,
}: {
  id: string;
  columns: EvidenceColumn[];
  rows: EvidenceRow[];
  sortState: SortState;
  onSort: (columnId: string) => void;
  /** Override the default table viewport (e.g. the paid table gets more room). */
  maxHeight?: number | string;
  /**
   * D4: no inner scroll pane at all — the table sizes to its container and the
   * section's own card scrolls. For a table whose whole job is to list every
   * row (Traffic by version), a 320 px viewport hid most of the answer behind a
   * nested scrollbar that the reader had to find first.
   */
  unclamped?: boolean;
}): React.ReactElement {
  return (
    // position:relative — same page-widening clip fix as EvidenceTable above.
    <Box
      data-testid={FUNNEL_REPORTS_TEST_IDS.chartEvidence(id)}
      sx={unclamped
        // `unclamped` drops the VERTICAL clamp (D4: the section shows all its
        // rows, page length is controlled by collapse) — it must NOT drop the
        // horizontal scroll with it. Without `overflowX`, a 1 098 px table in
        // an 878 px column was clipped by the section body's
        // `overflowX: hidden` with no scrollbar and no wheel: 2 of 8 columns
        // gone at 1280 px, 5 of 8 at 900 px, and the «Details» drill link
        // unreachable by pointer at both. There is no vertical scrollbar to
        // re-introduce here, because with no max-height the box always grows
        // to its content.
        ? { position: 'relative', overflowX: 'auto' }
        : { maxHeight: maxHeight ?? REPORT_TABLE_MAX_HEIGHT, overflow: 'auto', position: 'relative' }}
    >
      <DenseTable columns={columns} rows={rows} sortState={sortState} onSort={onSort} fit={unclamped} />
    </Box>
  );
}

function renderEvidenceCell(cell: React.ReactNode): React.ReactNode {
  if (React.isValidElement(cell)) return cell;
  if (cell === null || cell === undefined) return <OverflowText text="n/a" />;
  if (typeof cell === 'string' || typeof cell === 'number') {
    return <OverflowText text={String(cell)} />;
  }
  return cell;
}

function renderEvidenceInline(cell: React.ReactNode): React.ReactNode {
  if (React.isValidElement(cell)) return cell;
  if (cell === null || cell === undefined) return <OverflowText text="n/a" />;
  if (typeof cell === 'string' || typeof cell === 'number') {
    return <OverflowText text={String(cell)} />;
  }
  return cell;
}

function OverflowText({ text }: { text: string }): React.ReactElement {
  const shouldTruncate = text.length > 28;
  const visible = shouldTruncate ? truncateMiddle(text, 34) : text;
  const unavailable = isUnavailableDisplayText(text);

  return (
    <MuiTooltip title={shouldTruncate ? text : ''} disableHoverListener={!shouldTruncate}>
      <Box
        component="span"
        sx={{
          // F3 (spec §F): `text.disabled` is 2.68:1 in light, and «n/a» is live
          // text a reader must read — it is the difference between "zero" and
          // "we do not know". `text.secondary` is the quiet-but-legible token
          // (7.5:1). The sibling fix in `RateEvidenceCell` landed in W6; this
          // half of F3 was missed.
          color: unavailable ? 'text.secondary' : undefined,
          display: 'block',
          fontWeight: unavailable ? 400 : undefined,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {visible}
        {shouldTruncate && (
          <Box component="span" sx={visuallyHiddenSx}>
            {text}
          </Box>
        )}
      </Box>
    </MuiTooltip>
  );
}

/** Long narrative/action text wraps instead of truncating (1.4). */
function WrapText({ text }: { text: string }): React.ReactElement {
  const unavailable = isUnavailableDisplayText(text);
  return (
    <Box
      component="span"
      sx={{
        color: unavailable ? 'text.disabled' : undefined,
        display: 'block',
        minWidth: 0,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  );
}

function sortEvidenceRows(rows: EvidenceRow[], sortState: SortState): EvidenceRow[] {
  return [...rows].sort((left, right) => {
    const leftValue = left.sortValues?.[sortState.columnId];
    const rightValue = right.sortValues?.[sortState.columnId];
    const leftMissing = isMissingSortValue(leftValue);
    const rightMissing = isMissingSortValue(rightValue);

    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;

    const result = compareSortValues(leftValue, rightValue);
    return sortState.direction === 'asc' ? result : -result;
  });
}

function compareSortValues(left: string | number | null | undefined, right: string | number | null | undefined): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
}

function isMissingSortValue(value: string | number | null | undefined): boolean {
  return value === null || value === undefined || value === '';
}

function isUnavailableDisplayText(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  return normalized === 'n/a' || normalized === 'not available' || normalized === 'no denominator';
}

export function nextSortState(current: SortState, columnId: string): SortState {
  if (current.columnId !== columnId) {
    return { columnId, direction: 'desc' };
  }
  return { columnId, direction: current.direction === 'desc' ? 'asc' : 'desc' };
}

export function sortableRate(rate: number | null, numerator: number, denominator: number): number | null {
  if (denominator <= 0 || numerator > denominator) return null;
  return rate;
}

/**
 * D8 fix: the lead sentence describes the FUNNEL state (healthy funnels never
 * open with «Warning:»); data-quality severity is appended as its own clause
 * whenever it is non-ok, so genuinely-critical states stay loud (§10 note 6).
 */
function funnelHealthReason(row: {
  startedJourneys: number;
  paywallReachedJourneys: number;
  buyButtonClicks: number;
  offerConvertedJourneys: number;
  purchaseJourneys: number;
  dataQualitySeverity?: string;
  severity?: string;
}): string {
  const dataQuality = row.dataQualitySeverity ?? row.severity ?? 'unknown';
  const dataQualitySuffix = dataQuality === 'ok' || dataQuality === 'unknown'
    ? ''
    : ` — data quality: ${dataQuality}`;
  if (row.startedJourneys <= 0) return 'No starts were recorded in this window.';
  if (row.paywallReachedJourneys <= 0) {
    return `Attention: no users reached paywall from ${formatInteger(row.startedJourneys)} starts.${dataQualitySuffix}`;
  }
  if (row.offerConvertedJourneys <= 0 && row.purchaseJourneys <= 0) {
    return `Attention: users reached paywall, but no offers or purchases were recorded.${dataQualitySuffix}`;
  }
  return `Healthy: traffic reaches the paywall and conversions are recorded.${dataQualitySuffix}`;
}

function buildABDecisionEvidenceRows(decisions: FunnelABTestDecisionRow[], currency = 'usd'): EvidenceRow[] {
  return decisions.flatMap(decision => decision.scores.map(score => {
    const comparison = score.comparisonToControl[decision.primaryMetric];
    const winnerStatus = decision.winnerVariantId === score.variantId
      ? 'winner'
      : decision.controlVariantId === score.variantId
        ? 'control'
        : 'candidate';
    const probability = comparison?.probabilityToBeatControl === null || comparison?.probabilityToBeatControl === undefined
      ? 'n/a'
      : formatRate(comparison.probabilityToBeatControl);

    return {
      key: `${decision.testId}-${decision.iterationId}-${score.variantId}`,
      cells: [
        <IdentityNameCell
          key="test"
          name={decision.testName ? `${decision.testName} · ${decision.iterationName ?? decision.iterationId}` : undefined}
          id={`${decision.testId} / ${decision.iterationId}`}
          warnings={decision.identityWarnings}
        />,
        <IdentityNameCell key="variant" name={score.variantName} id={score.variantId} />,
        <EvidenceStack
          key="status"
          entries={[
            { label: 'State', value: decisionStatusLabel(decision.status) },
            { label: 'Role', value: score.role || 'n/a' },
            { label: 'Result', value: winnerStatus },
            { label: 'Metric', value: abMetricLabel(decision.primaryMetric) },
          ]}
        />,
        <EvidenceStack
          key="traffic"
          entries={[
            { label: 'Assigned users', value: formatInteger(score.assignedUsers) },
            { label: 'Sessions', value: formatInteger(score.sessions) },
          ]}
        />,
        <EvidenceStack
          key="early"
          entries={[
            { label: 'First -> second', value: formatRate(score.firstToSecondRate) },
            { label: 'Purchase CR', value: formatRate(score.purchaseConversionRate) },
          ]}
        />,
        <EvidenceStack
          key="revenue"
          entries={[
            { label: 'Revenue / user', value: formatNullableCurrency(score.revenuePerAssignedUser, currency) },
            { label: 'Profit / user', value: formatNullableCurrency(score.profitPerAssignedUser, currency) },
          ]}
        />,
        <EvidenceStack
          key="paid"
          entries={[
            { label: 'Spend', value: formatNullableCurrency(score.adSpend, currency) },
            { label: 'CAC', value: formatNullableCurrency(score.costPerPurchase, currency) },
            { label: 'ROAS', value: score.roas === null || score.roas === undefined ? 'n/a' : formatNumber(score.roas) },
          ]}
        />,
        <EvidenceStack
          key="confidence"
          entries={[
            { label: 'Probability vs control', value: probability },
            { label: 'Expected loss', value: formatNullableCurrency(comparison?.expectedLossPerUser, currency) },
          ]}
        />,
        abDecisionFlagsLabel(decision, score.variantId),
      ],
      sortValues: {
        test: decision.testName ?? `${decision.testId} / ${decision.iterationId}`,
        variant: score.variantName ?? score.variantId,
        status: decision.status,
        traffic: score.assignedUsers,
        earlyFunnel: score.firstToSecondRate,
        revenue: score.profitPerAssignedUser ?? score.revenuePerAssignedUser,
        paidEconomics: score.adSpend ?? 0,
        confidence: comparison?.probabilityToBeatControl,
      },
    };
  }));
}

function abMetricLabel(metric: FunnelABTestDecisionRow['primaryMetric']): string {
  switch (metric) {
    case 'first_to_second_rate':
      return 'First -> second';
    case 'purchase_conversion_rate':
      return 'Purchase CR';
    case 'revenue_per_assigned_user':
      return 'Revenue / user';
    case 'profit_per_assigned_user':
      return 'Profit / user';
    default:
      return metric;
  }
}

function abDecisionFlagsLabel(decision: FunnelABTestDecisionRow, variantId: string): string {
  const score = decision.scores.find(item => item.variantId === variantId);
  const guardrails = score?.guardrails
    .filter(guardrail => guardrail.breached)
    .map(guardrail => guardrail.reason || `${guardrail.metric} breached`) ?? [];
  const flags = [...decision.dataQualityFlags, ...guardrails];
  return flags.length ? flags.join(', ') : decision.recommendation;
}

function pivotMetricRows(rows: FunnelReportsDashboardData['metricTimeseries']): Array<Record<string, number | string>> {
  const buckets = new Map<string, Record<string, number | string>>();
  for (const row of rows) {
    const bucket = row.bucketTs.slice(0, 16).replace('T', ' ');
    const item = buckets.get(bucket) ?? { bucket };
    item[row.metricName] = row.metricValue;
    buckets.set(bucket, item);
  }
  return [...buckets.values()];
}

function edgeRouteLabel(row: FunnelReportsDashboardData['screenFlowEdges'][number]): string {
  return `${row.fromScreenName || row.fromScreenId || 'Start'} -> ${row.toScreenName || row.toScreenId || 'Exit'}`;
}

function edgeLabel(row: FunnelReportsDashboardData['screenFlowEdges'][number]): string {
  return `${edgeRouteLabel(row)}${row.actionKey ? ` (${row.actionKey})` : ''}`;
}

function edgeTooltipLabel(
  _label: unknown,
  payload: ReadonlyArray<{ payload?: FunnelReportsDashboardData['screenFlowEdges'][number] }> | undefined,
): string {
  const row = payload?.[0]?.payload;
  return row ? edgeLabel(row) : '';
}

function driverLabel(row: FunnelReportsDashboardData['rootCauseDrivers'][number]): string {
  return `${row.dimensionName}=${row.dimensionValue}`;
}

function readableDriverLabel(row: FunnelReportsDashboardData['rootCauseDrivers'][number]): string {
  return `${humanizeDimensionName(row.dimensionName)} = ${row.dimensionValue}`;
}

const METRIC_LABELS: Record<string, string> = {
  purchase_amount: 'Purchase revenue',
  initial_billing_revenue: 'Initial billing revenue',
  buy_click_to_offer_conversion_rate: 'Buy click → offer rate',
  paywall_to_offer_conversion_rate: 'Paywall → offer rate',
  first_to_second_rate: 'First → second rate',
  first_screen_open_rate: 'First screen open rate',
  started_journeys: 'Started journeys',
  paywall_reached_journeys: 'Paywall reached',
  buy_button_clicks: 'Buy clicks',
  offer_converted_journeys: 'Offer conversions',
  trial_started_journeys: 'Trials started',
  purchase_journeys: 'Purchases',
};

/** Display-layer metric names (D7): dictionary first, Title Case fallback. */
function formatMetricLabel(name: string): string {
  return METRIC_LABELS[name] ?? humanizeToken(name);
}

/** Registry statuses in operator language (same wording as ReportAttentionStrip). */
function decisionStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    ready_to_choose: 'Ready to decide',
    winner_declared: 'Winner declared',
    running: 'Running',
    stopped: 'Stopped',
    archived: 'Archived',
  };
  return labels[status] ?? humanizeToken(status);
}

function humanizeDimensionName(name: string): string {
  if (name.startsWith('variable.')) {
    return `Variable ${humanizeToken(name.slice('variable.'.length))}`;
  }

  const labels: Record<string, string> = {
    paywall_type: 'Paywall type',
    product_id: 'Product',
    price_id: 'Price',
    offer_type: 'Offer type',
    screen_id: 'Screen',
    canvas_transition_id: 'Route transition',
    ab_test_id: 'A/B test',
    ab_variant_id: 'A/B variant',
    channel: 'Traffic channel',
    client_geo_country: 'Country',
    viewport_bucket: 'Viewport',
  };

  return labels[name] ?? humanizeToken(name);
}

function humanizeToken(value: string): string {
  return value
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, char => char.toUpperCase());
}

function driverNarrative(row: FunnelReportsDashboardData['rootCauseDrivers'][number], currency = 'usd'): string {
  const delta = formatRate(row.relativeDelta);
  const revenue = formatCurrency(row.estimatedRevenueDelta, currency);
  const hint = driverInvestigationHint(row);
  return `This anomaly is scoped to ${readableDriverLabel(row)}. Treat it as the segment or surface where the metric changed, not as causal proof. Metric delta is ${delta}, estimated revenue delta is ${revenue}. ${hint}`;
}

function driverInvestigationHint(row: FunnelReportsDashboardData['rootCauseDrivers'][number]): string {
  if (row.dimensionName.startsWith('variable.')) {
    return `Compare ${humanizeDimensionName(row.dimensionName)} against sibling values before changing product behavior.`;
  }
  if (['paywall_type', 'product_id', 'price_id', 'offer_type'].includes(row.dimensionName)) {
    return 'Inspect paywall, product, price, offer, and billing configuration for this scope.';
  }
  if (['screen_id', 'canvas_transition_id'].includes(row.dimensionName)) {
    return 'Open the flow drill-down and inspect this screen or route transition first.';
  }
  if (['channel', 'client_geo_country', 'viewport_bucket'].includes(row.dimensionName)) {
    return 'Compare traffic mix and environment against the baseline window.';
  }
  if (['ab_test_id', 'ab_variant_id'].includes(row.dimensionName)) {
    return 'Inspect the active experiment variant allocation and recent iteration changes.';
  }
  return row.remediationHint || 'Use this as the first slice to compare against baseline and neighboring segments.';
}

function formatDriverImpactScore(row: FunnelReportsDashboardData['rootCauseDrivers'][number], currency = 'usd'): string {
  const impactScore = Number(row.impactScore);
  if (Number.isNaN(impactScore)) return 'n/a';

  const revenueDelta = Number(row.estimatedRevenueDelta);
  if (!Number.isNaN(revenueDelta) && revenueDelta !== 0 && Math.abs(Math.abs(revenueDelta) - impactScore) < 0.01) {
    return formatCurrency(impactScore, currency);
  }

  return formatNumber(impactScore);
}

function formatDriverSummary(value: string): string {
  if (isUnavailableDisplayText(value)) return value;

  return value
    .split('|')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const separatorIndex = part.indexOf('=');
      if (separatorIndex === -1) return part;
      const name = part.slice(0, separatorIndex).trim();
      const dimensionValue = part.slice(separatorIndex + 1).trim();
      return `${humanizeDimensionName(name)} = ${dimensionValue}`;
    })
    .join('; ');
}

function anomalyNarrative(anomaly: FunnelReportsDashboardData['anomalies'][number], currency = 'usd'): string {
  const driver = anomaly.topDriverSummary ? ` Affected segment / driver: ${formatDriverSummary(anomaly.topDriverSummary)}.` : '';
  return `${humanizeToken(anomaly.metricName)} moved ${formatRate(anomaly.relativeDelta)} versus expected baseline: actual ${formatMetricValue(anomaly.metricName, anomaly.actualValue, currency)}, expected ${formatMetricValue(anomaly.metricName, anomaly.expectedValue, currency)}, estimated revenue delta ${formatCurrency(anomaly.estimatedRevenueDelta, currency)}.${driver} This is an investigation clue, not causal proof.`;
}

function artifactPieLabel(row: { artifactKey?: string; percent?: number }): string {
  const percent = typeof row.percent === 'number' ? ` ${(row.percent * 100).toFixed(0)}%` : '';
  return `${truncate(row.artifactKey || 'artifact')}${percent}`;
}

function paidAcquisitionChartRows(rows: FunnelReportsDashboardData['campaignEconomics']): Array<Record<string, number | string>> {
  return rows.map(row => ({
    campaign: truncate(row.campaign || '(not set)', 24),
    spend: row.spend ?? 0,
    unmatchedSpend: row.unmatchedSpend ?? 0,
    offerConvertedJourneys: row.offerConvertedJourneys,
  }));
}

function sourceMediumLabel(source?: string, medium?: string): string {
  if (source && medium) return `${source} / ${medium}`;
  return source || medium || 'n/a';
}

/** Humanized match-tier labels; raw values remain the sort keys and filter inputs. */
export const MATCH_QUALITY_LABELS: Record<string, string> = {
  exact_ad_id: 'Exact ad ID',
  exact_adset_id: 'Exact ad set ID',
  exact_campaign_id: 'Exact campaign ID',
  utm_object_id: 'UTM object ID',
  utm_mapped: 'Project UTM mapping',
  utm_tuple: 'UTM tuple',
  destination_url: 'Destination URL',
  name_fallback: 'Name fallback',
  none: 'None',
};

export function matchQualityLabel(quality?: string): string {
  const raw = quality || 'none';
  return MATCH_QUALITY_LABELS[raw] ?? raw;
}

function metaIdsLabel(row: FunnelReportsDashboardData['campaignEconomics'][number]): string {
  const parts = [
    row.metaAdAccountId ? `account ${row.metaAdAccountId}` : '',
    row.metaCampaignId ? `campaign ${row.metaCampaignId}` : '',
    row.metaAdsetId ? `ad set ${row.metaAdsetId}` : '',
    row.metaAdId ? `ad ${row.metaAdId}` : '',
    row.metaCreativeId ? `creative ${row.metaCreativeId}` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' / ') : 'n/a';
}

// W-R6: the three creative-evidence helpers accept the FLAT diagnostics row
// (CD saved view) alongside campaign-economics rows — structural fields only.
type CreativeEvidenceLikeRow = Pick<FunnelReportsDashboardData['campaignEconomics'][number],
  | 'metaCreativeName' | 'metaCreativeId' | 'metaCreativeCta' | 'metaCreativeTitle' | 'metaCreativeBody'
  | 'metaDestinationUrl' | 'metaNormalizedDestinationUrl' | 'metaUrlTags'
  | 'impressions' | 'clicks' | 'outboundClicks'>;

export function creativeLabel(row: CreativeEvidenceLikeRow): string {
  const parts = [
    row.metaCreativeName || row.metaCreativeId || '',
    row.metaCreativeCta ? `CTA ${row.metaCreativeCta}` : '',
    row.metaCreativeTitle || row.metaCreativeBody || '',
  ].filter(Boolean);
  return parts.length ? parts.join(' / ') : 'n/a';
}

export function destinationEvidenceLabel(row: CreativeEvidenceLikeRow): string {
  const destination = row.metaDestinationUrl || row.metaNormalizedDestinationUrl;
  const urlTags = row.metaUrlTags ? `tags ${row.metaUrlTags}` : '';
  return [destination || '', urlTags].filter(Boolean).join(' / ') || 'n/a';
}

export function metaDeliveryLabel(row: CreativeEvidenceLikeRow): string {
  return [
    `impressions ${formatInteger(row.impressions ?? 0)}`,
    `clicks ${formatInteger(row.clicks ?? 0)}`,
    `outbound ${formatInteger(row.outboundClicks ?? 0)}`,
  ].join(' / ');
}

export function issueFlagsLabel(flags: string[] | undefined): string {
  return flags?.length ? flags.join(', ') : 'ok';
}

function formatAnomalyScore(anomaly: FunnelReportsDashboardData['anomalies'][number]): string {
  if (anomaly.anomalyScore === null || anomaly.anomalyScore === undefined || Number.isNaN(anomaly.anomalyScore)) {
    return anomaly.anomalyModelMethod || 'n/a';
  }
  const score = formatNumber(anomaly.anomalyScore);
  const direction = anomaly.anomalyDirection && anomaly.anomalyDirection !== 'unknown'
    ? anomaly.anomalyDirection
    : 'movement';
  const sample = anomaly.baselineSampleCount ? `${formatInteger(anomaly.baselineSampleCount)}d baseline` : 'baseline n/a';
  return `${score} ${direction} / ${sample}`;
}

function formatAnomalyPriority(anomaly: FunnelReportsDashboardData['anomalies'][number]): string {
  const label = anomaly.anomalyPriorityLabel
    ? anomaly.anomalyPriorityLabel.replace(/_/g, ' ')
    : 'unranked';
  const score = anomaly.anomalyPriorityScore === null || anomaly.anomalyPriorityScore === undefined
    ? null
    : formatInteger(anomaly.anomalyPriorityScore);
  return score ? `${label} (${score})` : label;
}

function formatAnomalyConfidence(anomaly: FunnelReportsDashboardData['anomalies'][number]): string {
  const confidence = formatRate(anomaly.anomalyConfidence ?? null);
  const label = anomaly.anomalyConfidenceLabel
    ? anomaly.anomalyConfidenceLabel.replace(/_/g, ' ')
    : 'unknown';
  return confidence === 'n/a' ? label : `${label} / ${confidence}`;
}

function anomalyArtifactLabel(anomaly: FunnelReportsDashboardData['anomalies'][number]): string {
  if (!anomaly.artifactKey && !anomaly.artifactKind && !anomaly.artifactContentFamily) {
    return 'n/a';
  }

  return [
    anomaly.artifactKind,
    anomaly.artifactContentFamily,
    anomaly.artifactKey,
  ].filter(Boolean).join(' / ');
}

function anomalyRecurrenceLabel(anomaly: FunnelReportsDashboardData['anomalies'][number]): string {
  if (!anomaly.incidentRecurrenceState && !anomaly.incidentOccurrenceCount) {
    return 'n/a';
  }

  const state = anomaly.incidentRecurrenceState
    ? anomaly.incidentRecurrenceState.replace(/_/g, ' ')
    : 'incident';
  const count = anomaly.incidentOccurrenceCount ? ` (${formatInteger(anomaly.incidentOccurrenceCount)}x)` : '';
  return `${state}${count}`;
}

function anomalyPaidExposureLabel(
  anomaly: FunnelReportsDashboardData['anomalies'][number],
  currency = 'usd',
): string {
  if (anomaly.paidExposureStatus === 'suppressed_data_quality') {
    return 'suppressed by data quality';
  }
  if (anomaly.paidExposureStatus !== 'estimated' || anomaly.paidSpendExposure === null || anomaly.paidSpendExposure === undefined) {
    return 'n/a';
  }

  const parts = [
    formatCurrency(anomaly.paidSpendExposure, currency),
    `${formatInteger(anomaly.paidSessionsExposure ?? 0)} paid sessions`,
  ];
  if (Number(anomaly.paidUnmatchedSpendExposure ?? 0) > 0) {
    parts.push(`${formatCurrency(anomaly.paidUnmatchedSpendExposure ?? 0, currency)} unmatched`);
  }
  return parts.join(' / ');
}

function isFlowOnlySection(id: string): boolean {
  return id === 'screen-flow' || id === 'metric-timeseries' || id === 'artifacts';
}

type DashboardSummary = FunnelReportsDashboardData['summary'];

type PlacementTotals = Pick<
  FunnelReportsDashboardData['aliasSummaries'][number],
  | 'startedJourneys'
  | 'firstScreenOpenedJourneys'
  | 'firstToSecondJourneys'
  | 'paywallReachedJourneys'
  | 'buyButtonClickJourneys'
  | 'buyButtonClicks'
  | 'offerConvertedJourneys'
  | 'trialStartedJourneys'
  | 'purchaseJourneys'
  | 'purchaseAmount'
  | 'initialBillingRevenue'
  | 'firstScreenOpenRate'
  | 'firstToSecondRate'
  | 'paywallReachRate'
  | 'paywallToBuyClickRate'
  | 'dataQualitySeverity'
  | 'latestMaterializedAt'
  | 'currency'
  | 'moneyTotalsState'
>;

function summarizePlacementRollups(rows: FunnelReportsDashboardData['aliasSummaries']): PlacementTotals | null {
  if (!rows.length) return null;
  const startedJourneys = rows.reduce((sum, row) => sum + row.startedJourneys, 0);
  const firstScreenOpenedJourneys = rows.reduce((sum, row) => sum + row.firstScreenOpenedJourneys, 0);
  const firstToSecondJourneys = rows.reduce((sum, row) => sum + row.firstToSecondJourneys, 0);
  const paywallReachedJourneys = rows.reduce((sum, row) => sum + row.paywallReachedJourneys, 0);
  const buyButtonClickJourneys = rows.reduce((sum, row) => sum + row.buyButtonClickJourneys, 0);
  const buyButtonClicks = rows.reduce((sum, row) => sum + row.buyButtonClicks, 0);
  const offerConvertedJourneys = rows.reduce((sum, row) => sum + row.offerConvertedJourneys, 0);
  const trialStartedJourneys = rows.reduce((sum, row) => sum + row.trialStartedJourneys, 0);
  const purchaseJourneys = rows.reduce((sum, row) => sum + row.purchaseJourneys, 0);
  const moneyStates = rows.map(row => row.moneyTotalsState ?? (
    row.initialBillingRevenue === null || row.purchaseAmount === null
      ? 'missing_currency'
      : row.initialBillingRevenue !== 0 && row.currency
        ? 'single_currency'
        : 'no_money'
  ));
  const moneyCurrencies = new Set(rows
    .filter((_, index) => moneyStates[index] === 'single_currency')
    .map(row => row.currency?.toUpperCase())
    .filter((value): value is string => Boolean(value)));
  const moneyBlocked = moneyStates.some(state => state === 'missing_currency' || state === 'mixed_currency')
    || moneyCurrencies.size > 1;
  const purchaseAmount = moneyBlocked
    ? null
    : rows.reduce((sum, row) => sum + (row.purchaseAmount ?? 0), 0);
  const initialBillingRevenue = moneyBlocked
    ? null
    : rows.reduce((sum, row) => sum + (row.initialBillingRevenue ?? 0), 0);
  return {
    startedJourneys,
    firstScreenOpenedJourneys,
    firstToSecondJourneys,
    paywallReachedJourneys,
    buyButtonClickJourneys,
    buyButtonClicks,
    offerConvertedJourneys,
    trialStartedJourneys,
    purchaseJourneys,
    purchaseAmount,
    initialBillingRevenue,
    firstScreenOpenRate: safeStepRate(firstScreenOpenedJourneys, startedJourneys),
    firstToSecondRate: safeStepRate(firstToSecondJourneys, firstScreenOpenedJourneys),
    paywallReachRate: safeStepRate(paywallReachedJourneys, startedJourneys),
    paywallToBuyClickRate: safeStepRate(buyButtonClickJourneys, paywallReachedJourneys),
    dataQualitySeverity: highestSeverity(rows.map(row => row.dataQualitySeverity)),
    latestMaterializedAt: latestTimestamp(rows.map(row => row.latestMaterializedAt)),
    currency: moneyCurrencies.size === 1 ? Array.from(moneyCurrencies)[0] : undefined,
    moneyTotalsState: moneyBlocked
      ? (moneyStates.includes('missing_currency') ? 'missing_currency' : 'mixed_currency')
      : moneyCurrencies.size === 1
        ? 'single_currency'
        : 'no_money',
  };
}

function mergeSummaryWithPlacementTotals(summary: DashboardSummary, totals: PlacementTotals): DashboardSummary & PlacementTotals {
  return {
    ...summary,
    ...totals,
    paywallToOfferConversionRate: safeRate(totals.offerConvertedJourneys, totals.paywallReachedJourneys),
    paywallToOfferConversionNumerator: totals.offerConvertedJourneys,
    paywallToOfferConversionDenominator: totals.paywallReachedJourneys,
    buyClickToOfferConversionRate: safeRate(totals.offerConvertedJourneys, totals.buyButtonClicks),
    buyClickToOfferConversionNumerator: totals.offerConvertedJourneys,
    buyClickToOfferConversionDenominator: totals.buyButtonClicks,
    minHealthScore: summary.minHealthScore,
  };
}

function fallbackPlacementTotals(summary: DashboardSummary): PlacementTotals {
  return {
    startedJourneys: summary.startedJourneys,
    firstScreenOpenedJourneys: 0,
    firstToSecondJourneys: 0,
    paywallReachedJourneys: summary.paywallReachedJourneys,
    buyButtonClickJourneys: 0,
    buyButtonClicks: summary.buyButtonClicks,
    offerConvertedJourneys: summary.offerConvertedJourneys,
    trialStartedJourneys: summary.trialStartedJourneys,
    purchaseJourneys: summary.purchaseJourneys,
    purchaseAmount: summary.purchaseAmount,
    initialBillingRevenue: summary.initialBillingRevenue,
    firstScreenOpenRate: null,
    firstToSecondRate: null,
    paywallReachRate: safeStepRate(summary.paywallReachedJourneys, summary.startedJourneys),
    paywallToBuyClickRate: null,
    dataQualitySeverity: summary.dataQualitySeverity,
    latestMaterializedAt: summary.latestMaterializedAt,
    currency: summary.currency,
  };
}

function highestSeverity(values: string[]): string {
  const severityRank: Record<string, number> = { critical: 4, warning: 3, ok: 2, unknown: 1 };
  return values.reduce((highest, value) => (
    (severityRank[value] ?? 0) > (severityRank[highest] ?? 0) ? value : highest
  ), 'unknown');
}

function latestTimestamp(values: Array<string | undefined>): string | undefined {
  const sorted = values.filter((value): value is string => Boolean(value)).sort();
  return sorted[sorted.length - 1];
}

function truncate(value: string, max = 18): string {
  return value.length > max ? `${value.slice(0, max - 1)}...` : value;
}

function truncateMiddle(value: string, max = 34): string {
  if (value.length <= max) return value;
  const headLength = Math.ceil((max - 3) / 2);
  const tailLength = Math.floor((max - 3) / 2);
  return `${value.slice(0, headLength)}...${value.slice(value.length - tailLength)}`;
}

// People-attribution S7: formatters live in ../formatters (cycle-free) — re-exported here.
export { formatCurrency, formatInteger, formatNullableCurrency } from '../formatters';
import { formatCurrency, formatInteger, formatNullableCurrency } from '../formatters';

/** FU part E: P(beat) pill on variant sub-rows — capped at «>99%» like the scorecard (R3-1). */
function formatVariantProbability(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  if (value >= 0.995) return '>99%';
  return `${Math.round(value * 100)}%`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}



/**
 * Unit costs are INDEPENDENT cost-per-one-outcome figures over the same
 * matched spend — showing the outcome count next to each price is what keeps
 * a rare outcome (8 trials vs 68 direct purchases) from reading as an
 * expensive one.
 */
/** S7: a people-mode unit cost line — typed absence when the twin is not served. */
function peopleUnitCost(cost: number | null | undefined, count: number | undefined, currency = 'usd'): string {
  if (cost === undefined || count === undefined) return 'n/a · people data needs refresh';
  return `${formatNullableCurrency(cost, currency)} · ${formatInteger(count)} people`;
}

function unitCostWithCount(cost: number | null | undefined, count: number, currency = 'usd'): string {
  const price = formatNullableCurrency(cost, currency);
  return `${price} · ${formatInteger(count)}`;
}

function formatMinorUnits(
  value: number,
  currency = 'usd',
  currencyExponent: number | null | undefined = undefined,
): string {
  // Undefined is a compatibility path for cached pre-exponent payloads. A
  // current row emits explicit null and must remain unavailable.
  const exponent = currencyExponent === undefined ? 2 : currencyExponent;
  if (exponent === null || exponent < 0 || exponent > 3) return 'n/a';
  return formatCurrency(value / (10 ** exponent), currency);
}

function formatPaymentBasisMinorUnits(
  value: number | null | undefined,
  currency = 'usd',
  currencyExponent: number | null | undefined = undefined,
  basis?: { status: 'available' | 'partial' | 'unavailable'; coverage: number },
): string {
  const coverage = basis ? `${Math.round(basis.coverage * 100)}% covered` : undefined;
  if (basis?.status === 'unavailable' || value === null || value === undefined) {
    return coverage ? `n/a · ${coverage}` : 'n/a';
  }
  // W-A: a partial basis renders its vouched value as a lower bound.
  const marker = basis?.status === 'partial' ? '≥ ' : '';
  const formatted = `${marker}${formatMinorUnits(value, currency, currencyExponent)}`;
  return basis && basis.coverage < 1 ? `${formatted} · ${coverage}` : formatted;
}

function formatPurchaseMode(mode: string | undefined): string {
  if (mode === 'one_time') return 'One-time';
  if (mode === 'subscription') return 'Subscription';
  return mode ? mode.replaceAll('_', ' ') : 'Subscription (legacy)';
}

function formatRate(value: number | null): string {
  if (value === null || Number.isNaN(value)) return 'n/a';
  return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 }).format(value);
}

/** Compact «rate (num / den)» line for the collapsed Funnel column. */
function formatRateWithCounts(rate: number | null, numerator: number, denominator: number): string {
  if (rate === null || Number.isNaN(rate)) return 'n/a';
  return `${formatRate(rate)} (${formatInteger(numerator)} / ${formatInteger(denominator)})`;
}

// 3-tier ROAS coloring (paid-acquisition-table-ux): breakeven-anchored.
const ROAS_GOOD_THRESHOLD = 1;
const ROAS_MID_THRESHOLD = 0.6;

function roasToneColor(value: number): string {
  if (value >= ROAS_GOOD_THRESHOLD) return 'success.main';
  if (value >= ROAS_MID_THRESHOLD) return 'warning.main';
  return 'error.main';
}

/**
 * One watch-only pLTV renderer shared by the primary Creative Performance
 * table and the campaign creative drill-down. The estimate is gross per
 * subscriber, excludes costs and is structurally informational only.
 */
export function WatchOnlyPltvChip({
  watchOnly,
  projectId,
  enabled,
  testId,
}: {
  /**
   * I4: campaign-grain bands carry NO deploymentKey (owner decision — the
   * campaign chip is not clickable, tooltip only); creative-row bands keep
   * the deep link into the Creative Decisions drawer.
   */
  watchOnly?: Omit<NonNullable<FunnelCreativePerformanceRow['watchOnlyPltv']>, 'deploymentKey'>
    & { deploymentKey?: string };
  projectId?: string;
  enabled?: boolean;
  testId: string;
}): React.ReactElement {
  const linkable = Boolean(watchOnly?.deploymentKey && projectId);
  if (!watchOnly || !enabled || (watchOnly.deploymentKey !== undefined && !linkable)) {
    return (
      <Typography
        variant="body2"
        color="text.disabled"
        title="No watch-only pLTV snapshot is available for this exact ad deployment."
      >
        —
      </Typography>
    );
  }

  const available = watchOnly.state === 'available'
    && watchOnly.p50Minor !== undefined
    && Boolean(watchOnly.originalCurrency);
  // I5a review MF-1: narrow currency symbol («$», not «US$») keeps the full
  // label — incl. the load-bearing «watch-only» disclaimer — inside the 260px
  // column at every grain and viewport.
  const narrowMoney = (minor: number): string =>
    formatMinorMoney(minor, watchOnly.originalCurrency!, watchOnly.currencyExponent ?? 2, { narrow: true });
  const label = available
    ? `≈${narrowMoney(watchOnly.p50Minor!)}`
      + ` [${narrowMoney(watchOnly.p10Minor ?? watchOnly.p50Minor!)}`
      + `–${narrowMoney(watchOnly.p90Minor ?? watchOnly.p50Minor!)}]`
      + ` · n=${watchOnly.ownSubscriptions} · watch-only`
    : `not decision-grade yet (n=${watchOnly.ownSubscriptions})`;
  const chipSx = { height: 22, fontSize: '0.68rem', maxWidth: '100%' };

  if (!linkable) {
    return (
      <Chip
        size="small"
        variant="outlined"
        data-testid={testId}
        title="Watch-only gross 12-month pLTV forecast per subscriber for this campaign. Costs are not included; this never drives kill/scale."
        label={label}
        sx={chipSx}
      />
    );
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      clickable
      component="a"
      href={`/project/${projectId}/reports/creative-decisions?deployment=${encodeURIComponent(watchOnly.deploymentKey!)}`}
      data-testid={testId}
      title="Watch-only gross 12-month pLTV forecast per subscriber. Costs are not included; this never drives kill/scale. Opens the exact deployment band history."
      label={label}
      sx={chipSx}
    />
  );
}

/** Color-coded ROAS number; `payback` appends ✓ at ≥1×. n/a stays dimmed text. */
/**
 * Early-funnel diagnostic cell: toned score pill (≥60 above baseline · 40–59
 * mixed · <40 below baseline), behavior sub-line, and a message-mismatch
 * investigation signal when Meta CTR is high but downstream behavior is low.
 * n/a under the 100-session floor — never 0. It cannot drive a verdict.
 */
export function FunnelQualityCell({ row, testIdBadge }: {
  row: FunnelCreativePerformanceRow;
  testIdBadge: string;
}): React.ReactElement {
  if (row.qualityJourneys === undefined) {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  if (row.funnelQualityScore === null || row.funnelQualityScore === undefined) {
    return (
      <Box>
        <Typography variant="body2" color="text.disabled">n/a</Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.66rem' }}>
          {'< 100 sessions'}
        </Typography>
      </Box>
    );
  }
  const tone = row.funnelQualityScore >= 60 ? 'success.main' : row.funnelQualityScore >= 40 ? 'warning.main' : 'error.main';
  return (
    <Box>
      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
        <Typography component="span" variant="body2" sx={{ color: tone, fontWeight: 700 }}>
          {row.funnelQualityScore}
        </Typography>
        {row.clickbaitSuspect && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label="Mismatch signal"
            data-testid={testIdBadge}
            title="High Meta CTR and below-baseline downstream behavior. Investigate message or audience mismatch; this is observational, not causal."
            sx={{ height: 18, fontSize: '0.6rem' }}
          />
        )}
      </Stack>
      {row.qualityPaywallReachRate !== undefined && row.qualityQuizDepthRate !== undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.66rem' }}>
          {`paywall ${(row.qualityPaywallReachRate * 100).toFixed(0)}% · quiz ${(row.qualityQuizDepthRate * 100).toFixed(0)}%`}
        </Typography>
      )}
    </Box>
  );
}

/** Verdict chip tones (approved mock): kill=error, watch=warning, scale=success, too_young=dashed neutral. */
function VerdictChip({ verdict, cohortAgeDays, testId, title }: {
  verdict: CampaignVerdict;
  cohortAgeDays?: number;
  testId: string;
  title?: string;
}): React.ReactElement | null {
  if (verdict === 'none') return null;
  if (verdict === 'too_young') {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={typeof cohortAgeDays === 'number' ? `too young (${cohortAgeDays}d)` : 'too young'}
        data-testid={testId}
        data-verdict="too_young"
        title={title}
        sx={{ height: 20, fontSize: '0.62rem', color: 'text.disabled', borderStyle: 'dashed' }}
      />
    );
  }
  const color = verdict === 'kill' ? 'error' : verdict === 'scale' ? 'success' : 'warning';
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={verdict.toUpperCase()}
      data-testid={testId}
      data-verdict={verdict}
      title={title}
      sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
    />
  );
}

/**
 * pLTV (12m) cell: toned projected ROAS + $/sub with the P10–P90 band and the
 * payback line. Honesty states (AC-008): too-young cohorts and rows without a
 * projection render n/a — never $0.00 or a fake точность.
 */
function PltvCellContent({ row, verdict, thresholds, basisSupported, independentRequired, watchOnlyTestId, watchOnlyEnabled }: {
  row: FunnelReportsDashboardData['campaignEconomics'][number];
  verdict: CampaignVerdict;
  thresholds: CampaignVerdictThresholds;
  basisSupported: boolean;
  independentRequired: boolean;
  /** I4: campaign watch-only chip testid — chip renders only when provided. */
  watchOnlyTestId?: string;
  watchOnlyEnabled?: boolean;
}): React.ReactElement {
  // I4 (owner-decided): campaign-grain watch-only band as fallback INSIDE the
  // honest-n/a states only — the server attaches it exclusively while the
  // decision-grade gross projection is unavailable, so the band and a
  // decision-grade value can never render together (AC-031/037).
  const watchOnlyFallback = row.watchOnlyPltv && watchOnlyTestId && watchOnlyEnabled ? (
    <Box sx={{ mt: 0.25 }}>
      {/* D-J: rendered INSIDE PltvWithCac — PltvCellContent is only ever
          mounted as its `pltv` child (campaign pLTV cell), so the CAC line
          sits beside this fallback too. */}
      <WatchOnlyPltvChip
        watchOnly={row.watchOnlyPltv}
        enabled
        testId={watchOnlyTestId}
      />
    </Box>
  ) : null;
  const independent = row.pltvByBasis;
  if (independent) {
    const contribution = independent.contribution;
    const supporting = [
      projectedBasisLine('Net', independent.netCollected),
      projectedBasisLine('Settlement', independent.settlementCash),
      projectedBasisLine('Gross audit', independent.gross),
    ];
    // W-A D1 (DISC-AC-001): a partial contribution basis RENDERS — with the
    // ≥ marker and its boundary line — instead of collapsing to n/a.
    const contributionPartial = contribution.status === 'partial';
    if (
      (contribution.status !== 'available' && contribution.status !== 'partial')
      || contribution.pltv12PerSub === undefined
    ) {
      return (
        <Box>
          <Typography variant="body2" color="text.disabled">n/a</Typography>
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {`Contribution: ${projectedBasisUnavailableReason(contribution.unavailableReasons)}`}
          </Typography>
          {supporting.map(line => (
            <Typography key={line} variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.66rem' }}>
              {line}
            </Typography>
          ))}
          {watchOnlyFallback}
        </Box>
      );
    }
    if (verdict === 'too_young') {
      return (
        <Box>
          <Typography variant="body2" color="text.disabled">n/a</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {`Contribution gated · cohort < ${thresholds.minCohortAgeDays} days`}
          </Typography>
          {supporting.map(line => (
            <Typography key={line} variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.66rem' }}>
              {line}
            </Typography>
          ))}
          {watchOnlyFallback}
        </Box>
      );
    }
    const paybackLabel = contribution.paybackMonth !== null && contribution.paybackMonth !== undefined
      ? `payback ~mo ${contribution.paybackMonth}`
      : contribution.paybackBeyondHorizon
        ? 'payback > 12 mo'
        : contribution.pltv12Roas !== null && contribution.pltv12Roas !== undefined
          ? 'payback: never'
          : null;
    const projectionCurrency = contribution.currency || row.moneyCurrency || 'USD';
    return (
      <Box>
        <RoasValue
          value={contribution.pltv12Roas}
          boundaryPrefix={contributionPartial}
          title="Primary projected contribution LTV per subscriber ÷ CAC. Refunds, disputes, credits, discounts, fees, tax, and service COGS are included through the reconciled cycle-value basis."
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
          {`${contributionPartial ? '≥ ' : ''}${formatCurrency(contribution.pltv12PerSub, projectionCurrency)}/sub · contribution`}
          {contribution.pltv12RoasP10 !== null && contribution.pltv12RoasP10 !== undefined
            && contribution.pltv12RoasP90 !== null && contribution.pltv12RoasP90 !== undefined
            ? ` · P10–P90 ${contribution.pltv12RoasP10.toFixed(2)}–${contribution.pltv12RoasP90.toFixed(2)}`
            : ''}
        </Typography>
        {contributionPartial && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.66rem' }}>
            {[
              contribution.settledThrough ? `boundary ${contribution.settledThrough}` : null,
              contribution.pendingCycles ? `${contribution.pendingCycles} pending` : null,
            ].filter(Boolean).join(' · ') || 'boundary-shaped basis'}
          </Typography>
        )}
        {/* W-R5 rider (owner 2026-08-24): expected checks per entrant —
            server-computed by the same posterior draws as the LTV quantiles. */}
        {typeof contribution.expectedChecksP50 === 'number' && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontSize: '0.66rem' }}
            title="Expected checks (payments) per entrant over 12 months — blended entrant classes; upfront and one-time entrants count one day-0 check. P50 is the median; P10–P90 the credible interval."
          >
            {`~${contribution.expectedChecksP50.toFixed(1)} checks/entrant`}
            {typeof contribution.expectedChecksP10 === 'number' && typeof contribution.expectedChecksP90 === 'number'
              ? ` · P10–P90 ${contribution.expectedChecksP10.toFixed(1)}–${contribution.expectedChecksP90.toFixed(1)}`
              : ''}
          </Typography>
        )}
        {supporting.map(line => (
          <Typography key={line} variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.66rem' }}>
            {line}
          </Typography>
        ))}
        {paybackLabel && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {paybackLabel}
          </Typography>
        )}
        {contribution.lowConfidence && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.66rem' }} title="Sparse renewal cycles or a small cohort — the projection leans on the account prior.">
            ⚠ low confidence
          </Typography>
        )}
      </Box>
    );
  }
  if (independentRequired) {
    return (
      <Box>
        <Typography variant="body2" color="text.disabled">n/a</Typography>
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.68rem' }}>
          independent basis payload missing
        </Typography>
      </Box>
    );
  }
  if (!basisSupported) {
    return (
      <Typography variant="body2" color="text.disabled" title="Projected values are hidden because the payload basis does not match the current gross pLTV model.">
        n/a
      </Typography>
    );
  }
  if (verdict === 'too_young') {
    return (
      <Box>
        <Typography variant="body2" color="text.disabled">n/a</Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.68rem' }}>
          {`cohort < ${thresholds.minCohortAgeDays} days`}
        </Typography>
        {watchOnlyFallback}
      </Box>
    );
  }
  if (row.pltv12PerSub === undefined) {
    return (
      <Box>
        <Typography variant="body2" color="text.disabled">n/a</Typography>
        {watchOnlyFallback}
      </Box>
    );
  }
  const paybackLabel = row.paybackMonth !== null && row.paybackMonth !== undefined
    ? `payback ~mo ${row.paybackMonth}`
    : row.paybackBeyondHorizon
      ? 'payback > 12 mo'
      : row.pltv12Roas !== null && row.pltv12Roas !== undefined
        ? 'payback: never'
        : null;
  return (
    <Box>
      <RoasValue
        value={row.pltv12Roas}
        title="Projected 12-month gross-collected LTV per subscriber ÷ CAC. Verdicts read the P10/P90 bounds, not this point estimate. It does not deduct refunds, disputes, fees, tax, credits or service COGS."
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
        {`$${row.pltv12PerSub.toFixed(0)}/sub`}
        {row.pltv12RoasP10 !== null && row.pltv12RoasP10 !== undefined
          && row.pltv12RoasP90 !== null && row.pltv12RoasP90 !== undefined
          ? ` · P10–P90 ${row.pltv12RoasP10.toFixed(2)}–${row.pltv12RoasP90.toFixed(2)}`
          : ''}
      </Typography>
      {paybackLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
          {paybackLabel}
        </Typography>
      )}
      {row.pltvLowConfidence && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.66rem' }} title="Sparse renewal cycles or a small cohort — the projection leans on the account prior.">
          ⚠ low confidence
        </Typography>
      )}
    </Box>
  );
}

/**
 * I5d: humanized money-withholding caption — vocabulary phrase plus the
 * self-heal expectation where the code has one. Raw snake_case stays out of
 * primary copy (UNLOCK-AC-049); unknown codes degrade to the readable
 * de-underscore fallback inside humanizeCreativeReasonCode.
 */
function projectedBasisUnavailableReason(reasons: string[]): string {
  const code = reasons[0];
  if (!code) return 'reconciliation unavailable';
  const caption = humanizeCreativeReasonCode(code) ?? 'reconciliation unavailable';
  const note = creativeReasonSelfHealNote(code);
  return note ? `${caption} · ${note}` : caption;
}

/**
 * W-A diagnosis v1 (DISC-AC-028/030/035): pure formatting over the server
 * board — a typed lever code maps to an action a human can take; no date
 * arithmetic, no threshold comparison, no category inference happens here.
 */
const MONEY_LEVER_PHRASES: Record<string, string> = {
  control_resync: 'run a provider-controls re-sync',
  refund_matching_wave: 'refund matching wave opens this (net unlock)',
  disputes_backfill: 'run the disputes history backfill',
  fees_diagnosis: 'fees composition diagnosis pending',
  settlement_pipeline_repair: 'settlement evidence pipeline needs repair',
  coverage_registry_decision: 'needs a coverage-registry decision (not-applicable or add a source)',
};

function moneyDiagnosisStateLine(entry: NonNullable<FunnelReportsDashboardData['moneyDiagnosis']>['categories'][number]): string {
  const lever = entry.lever ? MONEY_LEVER_PHRASES[entry.lever] ?? entry.lever.replaceAll('_', ' ') : null;
  // W-D1 (DISC-AC-105/107): the quantified residual renders at EVERY severity
  // level — severity changes emphasis upstream, never this line's presence.
  const residual = entry.refundResidual && entry.refundResidual.dayCount > 0
    ? ` · ${formatMinorUnits(entry.refundResidual.amountMinor)} refund evidence unresolved across ${entry.refundResidual.dayCount} ${entry.refundResidual.dayCount === 1 ? 'day' : 'days'}`
    : '';
  if (entry.state === 'reconciled') {
    return `reconciled through ${entry.reconciledThrough ?? 'n/a'}${entry.horizonNote ? ` · ${entry.horizonNote}` : ''}${residual}`;
  }
  if (entry.state === 'out_of_tolerance') {
    return `${entry.outOfToleranceDays ?? 0} days out of tolerance`
      + `${entry.reconciledThrough ? ` · reconciled through ${entry.reconciledThrough}` : ''}`
      + residual
      + `${lever ? ` · ${lever}` : ''}`;
  }
  if (entry.state === 'no_control_source') {
    return `no control source${lever ? ` · ${lever}` : ''}`;
  }
  if (entry.state === 'not_applicable') {
    // W-D0: a resolved coverage-registry decision — date, not a lever.
    return `not applicable${entry.registeredAt ? ` (registered ${entry.registeredAt.slice(0, 10)})` : ''}`;
  }
  return `no evidence yet${lever ? ` · ${lever}` : ''}`;
}

/**
 * W-A tile aggregation over vouched row money. ONE boundary grammar with
 * projectedBasisLine (DISC-AC-005): `≥` marker · `boundary <date>` ·
 * `N pending` — two grammars for this concept on one screen is a failure.
 */
type RealizedTileTotal = {
  value: number;
  contributors: number;
  partialRows: number;
  blockedRows: number;
  pendingSubscriptions: number;
  settledThrough: string | null;
};

function tileIsBounded(tile: RealizedTileTotal | null): tile is RealizedTileTotal {
  return Boolean(tile && (tile.partialRows > 0 || tile.blockedRows > 0));
}

function tileBoundaryLine(tile: RealizedTileTotal | null): string | null {
  if (!tileIsBounded(tile)) return null;
  const parts = [
    tile.settledThrough ? `boundary ${tile.settledThrough}` : null,
    tile.pendingSubscriptions > 0 ? `${tile.pendingSubscriptions} pending` : null,
    tile.blockedRows > 0 ? `${tile.blockedRows} ${tile.blockedRows === 1 ? 'row' : 'rows'} blocked` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function projectedBasisLine(
  label: string,
  projection: FunnelProjectedLtvBasisResult,
): string {
  // C1B-I2 (LADDER): a partial basis renders its value WITH the boundary
  // label — «≥» because pending live-edge cycles can only add money.
  if (projection.status === 'partial' && projection.pltv12Roas !== undefined && projection.pltv12Roas !== null) {
    const boundary = projection.settledThrough ? ` · boundary ${projection.settledThrough}` : '';
    const pending = projection.pendingCycles ? ` · ${projection.pendingCycles} pending` : '';
    return `${label} ≥ ${projection.pltv12Roas.toFixed(2)}×${boundary}${pending}`;
  }
  if (projection.status !== 'available' || projection.pltv12Roas === undefined || projection.pltv12Roas === null) {
    return `${label} n/a${projection.unavailableReasons[0] ? ` · ${projectedBasisUnavailableReason(projection.unavailableReasons)}` : ''}`;
  }
  return `${label} ${projection.pltv12Roas.toFixed(2)}×`;
}

/**
 * Verdict thresholds gear popover (AC-011): live inline validation (the same
 * rule set the PATCH endpoint enforces), Save disabled while invalid or for
 * read-only members (canEdit=false — owner/admin only, user decision
 * 2026-08-06); Reset-to-defaults refills the fields; a successful Save
 * re-verdicts the table client-side WITHOUT a payload refetch.
 */
function VerdictThresholdsPopover({ anchorEl, onClose, projectId, thresholds, onSaved }: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  projectId: string;
  thresholds: CampaignVerdictThresholds;
  onSaved: (thresholds: CampaignVerdictThresholds) => void;
}): React.ReactElement {
  const client = React.useMemo(() => new FunnelInsightsApiClient(), []);
  const open = Boolean(anchorEl);
  const [fields, setFields] = React.useState({
    kill: String(thresholds.killBelowP90Roas),
    scale: String(thresholds.scaleAboveP10Roas),
    age: String(thresholds.minCohortAgeDays),
  });
  const [canEdit, setCanEdit] = React.useState<boolean | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setFields({
      kill: String(thresholds.killBelowP90Roas),
      scale: String(thresholds.scaleAboveP10Roas),
      age: String(thresholds.minCohortAgeDays),
    });
    setServerError(null);
    const controller = new AbortController();
    client.getReportSettings({ projectId, signal: controller.signal })
      .then(settings => {
        setCanEdit(settings.canEdit);
        setFields({
          kill: String(settings.verdictThresholds.killBelowP90Roas),
          scale: String(settings.verdictThresholds.scaleAboveP10Roas),
          age: String(settings.verdictThresholds.minCohortAgeDays),
        });
      })
      .catch(() => setCanEdit(null));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const candidate: CampaignVerdictThresholds = {
    killBelowP90Roas: Number(fields.kill),
    scaleAboveP10Roas: Number(fields.scale),
    minCohortAgeDays: Number(fields.age),
  };
  const validationError = validateCampaignVerdictThresholds(candidate);
  const readOnly = canEdit === false;
  const save = async (): Promise<void> => {
    setSaving(true);
    setServerError(null);
    try {
      const saved = await client.patchReportSettings({ projectId, patch: candidate });
      onSaved(saved.verdictThresholds);
    } catch (error) {
      setServerError(error instanceof ReportSettingsApiError ? error.message : 'Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { 'data-testid': FUNNEL_REPORTS_TEST_IDS.paidThresholdsPopover } as never }}
    >
      <Stack spacing={1.5} sx={{ p: 2, width: 280 }}>
        <Typography variant="subtitle2">Gross pLTV signal thresholds</Typography>
        <TextField
          size="small"
          label="KILL when P90 gross pLTV:CAC below"
          value={fields.kill}
          onChange={event => setFields(current => ({ ...current, kill: event.target.value }))}
          disabled={readOnly}
          inputProps={{ inputMode: 'decimal', 'data-testid': FUNNEL_REPORTS_TEST_IDS.paidThresholdsKillInput }}
        />
        <TextField
          size="small"
          label="SCALE when P10 gross pLTV:CAC above"
          value={fields.scale}
          onChange={event => setFields(current => ({ ...current, scale: event.target.value }))}
          disabled={readOnly}
          inputProps={{ inputMode: 'decimal', 'data-testid': FUNNEL_REPORTS_TEST_IDS.paidThresholdsScaleInput }}
        />
        <TextField
          size="small"
          label="Minimum cohort age, days"
          value={fields.age}
          onChange={event => setFields(current => ({ ...current, age: event.target.value }))}
          disabled={readOnly}
          inputProps={{ inputMode: 'numeric', 'data-testid': FUNNEL_REPORTS_TEST_IDS.paidThresholdsAgeInput }}
        />
        {(validationError || serverError) && (
          <Typography variant="caption" color="error" data-testid={FUNNEL_REPORTS_TEST_IDS.paidThresholdsError}>
            {serverError ?? VERDICT_THRESHOLD_ERROR_COPY[validationError!]}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {readOnly
            ? 'Read-only: thresholds are managed by the project owner or an admin.'
            : 'Project-wide — applied to the dashboard and to new research reports. Stored reports keep the thresholds they were generated with.'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          These thresholds use estimated gross-collected value. They do not prove net-collected or contribution profitability.
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="text"
            disabled={readOnly}
            data-testid={FUNNEL_REPORTS_TEST_IDS.paidThresholdsReset}
            onClick={() => setFields({
              kill: String(DEFAULT_VERDICT_THRESHOLDS.killBelowP90Roas),
              scale: String(DEFAULT_VERDICT_THRESHOLDS.scaleAboveP10Roas),
              age: String(DEFAULT_VERDICT_THRESHOLDS.minCohortAgeDays),
            })}
          >
            Reset to defaults
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={Boolean(validationError) || readOnly || saving}
            data-testid={FUNNEL_REPORTS_TEST_IDS.paidThresholdsSave}
            onClick={() => { void save(); }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}

export function RoasValue({ value, payback, title, boundaryPrefix }: {
  value: number | null | undefined;
  payback?: boolean;
  title?: string;
  /** W-A: `≥` marker for boundary-shaped (partial) values — a true lower bound. */
  boundaryPrefix?: boolean;
}): React.ReactElement {
  if (value === null || value === undefined) {
    return <Typography variant="body2" color="text.disabled">n/a</Typography>;
  }
  const tone = value >= ROAS_GOOD_THRESHOLD ? 'good' : value >= ROAS_MID_THRESHOLD ? 'mid' : 'bad';
  return (
    <Typography
      component="span"
      variant="body2"
      title={title}
      data-tone={tone}
      sx={{ color: roasToneColor(value), fontWeight: 600 }}
    >
      {`${boundaryPrefix ? '≥ ' : ''}${value.toFixed(2)}×${payback && value >= ROAS_GOOD_THRESHOLD ? ' ✓' : ''}`}
    </Typography>
  );
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** PE-6b (D-P6): UTC day label for the prior-window badge (end handled -1ms by the caller). */
function formatWindowDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return `${MONTH_SHORT[date.getUTCMonth()]} ${String(date.getUTCDate()).padStart(2, '0')}`;
}

function rateEvidenceParts(rate: number | null, numerator: number, denominator: number): { top: string; bottom: string } {
  if (denominator > 0 && numerator > denominator) {
    return {
      top: 'mismatch',
      bottom: `${formatInteger(numerator)} / ${formatInteger(denominator)}`,
    };
  }
  return {
    top: rate === null ? 'n/a' : formatRate(rate),
    bottom: denominator > 0 ? `${formatInteger(numerator)} / ${formatInteger(denominator)}` : 'no denominator',
  };
}

function safeRate(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

function safeStepRate(numerator: number, denominator: number): number | null {
  if (denominator <= 0 || numerator > denominator) return null;
  return numerator / denominator;
}

function formatMetricValue(metricName: string, value: number, currency = 'usd'): string {
  if (metricName.includes('rate') || metricName.endsWith('_cr')) {
    return formatRate(value);
  }
  if (metricName.includes('amount') || metricName.includes('revenue')) {
    return formatCurrency(value, currency);
  }
  return formatNumber(value);
}

function formatMetricComponent(metricName: string, value: number, currency = 'usd'): string {
  if (metricName.includes('amount') || metricName.includes('revenue')) {
    return formatCurrency(value, currency);
  }
  return formatNumber(value);
}

function tooltipNumberFormatter(value: unknown): string {
  return typeof value === 'number'
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
    : String(value ?? '');
}

/** R2-3 rate tooltips: 0..1 ratios render as percentages. */
function ratioTooltipFormatter(value: unknown): string {
  return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : String(value ?? '');
}
